// =============================================================================
// CONSOLIDATED AI CHAT HOOKS
// =============================================================================
//
// This file consolidates all AI Chat hooks, including streaming session state:
//
// SECTION 1: UI EFFECTS (useTypewriterPlaceholder)
// - Typewriter placeholder animation
//
// SECTION 2: CHAT COMPOSER (useChatComposer)
// - Textarea input management
// - File attachments
// - Upload handling
//
// SECTION 3: STREAMING SESSION (useStreamingSession)
// - Session state management
// - Streaming state tracking
// - Message handling and queue management
// - Turn queue operations
//
// =============================================================================

import { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useTheme } from '@mui/material/styles';
import { capture, startTimer, captureWithDuration } from '../../../shared/services/posthog';
import {
  DescriptionIcon,
  ImageIcon,
  PictureAsPdfIcon,
  MovieIcon,
  MusicNoteIcon,
  TableChartIcon,
  ArchiveIcon,
  CodeIcon,
  UploadFileIcon,
} from '../../../shared/icons';
import { useUI } from '../../../shared/contexts/UIContext';
import { smartCache } from '../../../shared/services/data/SmartCache';
import { upsertChatMessage } from '../../../shared/services/data/ChatSyncService';
import {
  SCOPED_TRANSCRIPT_PROTOCOL_VERSION,
  TRANSCRIPT_MODE_LOCKER_SCOPED,
  TRANSCRIPT_MODE_ORDINARY,
  TRANSCRIPT_MODE_PENDING,
  transcriptModeForChat,
} from '../../../shared/services/data/chatTranscriptPolicy';
import { getActiveTemplate } from '../../../shared/services/data/templateStore';
import { getMyConnectorReadiness } from '../../../shared/services/connectorService';
import {
  FinanceOfficeController,
  FINANCE_OFFICE_TOOL,
} from '../../../shared/services/office/finance';
import {
  isReconcileExhausted,
  LockerOfficeQuiescenceJournal,
  RECONCILE_OUTCOMES,
} from '../../../shared/services/office/LockerOfficeQuiescenceJournal';
import streamProcessor from './streamProcessor';
import { createPrivateOfficeCancellationRegistry } from './privateOfficeCancellation';
import { DEFAULT_MODEL } from './constants';
import {
  sanitizeMessage,
  sanitizeComponentsArray,
  getFunctionUrl,
  parseSSEEvent,
  normalizeConnectorWorkerProgress,
  serializeComponentsToText,
  shouldGuardRemoteToolCall
} from './utils';
// Plan mode removed - no longer needed

const debug = process.env.NODE_ENV === 'development' ? console.log : () => { };
const FINANCE_CONNECTORS_UI_ENABLED =
  process.env.REACT_APP_FINANCE_CONNECTORS_ENABLED === 'true';
// Models a private (licensed-data) turn is allowed to use. These IDs are pinned
// literally and deliberately do NOT follow the model picker: the set mirrors the
// zero-data-retention attestation enforced server-side by MODEL_ALLOWLIST in
// financeConnectorProviderPolicy.js. Deriving it from MODELS meant a picker
// upgrade silently widened the private path to an unattested model, which the
// backend then rejects. Add an ID here only once it is covered by the ZDR
// contract and present in that backend allowlist.
const FINANCE_PRIVATE_MODELS = Object.freeze(
  new Set(['claude-haiku-4-5-20251001', 'claude-sonnet-5', 'claude-opus-4-8'])
);
// Attested model a private turn falls back to when the requested model is not
// covered. Pinned for the same reason as the set above.
const FINANCE_PRIVATE_FALLBACK_MODEL = 'claude-opus-4-8';

function createPrivateRequestId() {
  const randomId = typeof window !== 'undefined'
    ? window.crypto?.randomUUID?.()
    : null;
  if (!randomId) {
    throw new Error('Secure request identity generation is unavailable');
  }
  return randomId;
}

const PRIVATE_FINANCE_TOOL_NAMES = Object.freeze(
  new Set(Object.values(FINANCE_OFFICE_TOOL))
);
const LIVE_DATA_STAGE_LABELS = Object.freeze({
  accepted: 'Live data task accepted',
  starting: 'Preparing the licensed data request',
  connecting: 'Connecting to Daloopa',
  retrieving: 'Retrieving licensed financial data',
  writing: 'Writing cited results into the open file',
  verifying: 'Verifying the live Office output',
  completed: 'Daloopa work completed',
  failed: 'Daloopa work could not be completed',
  cancelled: 'Daloopa work was cancelled',
});
const PRIVATE_OPERATION_ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const PRIVATE_PROVIDER_ID_RE = /^[a-z][a-z0-9_-]{0,63}$/;
const DOCUMENT_PRIVACY_HEADER = 'X-Restack-Document-Privacy';
const SCOPED_TRANSCRIPT_DISPLAY_TEXT_MAX_CHARS = 32000;
const MID_TURN_STEER_ATTEMPTS = 2;
const LOCKER_OFFICE_OPERATION_ID_RE = /^[0-9a-f]{32}$/;
const LOCKER_OFFICE_OPERATION_RECEIPT_RE = /^[0-9a-f]{64}$/;
const LOCKER_OFFICE_REQUEST_TIMEOUT_MS = 10_000;
// Stands in for an operation id when the journal itself cannot be read, so
// even that banner has something to dismiss.
const OFFICE_RECOVERY_UNAVAILABLE_KEY = 'recovery-unavailable';
const DISMISSED_OFFICE_RECOVERY_STORAGE_KEY =
  'restack.locker.office-recovery-dismissed';
// Cap so a long-lived pane cannot grow this without bound. Oldest first, which
// is safe: re-raising the banner for an ancient dismissal is a nuisance, while
// dropping a recent one would re-nag the user about what they just dismissed.
const MAX_DISMISSED_OFFICE_RECOVERY_IDS = 64;

function readDismissedOfficeRecoveryIds() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return new Set();
    const raw = window.localStorage.getItem(
      DISMISSED_OFFICE_RECOVERY_STORAGE_KEY
    );
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((id) => typeof id === 'string' && id));
  } catch {
    // A corrupt or unavailable store must not break the pane. Losing the
    // dismissals only means the banner is shown again, which is the safe way
    // for this to fail.
    return new Set();
  }
}

function persistDismissedOfficeRecoveryIds(ids) {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    const list = Array.from(ids).slice(-MAX_DISMISSED_OFFICE_RECOVERY_IDS);
    window.localStorage.setItem(
      DISMISSED_OFFICE_RECOVERY_STORAGE_KEY,
      JSON.stringify(list)
    );
  } catch {
    // Non-fatal: the dismissal still holds for this pane session.
  }
}
const SCOPED_DELIVERY_NO_RETRY_CODES = Object.freeze(new Set([
  'scoped_transcript_request_outcome_unknown',
  'scoped_transcript_request_active_elsewhere',
  'scoped_transcript_request_interrupted',
]));

export function classifyInitialMessageDeliveryFailure({
  transcriptMode,
  dispatchStarted = false,
  responseStatus = null,
  serverErrorCode = null,
  acceptanceConfirmed = false,
} = {}) {
  if (transcriptMode !== TRANSCRIPT_MODE_LOCKER_SCOPED) {
    return Object.freeze({
      suppressFreshRetry: false,
      outcome: 'ordinary',
      errorCode: null,
      message: null,
    });
  }

  if (acceptanceConfirmed) {
    return Object.freeze({
      suppressFreshRetry: true,
      outcome: 'accepted',
      errorCode: 'scoped_transcript_delivery_accepted',
      message:
        'This secure request was accepted, but its live response was interrupted. Refresh this chat and review the transcript and open file before sending another instruction.',
    });
  }

  const status = Number(responseStatus);
  const responseMayHideAcceptance =
    !Number.isFinite(status)
    || status === 0
    || status === 408
    || status >= 500
    || (status >= 200 && status < 300);
  if (
    SCOPED_DELIVERY_NO_RETRY_CODES.has(serverErrorCode)
    || (dispatchStarted && responseMayHideAcceptance)
  ) {
    return Object.freeze({
      suppressFreshRetry: true,
      outcome: 'unknown',
      errorCode: 'scoped_transcript_delivery_outcome_unknown',
      message:
        'Restack could not confirm whether this secure request was accepted. Refresh this chat and review the transcript and open file before sending another instruction.',
    });
  }

  return Object.freeze({
    suppressFreshRetry: false,
    outcome: 'rejected',
    errorCode: null,
    message: null,
  });
}

export function createInitialDeliveryNotice({
  aiMessageId,
  decision,
  timestamp = new Date().toISOString(),
} = {}) {
  if (
    typeof aiMessageId !== 'string'
    || !aiMessageId
    || decision?.suppressFreshRetry !== true
    || typeof decision?.message !== 'string'
    || !decision.message
  ) {
    throw new Error('Secure delivery notice input is invalid');
  }
  return Object.freeze({
    id: aiMessageId,
    sender: 'ai',
    components: [{
      type: 'text',
      content: decision.message,
      timestamp,
    }],
    timestamp,
    isStreaming: false,
    hasError: true,
    isError: true,
    errorCode: decision.errorCode,
    metadata: {
      isStreaming: false,
      isComplete: true,
      deliveryOutcome: decision.outcome,
      requiresRefresh: true,
    },
  });
}

function responseErrorCode(rawBody) {
  if (typeof rawBody !== 'string' || !rawBody.trim()) return null;
  try {
    const parsed = JSON.parse(rawBody);
    return typeof parsed?.error === 'string' ? parsed.error : null;
  } catch {
    return null;
  }
}

export function isConfirmedPreDispatchAbort(error, deliveryDecision) {
  return error?.name === 'AbortError'
    && deliveryDecision?.suppressFreshRetry !== true;
}

export function planUnresolvedSteerRetry({
  unresolved,
  currentChatId,
  activeSessionId,
  displayText,
  attachmentKey,
} = {}) {
  const chatMatches = Boolean(
    unresolved?.chatId
    && currentChatId
    && unresolved.chatId === currentChatId
  );
  if (!chatMatches) {
    return Object.freeze({
      chatMatches: false,
      reuseExact: false,
      blockFreshRequest: false,
    });
  }
  const sessionMatches = Boolean(
    unresolved?.sessionId
    && activeSessionId
    && unresolved.sessionId === activeSessionId
  );
  const payloadMatches =
    unresolved?.displayText === displayText
    && unresolved?.attachmentKey === attachmentKey;
  const reuseExact = sessionMatches && payloadMatches;
  return Object.freeze({
    chatMatches: true,
    reuseExact,
    blockFreshRequest: !reuseExact,
  });
}

function boundedTranscriptDisplayText(value) {
  return String(value || '').slice(0, SCOPED_TRANSCRIPT_DISPLAY_TEXT_MAX_CHARS);
}

function steerAttachmentKey(files = []) {
  return files
    .map((file) => [
      String(file?.name || ''),
      String(file?.type || ''),
      Number(file?.size) || 0,
      Number(file?.lastModified) || 0,
    ].join(':'))
    .join('|');
}

function normalizeLockerOfficeOperations(value) {
  // An absent field and an empty array both mean "this tool call carries no
  // durable Office operations". Treating the second as corruption threw on
  // every unbound tool call and surfaced as "Secure Office operation receipts
  // are invalid", which reads like tampering rather than the ordinary case it
  // is. The server-side twin of this function had the same asymmetry and was
  // already corrected; this is the client half.
  //
  // The upper bound stays a hard failure: more receipts than the protocol
  // allows IS a malformed request, and there is no safe reading of it.
  if (value === undefined || value === null) return [];
  if (!Array.isArray(value) || value.length > 4) {
    throw new Error('Secure Office operation receipts are invalid');
  }
  return value.map((entry) => {
    const chatId = typeof entry?.chatId === 'string'
      ? entry.chatId.trim()
      : '';
    const operationId = typeof entry?.operationId === 'string'
      ? entry.operationId.toLowerCase()
      : '';
    const receipt = typeof entry?.receipt === 'string'
      ? entry.receipt.toLowerCase()
      : '';
    if (
      !/^[^/\u0000-\u001F\u007F]{1,512}$/.test(chatId)
      || !LOCKER_OFFICE_OPERATION_ID_RE.test(operationId)
      || !LOCKER_OFFICE_OPERATION_RECEIPT_RE.test(receipt)
    ) {
      throw new Error('Secure Office operation receipts are invalid');
    }
    return Object.freeze({ chatId, operationId, receipt });
  });
}

// While ask_user_question is open the SDK is parked inside the MCP handler, so
// a POST /messages steering turn cannot reach the model until that tool
// returns: only a tool result settles it. Typed composer text therefore has to
// travel as the answer, not as steering, or the user has no way to unblock the
// turn when the card is gone (dismissed, or lost to a re-render).
export function shouldAnswerPendingQuestionWithText({
  pendingQuestion = null,
  text = '',
  hasAttachments = false,
} = {}) {
  return Boolean(
    pendingQuestion?.callId
    && typeof pendingQuestion.submit === 'function'
    && !hasAttachments
    && typeof text === 'string'
    && text.trim().length > 0
  );
}

// Shape a typed reply like the card's own payload so the model reads one
// answer format. One free-text reply answers the card as a whole: it lands on
// the first question rather than pretending the user typed the same words for
// every question.
export function buildPendingQuestionTextAnswer(pendingQuestion, text) {
  const trimmed = typeof text === 'string' ? text.trim() : '';
  const questions = Array.isArray(pendingQuestion?.questions)
    ? pendingQuestion.questions
    : [];
  if (questions.length === 0) {
    return [{ question: '', answer: trimmed }];
  }
  return questions.map((entry, index) => ({
    question: typeof entry?.question === 'string' ? entry.question : '',
    answer: index === 0 ? trimmed : '',
  }));
}

export function officeToolAdmissionIdentity({
  toolName,
  durableOperations = [],
} = {}) {
  const normalizedToolName =
    typeof toolName === 'string' ? toolName.trim() : '';
  if (!normalizedToolName) {
    throw new Error('Secure Office tool identity is unavailable');
  }
  return JSON.stringify({
    toolName: normalizedToolName,
    durableOperations:
      normalizeLockerOfficeOperations(durableOperations),
  });
}

export async function fetchWithDeadline({
  fetchImpl = fetch,
  url,
  options = {},
  timeoutMs = LOCKER_OFFICE_REQUEST_TIMEOUT_MS,
  readJson = false,
}) {
  const deadlineMs = Math.max(1, Number(timeoutMs) || 1);
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), deadlineMs);
  try {
    const response = await fetchImpl(url, {
      ...options,
      signal: controller.signal,
    });
    if (!readJson) return { response, data: null };
    let data = {};
    try {
      data = await response.json();
    } catch {
      data = {};
    }
    return {
      response,
      data: data && typeof data === 'object' ? data : {},
    };
  } finally {
    clearTimeout(timeout);
  }
}

/**
 * Read one quiescence answer for exactly what it proves.
 *
 * POST /chats/:chatId/office-operations/:operationId/quiesced answers with a
 * 200 `{success, completed: true, duplicate: true}` whenever the fence holds
 * no pending record for the receipt: the fence document is absent, it belongs
 * to another chat, or the operation is not in it. That is terminal. The single
 * 409 the endpoint can raise is `locker_office_operation_pending`, and the
 * server only reaches it after finding the operation AND matching its receipt
 * digest, so it means the write is genuinely live and the entry must survive.
 * Everything else (403 receipt mismatch, 400, 5xx, an unreadable fence, a
 * transport failure) proves nothing either way and is retried under a ceiling.
 */
export function classifyLockerOfficeQuiescenceResponse({
  status,
  data = {},
} = {}) {
  if (
    Number(status) === 200
    && data?.success === true
    && data?.completed === true
  ) {
    return Object.freeze({
      completed: true,
      outcome: RECONCILE_OUTCOMES.UNKNOWN_TO_SERVER,
      status: 200,
    });
  }
  if (
    Number(status) === 409
    && data?.error === 'locker_office_operation_pending'
  ) {
    return Object.freeze({
      completed: false,
      outcome: RECONCILE_OUTCOMES.SERVER_CONFIRMED_PENDING,
      status: 409,
    });
  }
  return Object.freeze({
    completed: false,
    outcome: RECONCILE_OUTCOMES.UNRECONCILED,
    status: Number(status) || 0,
  });
}

export async function pollLockerOfficeOperationCompletion({
  baseUrl,
  authToken,
  operation,
  fetchImpl = fetch,
  timeoutMs = LOCKER_OFFICE_REQUEST_TIMEOUT_MS,
}) {
  const [normalized] = normalizeLockerOfficeOperations([operation]);
  let response;
  let data;
  try {
    ({ response, data } = await fetchWithDeadline({
      fetchImpl,
      url: `${String(baseUrl || '').replace(/\/+$/, '')}`
        + `/chats/${encodeURIComponent(normalized.chatId)}`
        + `/office-operations/${normalized.operationId}/quiesced`,
      options: {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken
            ? { Authorization: `Bearer ${authToken}` }
            : {}),
        },
        body: JSON.stringify({ receipt: normalized.receipt }),
      },
      timeoutMs,
      readJson: true,
    }));
  } catch {
    // A transport failure is not evidence about the operation.
    return classifyLockerOfficeQuiescenceResponse({ status: 0 });
  }
  return classifyLockerOfficeQuiescenceResponse({
    status: response?.status,
    data,
  });
}

const LOCKER_OFFICE_TRANSITIONS = Object.freeze(new Set([
  'client-persisted',
  'start',
  'abandon',
  'resolve-unknown',
]));

export async function transitionLockerOfficeOperation({
  baseUrl,
  authToken,
  operation,
  transition,
  fetchImpl = fetch,
  attempts = 2,
  confirmReviewed = false,
  confirmNoActivePromise = false,
  timeoutMs = LOCKER_OFFICE_REQUEST_TIMEOUT_MS,
}) {
  const [normalized] = normalizeLockerOfficeOperations([operation]);
  if (!LOCKER_OFFICE_TRANSITIONS.has(transition)) {
    throw new Error('Secure Office operation transition is invalid');
  }
  if (
    transition === 'resolve-unknown'
    && (
      confirmReviewed !== true
      || confirmNoActivePromise !== true
    )
  ) {
    throw new Error(
      'Secure Office operation review and completion confirmation are required'
    );
  }
  let lastError = null;
  for (
    let attempt = 0;
    attempt < Math.max(1, Number(attempts) || 1);
    attempt += 1
  ) {
    try {
      const { response, data } = await fetchWithDeadline({
        fetchImpl,
        url: `${String(baseUrl || '').replace(/\/+$/, '')}`
          + `/chats/${encodeURIComponent(normalized.chatId)}`
          + `/office-operations/${normalized.operationId}`
          + `/${transition}`,
        options: {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(authToken
              ? { Authorization: `Bearer ${authToken}` }
              : {}),
          },
          body: JSON.stringify({
            receipt: normalized.receipt,
            ...(transition === 'resolve-unknown'
              ? {
                confirmReviewed: true,
                confirmNoActivePromise: true,
              }
              : {}),
          }),
        },
        timeoutMs,
        readJson: true,
      });
      if (response.ok) {
        return Object.freeze({
          ...data,
          status: response.status,
        });
      }
      const transitionError = new Error(
        typeof data?.message === 'string'
          ? data.message
          : 'Secure Office operation transition failed'
      );
      transitionError.code =
        typeof data?.error === 'string'
          ? data.error
          : 'locker_office_transition_failed';
      transitionError.status = response.status;
      if (
        response.status !== 408
        && response.status !== 429
        && response.status < 500
      ) {
        throw transitionError;
      }
      lastError = transitionError;
    } catch (error) {
      lastError = error;
      if (
        Number(error?.status) >= 400
        && Number(error?.status) < 500
        && Number(error?.status) !== 408
        && Number(error?.status) !== 429
      ) {
        throw error;
      }
    }
  }
  throw lastError || new Error(
    'Secure Office operation transition was not confirmed'
  );
}

export async function deliverOfficeToolResult({
  fetchImpl = fetch,
  url,
  headers,
  payload,
  requireOfficeResultDelivered = false,
  requirePreStartAbandoned = false,
  attempts = 3,
  timeoutMs = LOCKER_OFFICE_REQUEST_TIMEOUT_MS,
}) {
  const retryDelaysMs = [0, 250, 750];
  const attemptCount = Math.max(
    1,
    Math.min(retryDelaysMs.length, Number(attempts) || 1)
  );
  let lastError = null;
  for (let attempt = 0; attempt < attemptCount; attempt += 1) {
    if (retryDelaysMs[attempt] > 0) {
      await new Promise((resolve) =>
        setTimeout(resolve, retryDelaysMs[attempt])
      );
    }
    try {
      const { response, data } = await fetchWithDeadline({
        fetchImpl,
        url,
        options: {
          method: 'POST',
          headers,
          body: JSON.stringify(payload),
        },
        timeoutMs,
        readJson: true,
      });
      if (
        response.ok
        && data?.success === true
        && (
          !requireOfficeResultDelivered
          || data?.officeResultDelivered === true
        )
        && (
          !requirePreStartAbandoned
          || data?.preStartAbandoned === true
        )
      ) {
        return Object.freeze({
          accepted: true,
          status: response.status,
          data,
        });
      }
      const status = Number(response.status) || 0;
      const retryable =
        status === 408
        || status === 429
        || status >= 500
        || response.ok;
      lastError = new Error(
        'Office tool result delivery was not confirmed'
      );
      lastError.status = status;
      lastError.code =
        typeof data?.error === 'string'
          ? data.error
          : 'office_tool_result_unconfirmed';
      if (!retryable) {
        return Object.freeze({
          accepted: false,
          unknown: false,
          status,
          error: lastError,
        });
      }
    } catch (error) {
      lastError = error;
    }
  }
  return Object.freeze({
    accepted: false,
    unknown: true,
    status: Number(lastError?.status) || null,
    error: lastError || new Error(
      'Office tool result delivery was not confirmed'
    ),
  });
}

export function planOfficeToolResultCompletion({
  accepted,
  durableOperationCount = 0,
  preStartAbandoned = false,
} = {}) {
  const hasDurableOperations =
    Number(durableOperationCount) > 0;
  return Object.freeze({
    markResultDelivered:
      accepted === true
      && hasDurableOperations
      && preStartAbandoned !== true,
    clearPreStartReceipt:
      accepted === true
      && hasDurableOperations
      && preStartAbandoned === true,
    suppressQuiescence:
      accepted !== true && hasDurableOperations,
  });
}

export function assertPreStartAbandonedError({
  preStartAbandoned = false,
  error = null,
} = {}) {
  if (
    preStartAbandoned === true
    && (
      typeof error !== 'string'
      || !error.trim()
    )
  ) {
    const failure = new Error(
      'A pre-start abandoned Office operation cannot report success'
    );
    failure.code =
      'locker_office_pre_start_success_forbidden';
    failure.suppressToolResult = true;
    throw failure;
  }
  return true;
}

export function assertNoActiveOfficePromise(
  records,
  operationId
) {
  const values =
    records && typeof records.values === 'function'
      ? [...records.values()]
      : [];
  const activeRegistration = values.find(
    (record) =>
      record.durableOperations?.some(
        (operation) =>
          operation.operationId === operationId
      )
  );
  if (
    activeRegistration
    && activeRegistration.admissionState?.settled !== true
  ) {
    throw new Error(
      'Office is still finishing this exact operation. Wait before resolving it.'
    );
  }
  return true;
}

export function createPreStartAbandonedFailureError({
  message = 'Secure Office operation failed before it started',
  code = 'locker_office_pre_start_failed',
  cause = null,
} = {}) {
  const error = new Error(message);
  error.code = code;
  error.suppressQuiescence = true;
  error.preStartAbandoned = true;
  if (cause) error.cause = cause;
  return error;
}

export function createPreStartAbandonedCancellationError() {
  return createPreStartAbandonedFailureError({
    message: 'Office operation was cancelled before it started',
    code: 'locker_office_pre_start_cancelled',
  });
}

export function shouldSuppressOfficeQuiescence(error) {
  return error?.suppressToolResult === true
    || error?.suppressQuiescence === true;
}

export function shouldSuppressCancelledOfficeResult({
  cancelled = false,
  preStartAbandoned = false,
} = {}) {
  return cancelled === true && preStartAbandoned !== true;
}

export async function abandonPreparedOfficeOperations({
  operations = [],
  abandonServerOperation,
  abandonLocalOperation = null,
  handleRunningOperation = null,
  handleLocalCleanupError = null,
} = {}) {
  if (
    !Array.isArray(operations)
    || typeof abandonServerOperation !== 'function'
  ) {
    throw new Error(
      'Secure Office operation abandon tracking is unavailable'
    );
  }
  let allServerAbandoned = true;
  for (const operation of operations) {
    try {
      await abandonServerOperation(operation);
    } catch (error) {
      allServerAbandoned = false;
      if (
        error?.code === 'locker_office_operation_running'
        && typeof handleRunningOperation === 'function'
      ) {
        try {
          await handleRunningOperation(operation, error);
        } catch {
          // Server state remains authoritative when local recovery fails.
        }
      }
      continue;
    }
    if (typeof abandonLocalOperation === 'function') {
      try {
        await abandonLocalOperation(operation);
      } catch (error) {
        if (typeof handleLocalCleanupError === 'function') {
          try {
            await handleLocalCleanupError(operation, error);
          } catch {
            // Local cleanup is best effort after confirmed server abandon.
          }
        }
      }
    }
  }
  return allServerAbandoned;
}

export async function runOfficeResultSingleFlight(
  registry,
  callId,
  operation
) {
  if (
    !registry
    || typeof registry.get !== 'function'
    || typeof registry.set !== 'function'
    || typeof registry.delete !== 'function'
    || typeof operation !== 'function'
  ) {
    throw new Error(
      'Office result single-flight tracking is unavailable'
    );
  }
  const existing = registry.get(callId);
  if (existing) return existing;
  const exact = Promise.resolve().then(operation);
  registry.set(callId, exact);
  try {
    return await exact;
  } finally {
    if (registry.get(callId) === exact) {
      registry.delete(callId);
    }
  }
}

export function createQuiescedOfficeReview({
  applyEdit,
  submitOutcome,
}) {
  if (
    typeof applyEdit !== 'function'
    || typeof submitOutcome !== 'function'
  ) {
    throw new Error('Office review handlers are unavailable');
  }
  let closed = false;
  let resolved = false;
  let activeEdits = 0;
  const editWaiters = new Set();
  let resolveCompletion;
  const completion = new Promise((resolve) => {
    resolveCompletion = resolve;
  });
  const settleEditWaiters = () => {
    if (activeEdits !== 0) return;
    for (const resolve of editWaiters) resolve();
    editWaiters.clear();
  };
  const waitForActiveEdits = () => {
    if (activeEdits === 0) return Promise.resolve();
    return new Promise((resolve) => {
      editWaiters.add(resolve);
    });
  };
  const resolveWhenQuiescent = async () => {
    await waitForActiveEdits();
    if (resolved) return;
    resolved = true;
    resolveCompletion();
  };

  return Object.freeze({
    completion,
    async apply(spec) {
      if (closed) {
        throw new Error('The proposed edit review is closed');
      }
      activeEdits += 1;
      try {
        return await applyEdit(spec);
      } finally {
        activeEdits -= 1;
        settleEditWaiters();
      }
    },
    async submit(outcome) {
      if (closed) {
        throw new Error('The proposed edit review is closed');
      }
      closed = true;
      await waitForActiveEdits();
      try {
        return await submitOutcome(outcome);
      } finally {
        await resolveWhenQuiescent();
      }
    },
    cancel() {
      closed = true;
      void resolveWhenQuiescent();
    },
  });
}

export function buildMidTurnSteerPayload({
  messageContent,
  chatId,
  requestId,
  userMessageId,
  assistantMessageId,
  displayText,
  selectedModel,
  documentPrivacy = null,
} = {}) {
  return withDocumentPrivacyPayload({
    message: { role: 'user', content: messageContent },
    chatId,
    clientRequestId: requestId,
    clientMessageId: userMessageId,
    clientAssistantMessageId: assistantMessageId,
    displayText: boundedTranscriptDisplayText(displayText),
    metadata: { requestId, steered: true },
    model: selectedModel || DEFAULT_MODEL,
  }, documentPrivacy);
}

export async function deliverMidTurnSteer({
  fetchImpl = fetch,
  url,
  headers,
  payload,
  attempts = MID_TURN_STEER_ATTEMPTS,
}) {
  const body = JSON.stringify(payload);
  const identity = Object.freeze({
    requestId: payload?.clientRequestId || null,
    userMessageId: payload?.clientMessageId || null,
    proposedAssistantMessageId: payload?.clientAssistantMessageId || null,
  });
  let lastError = null;

  for (let attempt = 0; attempt < Math.max(1, attempts); attempt += 1) {
    try {
      const response = await fetchImpl(url, {
        method: 'POST',
        headers,
        body,
      });
      if (!response.ok) {
        const status = Number(response.status) || 0;
        let serverErrorCode = null;
        if (typeof response.text === 'function') {
          try {
            serverErrorCode = responseErrorCode(
              await response.text()
            );
          } catch {
            serverErrorCode = null;
          }
        }
        if (SCOPED_DELIVERY_NO_RETRY_CODES.has(serverErrorCode)) {
          const error = new Error(
            'The secure steering request outcome requires reconciliation.'
          );
          error.code = serverErrorCode;
          return {
            outcome: 'unknown',
            identity,
            response,
            status,
            serverErrorCode,
            error,
          };
        }
        if (status >= 400 && status < 500 && status !== 408) {
          return {
            outcome: 'rejected',
            identity,
            response,
            status,
            serverErrorCode,
          };
        }
        lastError = new Error(
          `Steering delivery was not confirmed: ${status || 'unknown status'}`
        );
        continue;
      }
      let data;
      try {
        data = await response.json();
      } catch (error) {
        lastError = error;
        continue;
      }
      return {
        outcome: 'accepted',
        identity,
        response,
        data: data && typeof data === 'object' ? data : {},
      };
    } catch (error) {
      lastError = error;
    }
  }

  return {
    outcome: 'unknown',
    identity,
    error: lastError,
  };
}

export function planAcceptedMidTurnSteer(
  data = {},
  proposedAssistantMessageId = null
) {
  const mode = typeof data?.mode === 'string' && data.mode
    ? data.mode
    : 'started';
  const alreadyCompleted = data?.lifecycleState === 'completed';
  return Object.freeze({
    mode,
    assistantMessageId:
      typeof data?.assistantMessageId === 'string'
        ? data.assistantMessageId
        : proposedAssistantMessageId,
    stageTurn: !alreadyCompleted && mode !== 'steered',
    renderClassicUserMessage:
      !alreadyCompleted && mode !== 'steered' && mode !== 'queued',
  });
}

export class FinanceStreamPrivacyError extends Error {
  constructor(code, message, status = null) {
    super(message);
    this.name = 'FinanceStreamPrivacyError';
    this.code = code;
    this.status = status;
  }
}

export function normalizeDocumentPrivacyMarker(marker) {
  if (!marker || typeof marker !== 'object' || Array.isArray(marker)) return null;
  const allowedKeys = ['documentId', 'providers', 'state', 'taintedAt', 'version'];
  const keys = Object.keys(marker).sort();
  if (
    keys.length !== allowedKeys.length ||
    keys.some((key, index) => key !== allowedKeys[index]) ||
    marker.version !== 1 ||
    !PRIVATE_OPERATION_ID_RE.test(marker.documentId || '') ||
    !['clean', 'licensed_data'].includes(marker.state) ||
    !Array.isArray(marker.providers)
  ) {
    return null;
  }
  const providers = [...new Set(marker.providers)];
  if (
    providers.length > 8 ||
    providers.some((provider) => (
      typeof provider !== 'string' ||
      !PRIVATE_PROVIDER_ID_RE.test(provider)
    ))
  ) {
    return null;
  }
  if (
    marker.state === 'clean' &&
    (providers.length !== 0 || marker.taintedAt !== null)
  ) {
    return null;
  }
  if (
    marker.state === 'licensed_data' &&
    (
      providers.length === 0 ||
      typeof marker.taintedAt !== 'string' ||
      !Number.isFinite(Date.parse(marker.taintedAt))
    )
  ) {
    return null;
  }
  return Object.freeze({
    version: 1,
    documentId: marker.documentId.toLowerCase(),
    state: marker.state,
    providers: Object.freeze(providers.sort()),
    taintedAt: marker.state === 'licensed_data' ? marker.taintedAt : null,
  });
}

export function encodeDocumentPrivacyHeader(marker) {
  const normalized = normalizeDocumentPrivacyMarker(marker);
  if (!normalized) {
    throw new FinanceStreamPrivacyError(
      'invalid_document_privacy',
      'A valid Office document privacy marker is required'
    );
  }
  const encoded = btoa(JSON.stringify(normalized));
  return encoded
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '');
}

export function buildSessionStreamHeaders({
  authToken = null,
  documentPrivacy = null,
  financeConnectorsEnabled = FINANCE_CONNECTORS_UI_ENABLED,
} = {}) {
  const headers = { Accept: 'text/event-stream' };
  if (financeConnectorsEnabled) {
    headers[DOCUMENT_PRIVACY_HEADER] =
      encodeDocumentPrivacyHeader(documentPrivacy);
  }
  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  }
  return headers;
}

export function withDocumentPrivacyPayload(
  payload,
  documentPrivacy,
  financeConnectorsEnabled = FINANCE_CONNECTORS_UI_ENABLED
) {
  return financeConnectorsEnabled
    ? { ...payload, documentPrivacy: documentPrivacy || null }
    : { ...payload };
}

export function createStreamAttachError(status, payload = {}) {
  if (status === 400 || status === 428) {
    return new FinanceStreamPrivacyError(
      typeof payload?.error === 'string'
        ? payload.error
        : 'invalid_document_privacy',
      typeof payload?.message === 'string'
        ? payload.message
        : 'The Office document privacy handshake was rejected',
      status
    );
  }
  const error = new Error(`Session stream attach failed: ${status}`);
  error.status = status;
  return error;
}

export async function readValidatedDocumentPrivacy(controller) {
  if (!controller || typeof controller.getDocumentPrivacyState !== 'function') {
    throw new FinanceStreamPrivacyError(
      'document_privacy_required',
      'Verify the open Office document before connecting'
    );
  }
  const marker = normalizeDocumentPrivacyMarker(
    await controller.getDocumentPrivacyState()
  );
  if (!marker) {
    throw new FinanceStreamPrivacyError(
      'invalid_document_privacy',
      'The Office document privacy marker is invalid'
    );
  }
  return marker;
}

export async function readValidatedConnectorDocumentPrivacy(controller) {
  if (
    !controller ||
    typeof controller.getConnectorDocumentPrivacyState !== 'function'
  ) {
    throw new FinanceStreamPrivacyError(
      'document_privacy_required',
      'Verify the open Office document before connecting'
    );
  }
  const marker = normalizeDocumentPrivacyMarker(
    await controller.getConnectorDocumentPrivacyState()
  );
  if (!marker) {
    throw new FinanceStreamPrivacyError(
      'invalid_document_privacy',
      'The Office document privacy marker is invalid'
    );
  }
  return marker;
}

export function assertRemoteNonPrivateToolAllowed(metadata, documentMarker) {
  if (
    metadata?.remote === true &&
    metadata?.private !== true &&
    normalizeDocumentPrivacyMarker(documentMarker)?.state !== 'clean'
  ) {
    throw new FinanceStreamPrivacyError(
      'private_document_isolation',
      'Connected Office tools require a freshly verified clean document'
    );
  }
  return true;
}

export function isLiveDataConnectorReady(response, expectedUserId = null) {
  if (
    expectedUserId &&
    response?.subjectUserId !== expectedUserId
  ) {
    return false;
  }
  const connector = response?.connector;
  return connector?.id === 'daloopa' &&
    connector.available === true &&
    connector.connected === true &&
    connector.status === 'connected' &&
    connector.errorCode == null;
}

export function normalizeFinanceLiveDataEvent(event) {
  if (
    !event ||
    event.private !== true ||
    event.connectorId !== 'daloopa' ||
    !PRIVATE_OPERATION_ID_RE.test(event.operationId || '') ||
    !Object.prototype.hasOwnProperty.call(LIVE_DATA_STAGE_LABELS, event.stage)
  ) {
    return null;
  }
  const expectedType = event.stage === 'completed'
    ? 'finance_live_data_completed'
    : ['failed', 'cancelled'].includes(event.stage)
      ? 'finance_live_data_failed'
      : 'finance_live_data_progress';
  if (event.type !== expectedType) return null;
  return Object.freeze({
    type: expectedType,
    operationId: event.operationId.toLowerCase(),
    stage: event.stage,
    message: LIVE_DATA_STAGE_LABELS[event.stage],
    active: !['completed', 'failed', 'cancelled'].includes(event.stage),
    ...(expectedType === 'finance_live_data_completed'
      ? {
          app: ['powerpoint', 'excel', 'word'].includes(event.app)
            ? event.app
            : null,
          citationCount: Number.isSafeInteger(event.citationCount) &&
            event.citationCount >= 0 &&
            event.citationCount <= 100
            ? event.citationCount
            : 0,
        }
      : {}),
    ...(expectedType === 'finance_live_data_failed'
      ? {
          code: typeof event.code === 'string' &&
            /^[a-z][a-z0-9_]{0,63}$/.test(event.code)
            ? event.code
            : 'worker_failed',
        }
      : {}),
  });
}

export function financeSessionRequiresRotation(
  sessionId,
  binding,
  documentMarker
) {
  if (!sessionId || !documentMarker?.documentId) return false;
  if (!binding || binding.sessionId !== sessionId) return true;
  return binding.documentId !== documentMarker.documentId;
}

export function selectFinancePrivateModel(model) {
  return FINANCE_PRIVATE_MODELS.has(model) ? model : FINANCE_PRIVATE_FALLBACK_MODEL;
}

const PRIVATE_OFFICE_TOOL_NAMES_BY_APP = Object.freeze({
  powerpoint: PRIVATE_FINANCE_TOOL_NAMES,
  excel: PRIVATE_FINANCE_TOOL_NAMES,
  word: PRIVATE_FINANCE_TOOL_NAMES,
});

const PRIVATE_DOCUMENT_TOOL_NAMES_BY_APP = Object.freeze({
  powerpoint: new Set([
    'get_initial_state',
    'execute_office_js',
    'screenshot_slide',
    'edit_slide_xml',
    'edit_slide_chart',
    'edit_slide_master',
    'read_slide_text',
    'edit_slide_text',
    'duplicate_slide',
    'verify_slides',
    'read_deck_content',
    'list_slide_shapes',
    'ask_user_question',
  ]),
  excel: new Set([
    'get_initial_state',
    'execute_office_js',
    'get_cell_ranges',
    'screenshot_range',
    'get_range_as_csv',
    'search_data',
    'get_all_objects',
    'set_cell_range',
    'clear_cell_range',
    'copy_to',
    'modify_sheet_structure',
    'modify_workbook_structure',
    'resize_range',
    'modify_object',
    'ask_user_question',
  ]),
  word: new Set([
    'get_initial_state',
    'execute_office_js',
    'edit_doc_text',
    'edit_doc_list',
    'read_doc_section',
    'collapse_blank_paragraphs',
    'verify_doc',
    'screenshot_document',
    'ask_user_question',
  ]),
});

export function assertPrivateOfficeToolAllowed(
  app,
  toolName,
  isPrivate,
  financeOfficeProtocol = false
) {
  if (isPrivate !== true) return true;
  const allowed = financeOfficeProtocol === true
    ? PRIVATE_OFFICE_TOOL_NAMES_BY_APP[app]
    : PRIVATE_DOCUMENT_TOOL_NAMES_BY_APP[app];
  if (!allowed?.has(toolName)) {
    throw new Error('Private Office tool is not allowed for this host');
  }
  return true;
}

export function validatePrivateOfficeEventMetadata(
  app,
  toolName,
  metadata,
  documentMarker,
  toolArgs = null,
  activeLiveDataOperationId = null
) {
  if (metadata?.private !== true) return Object.freeze({ private: false });
  if (
    !PRIVATE_OPERATION_ID_RE.test(metadata.documentId || '') ||
    metadata.documentId.toLowerCase() !== documentMarker?.documentId?.toLowerCase()
  ) {
    throw new Error('Private Office document identity does not match');
  }
  const financeOfficeProtocol = metadata.financeOfficeProtocol === true;
  assertPrivateOfficeToolAllowed(
    app,
    toolName,
    true,
    financeOfficeProtocol
  );
  let operationId = null;
  if (financeOfficeProtocol) {
    const metadataOperationId = String(metadata.operationId || '').toLowerCase();
    const argsOperationId = String(toolArgs?.operationId || '').toLowerCase();
    const activeOperationId =
      String(activeLiveDataOperationId || '').toLowerCase();
    if (
      !PRIVATE_OPERATION_ID_RE.test(metadataOperationId) ||
      !PRIVATE_OPERATION_ID_RE.test(argsOperationId) ||
      !PRIVATE_OPERATION_ID_RE.test(activeOperationId) ||
      metadataOperationId !== argsOperationId ||
      metadataOperationId !== activeOperationId
    ) {
      throw new Error('Private Office operation identity does not match');
    }
    operationId = metadataOperationId;
  }
  if (
    !financeOfficeProtocol &&
    documentMarker?.state !== 'licensed_data'
  ) {
    throw new Error('Private document tools require a licensed document marker');
  }
  return Object.freeze({
    private: true,
    financeOfficeProtocol,
    documentId: metadata.documentId.toLowerCase(),
    ...(operationId ? { operationId } : {}),
  });
}

export function upgradeTurnEntryToEphemeral(entry) {
  if (!entry || typeof entry !== 'object') return false;
  entry.ephemeral = true;
  return true;
}

export function routeTurnSink(
  ephemeral,
  { privateSink, persistentSink }
) {
  if (
    typeof privateSink !== 'function' ||
    typeof persistentSink !== 'function'
  ) {
    throw new TypeError('Both private and persistent turn sinks are required');
  }
  return ephemeral === true ? privateSink() : persistentSink();
}

export function turnUsesMemoryOnlySink({
  ephemeral = false,
  privacyPending = false,
} = {}) {
  return ephemeral === true || privacyPending === true;
}

export function shouldStageTurnForPrivacy({
  ephemeral = false,
  financeConnectorsEnabled = FINANCE_CONNECTORS_UI_ENABLED,
} = {}) {
  return financeConnectorsEnabled === true && ephemeral !== true;
}

export function isTurnReadyForStreamEvents(entry) {
  return Boolean(entry) && entry.eventReady !== false;
}

export function shouldInitializeTurnBeforeToolDispatch(event, {
  hasTurnState = false,
  hasEventReadyTurn = false,
  hasPendingConductorTurn = false,
} = {}) {
  return event?.type === 'tool_call_request' &&
    event.private !== true &&
    event.remote !== true &&
    hasTurnState !== true &&
    (hasEventReadyTurn === true || hasPendingConductorTurn === true);
}

export function terminalLiveDataSessionState(current, {
  operationId = null,
  stage = 'failed',
  code = 'private_stream_closed',
  message = LIVE_DATA_STAGE_LABELS.failed,
} = {}) {
  if (!current || current.active !== true) return current;
  const expectedOperationId = String(operationId || '').toLowerCase();
  const currentOperationId = String(current.operationId || '').toLowerCase();
  if (
    expectedOperationId &&
    currentOperationId &&
    expectedOperationId !== currentOperationId
  ) {
    return current;
  }
  const terminalStage = ['completed', 'failed', 'cancelled'].includes(stage)
    ? stage
    : 'failed';
  return {
    ...current,
    type: terminalStage === 'completed'
      ? 'finance_live_data_completed'
      : 'finance_live_data_failed',
    stage: terminalStage,
    message,
    active: false,
    ...(terminalStage === 'completed' ? {} : { code }),
  };
}

export function createFinanceOfficeControllerIfEnabled({
  app,
  officeBridge,
  financeConnectorsEnabled = FINANCE_CONNECTORS_UI_ENABLED,
  controllerFactory = (options) => new FinanceOfficeController(options),
} = {}) {
  if (!financeConnectorsEnabled || !officeBridge) return null;
  return controllerFactory({ app });
}

export async function attestPendingTurnForPersistence(
  entry,
  { persistUserMessage, removeMemoryMessages }
) {
  if (!entry?.privacyPending || entry.ephemeral === true) return false;
  if (
    !entry.stagedUserMessage ||
    typeof persistUserMessage !== 'function' ||
    typeof removeMemoryMessages !== 'function'
  ) {
    throw new FinanceStreamPrivacyError(
      'turn_privacy_attestation_invalid',
      'The turn cannot cross the persistent transcript boundary'
    );
  }
  await persistUserMessage(entry.stagedUserMessage);
  removeMemoryMessages([
    entry.stagedUserMessage.id,
    entry.aiMessageId,
  ].filter(Boolean));
  entry.stagedUserMessage = null;
  entry.privacyPending = false;
  return true;
}

export function shouldSuppressConductorTurn({
  documentMarker,
  privateSessionActive = false,
  officePresent = false,
  financeConnectorsEnabled = FINANCE_CONNECTORS_UI_ENABLED,
} = {}) {
  if (!financeConnectorsEnabled) return false;
  return privateSessionActive === true ||
    documentMarker?.state === 'licensed_data' ||
    (officePresent === true && !documentMarker);
}

export function canConnectConductor(
  documentMarker,
  resolution = 'ready',
  financeConnectorsEnabled = FINANCE_CONNECTORS_UI_ENABLED
) {
  if (!financeConnectorsEnabled) return true;
  return resolution === 'ready' &&
    normalizeDocumentPrivacyMarker(documentMarker)?.state === 'clean';
}

const PRIVATE_LIVE_DATA_EVENT_TYPES = Object.freeze(new Set([
  'finance_live_data_progress',
  'finance_live_data_completed',
  'finance_live_data_failed',
]));

export function classifyPrivateStreamEvent(event, {
  currentDocumentId = null,
  hasPendingTurn = false,
  activePrivateTurn = false,
  activeLiveDataOperationId = null,
  registeredPrivateCall = false,
} = {}) {
  if (event?.private !== true) {
    return Object.freeze({
      private: false,
      accepted: true,
      activate: false,
      reason: null,
    });
  }
  const expectedDocumentId = String(currentDocumentId || '').toLowerCase();
  const eventDocumentId = String(event.documentId || '').toLowerCase();
  if (
    !PRIVATE_OPERATION_ID_RE.test(eventDocumentId) ||
    eventDocumentId !== expectedDocumentId
  ) {
    return Object.freeze({
      private: true,
      accepted: false,
      activate: false,
      reason: 'document_privacy_mismatch',
    });
  }

  let accepted = false;
  let activate = false;
  if (PRIVATE_LIVE_DATA_EVENT_TYPES.has(event.type)) {
    accepted = Boolean(
      activeLiveDataOperationId &&
      String(event.operationId || '').toLowerCase() ===
        String(activeLiveDataOperationId).toLowerCase()
    );
    activate = accepted;
  } else if (event.type === 'tool_call_cancel') {
    accepted = event.financeOfficeProtocol === true
      ? Boolean(
          registeredPrivateCall &&
          activeLiveDataOperationId &&
          String(event.operationId || '').toLowerCase() ===
            String(activeLiveDataOperationId).toLowerCase()
        )
      : registeredPrivateCall;
  } else if (event.type === 'tool_call_request') {
    accepted = event.financeOfficeProtocol === true
      ? Boolean(
          activeLiveDataOperationId &&
          String(event.operationId || '').toLowerCase() ===
            String(activeLiveDataOperationId).toLowerCase()
        )
      : hasPendingTurn || activePrivateTurn;
    activate = accepted;
  } else {
    accepted = hasPendingTurn || activePrivateTurn;
    activate = accepted;
  }
  return Object.freeze({
    private: true,
    accepted,
    activate,
    reason: accepted ? null : 'private_event_unowned',
  });
}

export function buildSessionStreamUrl(
  streamingBaseUrl,
  sessionId,
  chatId
) {
  const params = new URLSearchParams();
  params.set('chatId', chatId || '');
  return `${streamingBaseUrl}/sessions/${sessionId}/stream?${params.toString()}`;
}

// =============================================================================
// SECTION 1: UI EFFECTS
// =============================================================================

/**
/**
 * useTypewriterPlaceholder - Typewriter placeholder animation
 *
 * Manages:
 * - Cycling through placeholder prompts
 * - Cursor blinking animation
 * - Smooth transitions between prompts
 */
export const useTypewriterPlaceholder = (
  prompts,
  { isEnabled = true, holdDelay = 2600, cursorBlinkInterval = 650 } = {},
) => {
  const [displayText, setDisplayText] = useState('');
  const [cursorVisible, setCursorVisible] = useState(true);
  const [cycleKey, setCycleKey] = useState(0);
  const stateRef = useRef({ promptIndex: 0 });

  const promptsKey = useMemo(() => (
    Array.isArray(prompts) ? prompts.filter(Boolean).join('|') : ''
  ), [prompts]);

  const safePrompts = useMemo(() => (
    Array.isArray(prompts) ? prompts.filter(Boolean) : []
  ), [promptsKey]);

  useEffect(() => {
    stateRef.current = { promptIndex: 0 };
    setDisplayText('');
    setCursorVisible(true);
    setCycleKey(0);
  }, [promptsKey]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return () => {};
    }

    if (!isEnabled || safePrompts.length === 0) {
      setDisplayText('');
      setCursorVisible(true);
      stateRef.current = { promptIndex: 0 };
      setCycleKey(0);
      return () => {};
    }

    let holdTimeoutId = null;
    let transitionTimeoutId = null;
    let cancelled = false;
    const transitionDelay = 160;

    const scheduleNext = () => {
      if (cancelled) {
        return;
      }

      if (holdTimeoutId) {
        window.clearTimeout(holdTimeoutId);
        holdTimeoutId = null;
      }

      if (transitionTimeoutId) {
        window.clearTimeout(transitionTimeoutId);
        transitionTimeoutId = null;
      }

      const currentIndex = stateRef.current.promptIndex % safePrompts.length;
      const prompt = safePrompts[currentIndex] || '';
      setDisplayText(prompt);
      setCursorVisible(true);
      setCycleKey((key) => key + 1);

      stateRef.current.promptIndex = (currentIndex + 1) % safePrompts.length;

      holdTimeoutId = window.setTimeout(() => {
        if (cancelled) {
          return;
        }
        setCursorVisible(false);
        setDisplayText('');
        transitionTimeoutId = window.setTimeout(() => {
          if (!cancelled) {
            scheduleNext();
          }
        }, transitionDelay);
      }, holdDelay);
    };

    stateRef.current = { promptIndex: 0 };
    scheduleNext();

    return () => {
      cancelled = true;
      if (holdTimeoutId) {
        window.clearTimeout(holdTimeoutId);
      }
      if (transitionTimeoutId) {
        window.clearTimeout(transitionTimeoutId);
      }
    };
  }, [holdDelay, isEnabled, safePrompts]);

  useEffect(() => {
    if (!isEnabled || !displayText) {
      setCursorVisible(true);
      return () => {};
    }

    const blinkId = window.setInterval(() => {
      setCursorVisible((prev) => !prev);
    }, cursorBlinkInterval);

    return () => {
      window.clearInterval(blinkId);
      setCursorVisible(true);
    };
  }, [cursorBlinkInterval, displayText, isEnabled]);

  return {
    displayText,
    cursorVisible,
    cycleKey,
  };
};

// =============================================================================
// SECTION 3: CHAT COMPOSER
// =============================================================================

const FILE_TYPE_CONFIG = {
  // Images
  'image/jpeg': { name: 'JPG', icon: ImageIcon },
  'image/png': { name: 'PNG', icon: ImageIcon },
  'image/gif': { name: 'GIF', icon: ImageIcon },
  'image/webp': { name: 'WEBP', icon: ImageIcon },
  'image/svg+xml': { name: 'SVG', icon: ImageIcon },
  // PDFs (use the actual "PDF"-rendered glyph, not a generic doc-scanner icon)
  'application/pdf': { name: 'PDF', icon: PictureAsPdfIcon },
  // Word docs
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { name: 'DOCX', icon: DescriptionIcon },
  'application/msword': { name: 'DOC', icon: DescriptionIcon },
  // Spreadsheets
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': { name: 'XLSX', icon: TableChartIcon },
  'application/vnd.ms-excel': { name: 'XLS', icon: TableChartIcon },
  'text/csv': { name: 'CSV', icon: TableChartIcon },
  // Presentations
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': { name: 'PPTX', icon: DescriptionIcon },
  // Plain text / code
  'text/plain': { name: 'TXT', icon: DescriptionIcon },
  'text/markdown': { name: 'MD', icon: DescriptionIcon },
  'application/json': { name: 'JSON', icon: CodeIcon },
  // Archives
  'application/zip': { name: 'ZIP', icon: ArchiveIcon },
  'application/x-zip-compressed': { name: 'ZIP', icon: ArchiveIcon }
};

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const mapFileTypeInfo = (type, name) => {
  const safeType = typeof type === 'string' ? type : '';
  const safeName = typeof name === 'string' ? name.toLowerCase() : '';

  if (FILE_TYPE_CONFIG[safeType]) return FILE_TYPE_CONFIG[safeType];

  // MIME-prefix matching first (most reliable)
  if (safeType.startsWith('image/')) return { name: 'IMAGE', icon: ImageIcon };
  if (safeType.startsWith('video/')) return { name: 'VIDEO', icon: MovieIcon };
  if (safeType.startsWith('audio/')) return { name: 'AUDIO', icon: MusicNoteIcon };

  // Extension-based fallback for browsers that don't report a MIME type
  if (safeType.includes('pdf') || safeName.endsWith('.pdf')) return { name: 'PDF', icon: PictureAsPdfIcon };
  if (safeType.includes('word') || safeName.endsWith('.docx') || safeName.endsWith('.doc')) return { name: 'DOC', icon: DescriptionIcon };
  if (safeType.includes('sheet') || safeType.includes('excel') || safeName.endsWith('.xlsx') || safeName.endsWith('.xls') || safeName.endsWith('.csv')) return { name: 'SHEET', icon: TableChartIcon };
  if (safeType.includes('presentation') || safeName.endsWith('.pptx') || safeName.endsWith('.ppt')) return { name: 'PPT', icon: DescriptionIcon };
  if (safeType.includes('zip') || safeName.endsWith('.zip') || safeName.endsWith('.tar') || safeName.endsWith('.gz')) return { name: 'ZIP', icon: ArchiveIcon };
  if (safeType.includes('json') || safeName.endsWith('.json')) return { name: 'JSON', icon: CodeIcon };
  if (safeName.endsWith('.js') || safeName.endsWith('.ts') || safeName.endsWith('.py') || safeName.endsWith('.html') || safeName.endsWith('.css')) return { name: 'CODE', icon: CodeIcon };
  if (safeType.includes('text') || safeName.endsWith('.txt') || safeName.endsWith('.md')) return { name: 'TXT', icon: DescriptionIcon };

  const fallback = safeType.split('/')[1]?.toUpperCase() || 'FILE';
  return { name: fallback, icon: UploadFileIcon };
};

/**
 * useChatComposer - Chat composer input and file attachments
 *
 * Manages:
 * - Textarea input and auto-sizing
 * - File upload and previews
 * - File type detection and formatting
 * - Keyboard handling
 */
export const useChatComposer = ({ useGlobalState = true } = {}) => {
  const theme = useTheme();
  const { chatComposerValue, setChatComposerValue } = useUI();
  const [localComposerValue, setLocalComposerValue] = useState('');

  const shouldUseGlobalState = useGlobalState !== false;
  const composerValue = shouldUseGlobalState ? chatComposerValue : localComposerValue;
  const setComposerValue = shouldUseGlobalState ? setChatComposerValue : setLocalComposerValue;

  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [filePreviews, setFilePreviews] = useState([]);
  const [showUploadDropdown, setShowUploadDropdown] = useState(false);

  // 44px = 20px line + 12px top + 12px bottom (border-box). MUST match
  // MessageTextarea's padding/line-height in chatStyles.js: this value is set
  // as the element's INLINE height/min-height, so if it is smaller than
  // padding + one line, the empty composer clips its own text and the CSS
  // padding never visibly applies.
  const textareaMinHeight = useMemo(() => theme.spacing(5.5), [theme]);
  const textareaMaxHeight = useMemo(() => theme.spacing(30), [theme]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = textareaMinHeight;
      textareaRef.current.style.minHeight = textareaMinHeight;
      textareaRef.current.style.overflow = 'hidden';
    }
  }, [textareaMinHeight]);

  const setInput = useCallback((value) => {
    setComposerValue(value);
  }, [setComposerValue]);

  const appendInput = useCallback((value, { separator = ' ' } = {}) => {
    setComposerValue((prev) => {
      if (!prev) return value;
      return `${prev}${separator}${value}`;
    });
  }, [setComposerValue]);

  const applyTextareaAutoSize = useCallback((textarea) => {
    if (!textarea) return;

    // Reset height to minimum first so scrollHeight reflects content height
    // for the CURRENT value (not stale layout from a prior larger value).
    textarea.style.height = textareaMinHeight;

    // If empty, stay at minimum
    if (!textarea.value || textarea.value.trim() === '') {
      textarea.style.overflow = 'hidden';
      return;
    }

    // Force browser reflow by reading offsetHeight
    void textarea.offsetHeight;

    // Now set to auto and measure actual content height
    textarea.style.height = 'auto';
    const scrollHeight = textarea.scrollHeight;
    const numericMin = parseFloat(textareaMinHeight);
    const numericMax = parseFloat(textareaMaxHeight);

    // Stay pinned to minHeight for single-line content
    if (scrollHeight <= numericMin) {
      textarea.style.height = textareaMinHeight;
      textarea.style.overflow = 'hidden';
      return;
    }

    const clampedHeight = Math.min(scrollHeight, numericMax);
    textarea.style.height = `${clampedHeight}px`;
    textarea.style.overflow = scrollHeight > numericMax ? 'auto' : 'hidden';
  }, [textareaMinHeight, textareaMaxHeight]);

  // Re-run autosize whenever the controlled value changes — including the
  // initial render with a persisted draft, paste-from-clipboard, programmatic
  // setInput() calls, and value rehydration after navigation. Without this,
  // the textarea was stuck at its single-line minimum for any content
  // that arrived without a synthetic `input` event, so long prompts looked
  // clipped to a couple of lines.
  useEffect(() => {
    if (textareaRef.current) {
      applyTextareaAutoSize(textareaRef.current);
    }
  }, [composerValue, applyTextareaAutoSize]);

  const handleInputChange = useCallback((event) => {
    const { value } = event.target;
    setComposerValue(value);
    applyTextareaAutoSize(event.target);
  }, [applyTextareaAutoSize, setComposerValue]);

  const handleKeyDown = useCallback((event, { onSend, disabled }) => {
    if (disabled) {
      return;
    }

    if (event.key === 'Backspace' || event.key === 'Delete') {
      return;
    }

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (typeof onSend === 'function') {
        onSend();
      }
    }
  }, []);

  const resetTextareaHeight = useCallback(() => {
    if (!textareaRef.current) return;
    textareaRef.current.style.height = textareaMinHeight;
    textareaRef.current.style.minHeight = textareaMinHeight;
    textareaRef.current.style.overflow = 'hidden';
  }, [textareaMinHeight]);

  const clearFilePreview = useCallback((preview) => {
    if (preview?.imageUrl) {
      URL.revokeObjectURL(preview.imageUrl);
    }
  }, []);

  const clearAttachments = useCallback(() => {
    setSelectedFiles([]);
    setFilePreviews((prev) => {
      prev.forEach(clearFilePreview);
      return [];
    });
  }, [clearFilePreview]);

  // Shared ingestion for the file picker AND clipboard paste: append files to
  // the current attachment set with previews.
  const addAttachmentFiles = useCallback((files) => {
    const fileArray = Array.from(files || []).filter(Boolean);
    if (fileArray.length === 0) return;

    const timestamp = Date.now();
    const newPreviews = fileArray.map((file, index) => {
      const basePreview = {
        id: `file-${timestamp}-${index}`,
        name: file.name,
        type: file.type,
        size: file.size,
        uploadStatus: 'ready',
        uploadProgress: 0,
        storageUrl: null
      };

      if (file.type.startsWith('image/')) {
        try {
          basePreview.imageUrl = URL.createObjectURL(file);
        } catch (error) {
          console.warn('Failed to create preview URL', error);
        }
      }

      return basePreview;
    });

    setSelectedFiles((prev) => [...prev, ...fileArray]);
    setFilePreviews((prev) => [...prev, ...newPreviews]);
    setShowUploadDropdown(false);
  }, []);

  const handleFileUpload = useCallback((event) => {
    addAttachmentFiles(event.target.files);
    event.target.value = '';
  }, [addAttachmentFiles]);

  const setAttachmentsFromFiles = useCallback((files = []) => {
    const fileArray = Array.isArray(files) ? files : [];

    setSelectedFiles(fileArray);
    setFilePreviews((prev) => {
      prev.forEach(clearFilePreview);
      const timestamp = Date.now();

      return fileArray.map((file, index) => {
        const preview = {
          id: `file-${timestamp}-${index}`,
          name: file.name,
          type: file.type,
          size: file.size,
          uploadStatus: 'ready',
          uploadProgress: 0,
          storageUrl: null
        };

        if (file.type?.startsWith('image/')) {
          try {
            preview.imageUrl = URL.createObjectURL(file);
          } catch (error) {
            console.warn('Failed to create preview URL from provided file', error);
          }
        }

        return preview;
      });
    });
  }, [clearFilePreview]);

  const removeAttachmentAt = useCallback((index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setFilePreviews((prev) => {
      const next = prev.filter((_, i) => i !== index);
      const removed = prev[index];
      if (removed) {
        clearFilePreview(removed);
      }
      return next;
    });
  }, [clearFilePreview]);

  const getFileTypeInfo = useCallback((type, name) => mapFileTypeInfo(type, name), []);

  return {
    input: composerValue,
    setInput,
    appendInput,
    textareaRef,
    textareaMinHeight,
    textareaMaxHeight,
    resetTextareaHeight,
    applyTextareaAutoSize,
    handleInputChange,
    handleKeyDown,
    fileInputRef,
    selectedFiles,
    filePreviews,
    showUploadDropdown,
    setShowUploadDropdown,
    handleFileUpload,
    addAttachmentFiles,
    setAttachmentsFromFiles,
    removeAttachmentAt,
    clearAttachments,
    formatFileSize,
    getFileTypeInfo
  };
};

// =============================================================================
// SECTION 3: STREAMING SESSION (useStreamingSession)
// =============================================================================

const generateMessageId = () => `msg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const INITIAL_STREAM_STATUS = 'Thinking';

const withTimeout = (promise, timeoutMs, label) => new Promise((resolve) => {
  let settled = false;
  const timer = setTimeout(() => {
    if (settled) return;
    settled = true;
    console.warn(`[hooks] ${label} timed out after ${timeoutMs}ms`);
    resolve(null);
  }, timeoutMs);
  Promise.resolve(promise).then(
    (value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(value);
    },
    (error) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      console.warn(`[hooks] ${label} failed:`, error?.message);
      resolve(null);
    }
  );
});

// apply_template: which template file types each Office host can ingest.
const TEMPLATE_EXTENSIONS_BY_APP = {
  powerpoint: ['.pptx', '.potx'],
  excel: ['.xlsx', '.xltx'],
  word: ['.docx', '.dotx'],
};

const templateMatchesApp = (fileName, appKey) => {
  const lower = String(fileName || '').toLowerCase();
  return (TEMPLATE_EXTENSIONS_BY_APP[appKey] || []).some((ext) => lower.endsWith(ext));
};

// Binary-safe base64 for Office insert*FromBase64 APIs (chunked so large
// template files cannot blow the call stack).
const arrayBufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  const CHUNK = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
};

export const useStreamingSession = ({
  currentUser,
  currentPresentation,
  repository,
  selectedModel,
  selectedSlides = [],
  analytics,
  chatData,
  composer,
  powerpointBridge = null,
  app = 'powerpoint'  // Office host surface: 'powerpoint' | 'excel' | 'word'. Routes tool calls
                      // to the matching bridge methods and is sent on session create.
}) => {
  const {
    isChatStreaming,
    setIsChatStreaming
  } = useUI();

  const streamingStateRef = useRef(null);
  const isChatStreamingRef = useRef(false);
  const abortControllerRef = useRef(null);
  const sessionStreamRef = useRef({
    sessionId: null,
    controller: null,
    promise: null,
    startPromise: null,
    isActive: false,
    streamUrl: null
  });
  const pendingTurnQueueRef = useRef([]);
  const pendingTurnWaitersRef = useRef([]);
  const unresolvedSteerDeliveryRef = useRef(null);
  const unresolvedInitialDeliveryRef = useRef(null);
  const activeTurnRef = useRef(null);
  const [hasBootstrappedInitialPrompt, setHasBootstrappedInitialPrompt] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const autoScrollRef = useRef(true);
  const messageWriteQueueRef = useRef(Promise.resolve());
  const officeToolQueueRef = useRef(Promise.resolve());
  const officeToolRunsByCallRef = useRef(new Map());
  const lockerOfficeJournalRef = useRef(null);
  const lockerOfficeOperationsByCallRef = useRef(new Map());
  const preStartAbandonedOfficeCallIdsRef = useRef(new Set());
  const reconciledPreStartOfficeCallIdsRef = useRef(new Set());
  const preStartOfficeResultPostsRef = useRef(new Map());
  const unresolvedOfficeRecoveryRef = useRef([]);
  // Operations the user has explicitly dismissed from the banner. Keyed by
  // operation id so a genuinely new unresolved operation still raises it.
  //
  // Persisted, because this was a useRef and the pane reloads: an operation the
  // server still reports as pending is never dropped from the journal (that
  // record is deliberately kept), so an in-memory dismissal came back on every
  // reopen and the banner was unescapable in practice. Persisting the
  // dismissal hides the notice without touching the journal entry, which is
  // the split we actually want: the user stops being nagged, the record of a
  // possibly-live write survives.
  const dismissedOfficeRecoveryIdsRef = useRef(null);
  if (dismissedOfficeRecoveryIdsRef.current === null) {
    dismissedOfficeRecoveryIdsRef.current = readDismissedOfficeRecoveryIds();
  }
  const [
    officeOperationRecoveryRequired,
    setOfficeOperationRecoveryRequired,
  ] = useState(false);
  const [
    officeOperationRecovery,
    setOfficeOperationRecovery,
  ] = useState(null);
  const cancelledRemoteToolCallIdsRef = useRef(new Set());
  const privateOfficeCancellationRegistryRef = useRef(null);
  if (!privateOfficeCancellationRegistryRef.current) {
    privateOfficeCancellationRegistryRef.current = createPrivateOfficeCancellationRegistry({
      onCancel: (callId) => {
        cancelledRemoteToolCallIdsRef.current.add(callId);
        setTimeout(() => cancelledRemoteToolCallIdsRef.current.delete(callId), 10 * 60 * 1000);
      },
    });
  }
  const getLockerOfficeJournal = useCallback(() => {
    if (lockerOfficeJournalRef.current) {
      return lockerOfficeJournalRef.current;
    }
    if (typeof window === 'undefined' || !window.localStorage) {
      throw new Error('Secure Office operation recovery storage is unavailable');
    }
    lockerOfficeJournalRef.current =
      new LockerOfficeQuiescenceJournal({
        storage: window.localStorage,
      });
    return lockerOfficeJournalRef.current;
  }, []);
  const setOfficeRecoveryEntries = useCallback((entries) => {
    const exactEntries = (Array.isArray(entries) ? entries : [])
      .filter(
        (entry) =>
          !dismissedOfficeRecoveryIdsRef.current.has(
            entry?.operationId
          )
      );
    unresolvedOfficeRecoveryRef.current = exactEntries;
    setOfficeOperationRecoveryRequired(exactEntries.length > 0);
    const first = exactEntries[0] || null;
    setOfficeOperationRecovery(first
      ? Object.freeze({
        operationId: first.operationId,
        chatId: first.chatId,
        callId: first.callId,
        status: first.status,
        // The banner needs to say which of the two situations this is: a
        // write the server still reports as live, or a local record the
        // server never backed. They have different exits.
        serverConfirmedPending:
          first.serverConfirmedPending === true,
        reconcileExhausted: isReconcileExhausted(first),
      })
      : null);
  }, []);
  const confirmDurableOfficeOperationComplete = useCallback(
    async (operation) => {
      if (!currentUser) {
        return Object.freeze({
          completed: false,
          outcome: RECONCILE_OUTCOMES.UNRECONCILED,
          status: 0,
        });
      }
      const authToken = await currentUser.getIdToken();
      const streamingBaseUrl =
        process.env.REACT_APP_STREAMING_SERVICE_URL
        || 'https://claude-streaming-service-103376361923.us-central1.run.app';
      return pollLockerOfficeOperationCompletion({
        baseUrl: streamingBaseUrl,
        authToken,
        operation,
      });
    },
    [currentUser]
  );
  const transitionDurableOfficeOperation = useCallback(
    async (
      operation,
      transition,
      attempts = 2,
      confirmReviewed = false,
      confirmNoActivePromise = false
    ) => {
      if (!currentUser) {
        throw new Error(
          'Secure Office operation identity is unavailable'
        );
      }
      const authToken = await currentUser.getIdToken();
      const streamingBaseUrl =
        process.env.REACT_APP_STREAMING_SERVICE_URL
        || 'https://claude-streaming-service-103376361923.us-central1.run.app';
      return transitionLockerOfficeOperation({
        baseUrl: streamingBaseUrl,
        authToken,
        operation,
        transition,
        attempts,
        confirmReviewed,
        confirmNoActivePromise,
      });
    },
    [currentUser]
  );
  /**
   * Ask the server about each entry that is waiting on explicit resolution and
   * keep only the ones it still backs. An operation the server has no record of
   * is dropped here, which is what clears the banner without user action. An
   * operation the server reports as pending is kept, whatever the ceiling says:
   * that is a live write to the open file and only the resolve path clears it.
   */
  const reconcileUnresolvedOfficeEntries = useCallback(
    async (journal, entries) => {
      const surviving = [];
      for (const entry of entries) {
        if (isReconcileExhausted(entry)) {
          surviving.push(entry);
          continue;
        }
        let answer = null;
        try {
          answer =
            await confirmDurableOfficeOperationComplete(entry);
        } catch {
          // A malformed entry or a dead connection proves nothing. It counts
          // as one inconclusive attempt against the ceiling, nothing more.
          answer = null;
        }
        if (answer?.completed === true) {
          try {
            journal.removeUnknownToServer(entry);
            continue;
          } catch (error) {
            console.warn('[hooks] Secure Office operation cleanup failed:', {
              operationId: entry.operationId,
              code:
                error?.code
                || error?.name
                || 'locker_office_cleanup_failed',
            });
          }
        }
        let updated = entry;
        try {
          updated =
            journal.recordReconcileOutcome(
              entry.operationId,
              answer?.outcome
                || RECONCILE_OUTCOMES.UNRECONCILED
            ) || entry;
        } catch {
          // Recovery storage may be unwritable; the fence is still closed.
        }
        surviving.push(updated);
      }
      return surviving;
    },
    [confirmDurableOfficeOperationComplete]
  );
  const flushSettledLockerOfficeOperations = useCallback(async () => {
    if (!currentUser?.uid) {
      unresolvedOfficeRecoveryRef.current = [];
      setOfficeRecoveryEntries([]);
      return [];
    }
    let journal;
    try {
      journal = getLockerOfficeJournal();
      // Retire entries the server never backed before planning anything, so a
      // record that outlived every server trace of it cannot reach the banner.
      journal.pruneUnbacked({ userId: currentUser.uid });
      const recovery = journal.recoveryPlan({
        userId: currentUser.uid,
      });
      const activeOperationIds = new Set(
        [...lockerOfficeOperationsByCallRef.current.values()]
          .flat()
          .map((operation) => operation.operationId)
      );
      const alreadyUnresolvedIds = new Set(
        unresolvedOfficeRecoveryRef.current.map(
          (entry) => entry.operationId
        )
      );
      const unresolved = recovery.requiresExplicitResolution
        .filter(
          (entry) =>
            !activeOperationIds.has(entry.operationId)
            || alreadyUnresolvedIds.has(entry.operationId)
        );
      for (
        const entry
        of recovery.safeToAbandon.filter(
          (candidate) =>
            !activeOperationIds.has(candidate.operationId)
        )
      ) {
        try {
          await transitionDurableOfficeOperation(
            entry,
            'abandon',
            3
          );
          journal.abandonSafe(entry.operationId);
        } catch (error) {
          if (error?.code === 'locker_office_operation_running') {
            try {
              unresolved.push(
                journal.markRunning(entry.operationId)
              );
            } catch {
              unresolved.push(entry);
            }
          }
        }
      }
      const completionResults = await journal.flushCompleted({
        userId: currentUser.uid,
        confirmComplete:
          confirmDurableOfficeOperationComplete,
      });
      const completedOperationIds = new Set(
        completionResults
          .filter((result) => result.completed)
          .map((result) => result.operationId)
      );
      const pendingCompletionIds = new Set(
        completionResults
          .filter((result) => !result.completed)
          .map((result) => result.operationId)
      );
      // Read the entries back after the poll: flushCompleted just recorded
      // what the server said, and the banner has to reflect that, not the
      // snapshot taken before the request went out.
      const reconciledById = new Map(
        journal.list({ userId: currentUser.uid }).map(
          (entry) => [entry.operationId, entry]
        )
      );
      const pendingCompletionEntries =
        recovery.pollExactCompletion
          .filter((entry) =>
            pendingCompletionIds.has(entry.operationId)
          )
          .map((entry) =>
            reconciledById.get(entry.operationId) || entry
          );
      // A running entry never polled anything before this, so a banner for an
      // operation the server had already forgotten could not clear itself.
      // The quiescence endpoint is a read-only probe, so asking is safe.
      const survivingUnresolved =
        await reconcileUnresolvedOfficeEntries(
          journal,
          unresolved.map(
            (entry) =>
              reconciledById.get(entry.operationId) || entry
          )
        );
      setOfficeRecoveryEntries([
        ...survivingUnresolved,
        ...pendingCompletionEntries,
      ]);
      if (completedOperationIds.size > 0) {
        for (
          const [callId, operations]
          of lockerOfficeOperationsByCallRef.current.entries()
        ) {
          const remaining = operations.filter(
            (operation) =>
              !completedOperationIds.has(operation.operationId)
          );
          if (remaining.length > 0) {
            lockerOfficeOperationsByCallRef.current.set(
              callId,
              remaining
            );
          } else {
            lockerOfficeOperationsByCallRef.current.delete(callId);
          }
        }
      }
      return completionResults;
    } catch (error) {
      unresolvedOfficeRecoveryRef.current = [];
      setOfficeOperationRecovery(null);
      // An unreadable journal still warns the user, but the warning has to be
      // dismissible. Its predecessor could only be cleared from dev tools.
      setOfficeOperationRecoveryRequired(
        !dismissedOfficeRecoveryIdsRef.current.has(
          OFFICE_RECOVERY_UNAVAILABLE_KEY
        )
      );
      console.warn('[hooks] Secure Office operation recovery is unavailable:', {
        code: error?.code || error?.name || 'locker_office_recovery_failed',
      });
      return [];
    }
  }, [
    confirmDurableOfficeOperationComplete,
    currentUser?.uid,
    getLockerOfficeJournal,
    reconcileUnresolvedOfficeEntries,
    setOfficeRecoveryEntries,
    transitionDurableOfficeOperation,
  ]);
  const resolveOfficeOperationRecovery = useCallback(async ({
    operationId,
    confirmReviewed,
  } = {}) => {
    if (confirmReviewed !== true) {
      throw new Error(
        'Review the open file before resolving this Office operation'
      );
    }
    const exact = unresolvedOfficeRecoveryRef.current.find(
      (entry) => entry.operationId === operationId
    );
    if (
      !exact
      || (
        exact.status !== 'running'
        && exact.status !== 'result_delivered'
      )
    ) {
      throw new Error(
        'The exact unresolved Office operation is unavailable'
      );
    }
    assertNoActiveOfficePromise(
      officeToolRunsByCallRef.current,
      exact.operationId
    );
    let result;
    try {
      result = await transitionDurableOfficeOperation(
        exact,
        'resolve-unknown',
        1,
        true,
        true
      );
    } catch (error) {
      if (
        error?.code === 'locker_office_resolution_grace_active'
      ) {
        throw new Error(
          'This operation is still inside its safety window. Review the file and try again after the wait period.'
        );
      }
      if (
        error?.code === 'locker_office_operation_active'
        || error?.code === 'locker_office_operation_running'
      ) {
        throw new Error(
          'Office is still finishing this exact operation. Wait before resolving it.'
        );
      }
      throw error;
    }
    if (
      result?.success !== true
      || result?.resolved !== true
    ) {
      throw new Error(
        'Secure Office operation resolution was not confirmed'
      );
    }
    const journal = getLockerOfficeJournal();
    journal.removeExplicitlyResolved({
      ...exact,
      confirmReviewed: true,
    });
    const remaining =
      unresolvedOfficeRecoveryRef.current.filter(
        (entry) => entry.operationId !== exact.operationId
      );
    setOfficeRecoveryEntries(remaining);
    for (
      const [callId, operations]
      of lockerOfficeOperationsByCallRef.current.entries()
    ) {
      const next = operations.filter(
        (operation) =>
          operation.operationId !== exact.operationId
      );
      if (next.length > 0) {
        lockerOfficeOperationsByCallRef.current.set(callId, next);
      } else {
        lockerOfficeOperationsByCallRef.current.delete(callId);
      }
    }
    return true;
  }, [
    getLockerOfficeJournal,
    setOfficeRecoveryEntries,
    transitionDurableOfficeOperation,
  ]);
  /**
   * Clear the banner without asking the server to resolve anything.
   *
   * For an entry the server never confirmed as pending this also deletes the
   * local record, because that record is all that keeps the banner alive. For
   * an operation the server DOES report as pending the record survives and
   * only the banner is hidden for this session: that write is real, the fence
   * still blocks new turns on it, and the resolve path is its only true clear.
   */
  const dismissOfficeOperationRecovery = useCallback(({
    operationId = null,
  } = {}) => {
    if (!operationId) {
      dismissedOfficeRecoveryIdsRef.current.add(
        OFFICE_RECOVERY_UNAVAILABLE_KEY
      );
      persistDismissedOfficeRecoveryIds(
        dismissedOfficeRecoveryIdsRef.current
      );
      unresolvedOfficeRecoveryRef.current = [];
      setOfficeOperationRecovery(null);
      setOfficeOperationRecoveryRequired(false);
      return { dropped: false, hidden: true };
    }
    dismissedOfficeRecoveryIdsRef.current.add(operationId);
    persistDismissedOfficeRecoveryIds(dismissedOfficeRecoveryIdsRef.current);
    const exact = unresolvedOfficeRecoveryRef.current.find(
      (entry) => entry.operationId === operationId
    );
    let dropped = false;
    if (exact && exact.serverConfirmedPending !== true) {
      try {
        dropped = getLockerOfficeJournal().dismissUnbacked({
          ...exact,
          confirmDismissed: true,
        });
      } catch (error) {
        console.warn('[hooks] Secure Office operation dismissal kept the local record:', {
          operationId,
          code:
            error?.code
            || error?.name
            || 'locker_office_dismiss_failed',
        });
      }
    }
    setOfficeRecoveryEntries(
      unresolvedOfficeRecoveryRef.current.filter(
        (entry) => entry.operationId !== operationId
      )
    );
    return { dropped, hidden: true };
  }, [getLockerOfficeJournal, setOfficeRecoveryEntries]);
  useEffect(() => {
    void flushSettledLockerOfficeOperations();
    if (!currentUser?.uid) return undefined;
    const retryTimer = window.setInterval(() => {
      void flushSettledLockerOfficeOperations();
    }, 15_000);
    return () => window.clearInterval(retryTimer);
  }, [currentUser?.uid, flushSettledLockerOfficeOperations]);
  const finalizedMessageIdsRef = useRef(new Set());
  // When handleSend creates a new chat, the addMessage closure still has stale
  // currentChatId=null. This ref lets addSanitizedMessage bypass the stale
  // closure and write directly to the correct chat via the repository.
  const chatIdOverrideRef = useRef(null);
  // THE TURN'S HOME CHAT, captured once when the turn starts and immutable
  // for its lifetime. Every streaming write, heartbeat, and finalizer routes
  // here, so switching the VIEWED chat mid-turn can never siphon the
  // response into whatever chat the user happens to be looking at.
  // Navigation is a viewport change only.
  const activeTurnChatIdRef = useRef(null);
  const initialOfficeStateCacheRef = useRef(new Map());
  const [queuedMessages, setQueuedMessages] = useState([]);
  const scrollToLatestMessage = useCallback((behavior = 'auto', force = false) => {
    if (!messagesContainerRef.current) {
      return;
    }
    if (!force && !autoScrollRef.current) {
      return;
    }
    try {
      const container = messagesContainerRef.current;
      const viewportHeight = container.clientHeight || 0;
      const maxScroll = Math.max(0, container.scrollHeight - viewportHeight);
      container.scrollTo({
        top: maxScroll,
        behavior
      });
      if (force) {
        autoScrollRef.current = true;
      }
    } catch (error) {
      console.warn('⚠️ Failed to scroll chat into view:', error?.message);
    }
  }, []);

  const [draftEnvelopeLog, setDraftEnvelopeLog] = useState([]);
  const [pendingMessage, setPendingMessage] = useState(null);
  // ask_user_question tool: { callId, questions, submit } while the agent is
  // waiting on the user's tap. Rendered as an options card above the composer.
  const [pendingUserQuestion, setPendingUserQuestion] = useState(null);
  // Mirror of the state above for handleSend, which runs outside the render
  // that owns it. Cleared synchronously on submit so the delivery-failure
  // fallback cannot re-enter the answer path and loop.
  const pendingUserQuestionRef = useRef(null);
  useEffect(() => {
    pendingUserQuestionRef.current = pendingUserQuestion;
  }, [pendingUserQuestion]);

  // handleSend is defined much later in this hook; the answer-fallback below
  // reaches it through a ref (assigned by an effect after its definition).
  const handleSendRef = useRef(null);

  // Submit the user's answers for the pending ask_user_question card and
  // clear it. Functional setter avoids stale-closure on the pending object.
  // If the server refuses the result (the turn was stopped, the 10 minute
  // answer window expired, the pane reconnected, or the durable Office fence
  // rejects it), deliver the answers as a fresh chat message so the user's
  // input is never silently dropped. That fallback cannot settle the open tool
  // call, so it is a last resort, not the normal path.
  const answerUserQuestion = useCallback((answers) => {
    pendingUserQuestionRef.current = null;
    setPendingUserQuestion((pending) => {
      if (pending?.submit) {
        Promise.resolve(pending.submit(answers))
          .then((delivered) => {
            if (delivered !== false) return;
            const text = (Array.isArray(answers) ? answers : [])
              .map((a) => `${a?.question || ''}: ${Array.isArray(a?.answer) ? a.answer.join(', ') : (a?.answer || '')}`)
              .filter((line) => line.trim() !== ':')
              .join('\n');
            if (text.trim() && typeof handleSendRef.current === 'function') {
              console.warn('[hooks] Tool result for the question was refused; resending answers as a message');
              handleSendRef.current(`Answering your earlier question:\n${text}`);
            }
          })
          .catch((err) => {
            console.error('[hooks] Failed to submit ask_user_question answers:', err);
          });
      }
      return null;
    });
  }, []);

  // propose_doc_edits tool (Word): { callId, edits, invalid, apply, submit } while
  // the agent is paused waiting on the user to review the staged edit cards.
  const [pendingProposeEdits, setPendingProposeEdits] = useState(null);
  const clearPendingProposeEdits = useCallback((reason = 'cancelled') => {
    setPendingProposeEdits((pending) => {
      pending?.cancel?.(reason);
      return null;
    });
  }, []);

  // The card resolves the review (applied / dismissed / failed). Post the outcome
  // to the tool call and clear the card. Functional setter avoids a stale closure.
  const submitProposeEdits = useCallback((outcome) => {
    setPendingProposeEdits((pending) => {
      if (pending?.submit) {
        Promise.resolve(pending.submit(outcome)).catch((err) => {
          console.error('[hooks] Failed to submit propose_doc_edits review:', err);
        });
      }
      return null;
    });
  }, []);

  // Apply a single staged edit to the document (edit_doc_text under the hood).
  const applyProposeEdit = useCallback((spec) => {
    return new Promise((resolve, reject) => {
      setPendingProposeEdits((pending) => {
        if (pending?.apply) {
          Promise.resolve(pending.apply(spec)).then(resolve, reject);
        } else {
          reject(new Error('No pending propose_doc_edits to apply against'));
        }
        return pending; // keep the card mounted while the user works through it
      });
    });
  }, []);

  const [liveStreamingMessage, setLiveStreamingMessage] = useState(null); // Local UI-only state for timer updates
  const [liveDataMode, setLiveDataMode] = useState(false);
  const [liveDataSession, setLiveDataSession] = useState(null);
  const liveDataSessionRef = useRef(null);
  const privateSessionActiveRef = useRef(false);
  const ephemeralScopeRef = useRef({
    documentId: null,
    chatId: null,
    sessionId: null,
  });
  const [ephemeralMessages, setEphemeralMessages] = useState([]);
  const [documentPrivacy, setDocumentPrivacy] = useState(null);
  const [documentPrivacyResolution, setDocumentPrivacyResolution] =
    useState('resolving');
  const [financeOfficeReady, setFinanceOfficeReady] = useState(false);
  const [financeConnectorReady, setFinanceConnectorReady] = useState(false);
  const financeOfficeControllerRef = useRef(null);
  const documentPrivacyRef = useRef(null);
  useEffect(() => {
    if (!FINANCE_CONNECTORS_UI_ENABLED || !powerpointBridge) {
      financeOfficeControllerRef.current = null;
      setFinanceOfficeReady(false);
      setDocumentPrivacy(null);
      setDocumentPrivacyResolution(
        FINANCE_CONNECTORS_UI_ENABLED ? 'error' : 'disabled'
      );
      documentPrivacyRef.current = null;
      return undefined;
    }
    let cancelled = false;
    if (!documentPrivacyRef.current) {
      setDocumentPrivacyResolution('resolving');
    }
    try {
      const controller = createFinanceOfficeControllerIfEnabled({
        app,
        officeBridge: powerpointBridge,
      });
      financeOfficeControllerRef.current = controller;
      readValidatedDocumentPrivacy(controller)
        .then(async (normalized) => {
          if (cancelled) return;
          setDocumentPrivacy(normalized);
          setDocumentPrivacyResolution('ready');
          documentPrivacyRef.current = normalized;
          try {
            await controller.getConnectorDocumentPrivacyState();
            if (!cancelled) {
              setFinanceOfficeReady(
                controller.supportsLiveDataTarget?.() === true
              );
            }
          } catch {
            if (!cancelled) setFinanceOfficeReady(false);
          }
        })
        .catch((error) => {
          if (cancelled) return;
          setFinanceOfficeReady(false);
          setDocumentPrivacy(null);
          setDocumentPrivacyResolution('error');
          documentPrivacyRef.current = null;
          console.warn('[hooks] Live data document marker is unavailable', {
            code: error?.code || 'finance_office_unavailable',
          });
        });
    } catch (error) {
      financeOfficeControllerRef.current = null;
      setFinanceOfficeReady(false);
      setDocumentPrivacy(null);
      setDocumentPrivacyResolution('error');
      documentPrivacyRef.current = null;
      console.warn('[hooks] Live data Office boundary is unavailable', {
        code: error?.code || 'finance_office_unavailable',
      });
    }
    return () => {
      cancelled = true;
      financeOfficeControllerRef.current = null;
      setFinanceOfficeReady(false);
      setDocumentPrivacyResolution('resolving');
      documentPrivacyRef.current = null;
    };
  }, [app, powerpointBridge]);

  const readFreshDocumentPrivacy = useCallback(async () => {
    if (!FINANCE_CONNECTORS_UI_ENABLED) return null;
    const controller = financeOfficeControllerRef.current;
    if (!powerpointBridge || !controller) {
      setDocumentPrivacy(null);
      setDocumentPrivacyResolution('error');
      documentPrivacyRef.current = null;
      throw new FinanceStreamPrivacyError(
        'document_privacy_required',
        'Verify the open Office document before connecting'
      );
    }
    if (!documentPrivacyRef.current) {
      setDocumentPrivacyResolution('resolving');
    }
    try {
      const marker = await readValidatedDocumentPrivacy(controller);
      setDocumentPrivacy(marker);
      setDocumentPrivacyResolution('ready');
      documentPrivacyRef.current = marker;
      return marker;
    } catch (error) {
      setDocumentPrivacy(null);
      setDocumentPrivacyResolution('error');
      documentPrivacyRef.current = null;
      if (error instanceof FinanceStreamPrivacyError) throw error;
      throw new FinanceStreamPrivacyError(
        error?.code || 'document_privacy_required',
        'Verify the open Office document before connecting'
      );
    }
  }, [powerpointBridge]);

  useEffect(() => {
    if (!FINANCE_CONNECTORS_UI_ENABLED || !currentUser) {
      setFinanceConnectorReady(false);
      return undefined;
    }
    let cancelled = false;
    let requestController = null;
    const refreshReadiness = async () => {
      if (!cancelled) setFinanceConnectorReady(false);
      requestController?.abort();
      requestController = new AbortController();
      try {
        const response = await getMyConnectorReadiness(currentUser, 'daloopa', {
          signal: requestController.signal,
          timeoutMs: 10000,
        });
        if (!cancelled) {
          setFinanceConnectorReady(
            isLiveDataConnectorReady(response, currentUser.uid)
          );
        }
      } catch (error) {
        if (!cancelled && error?.name !== 'AbortError') {
          setFinanceConnectorReady(false);
        }
      }
    };
    const onFocus = () => { void refreshReadiness(); };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') void refreshReadiness();
    };
    setFinanceConnectorReady(false);
    void refreshReadiness();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      cancelled = true;
      requestController?.abort();
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [currentUser]);

  useEffect(() => {
    if (!financeConnectorReady) setLiveDataMode(false);
  }, [financeConnectorReady]);

  const upsertEphemeralMessage = useCallback((message) => {
    if (!message?.id) return;
    setEphemeralMessages((current) => {
      const index = current.findIndex((candidate) => candidate.id === message.id);
      if (index < 0) return [...current, message];
      const next = [...current];
      next[index] = message;
      return next;
    });
  }, []);
  const updateLiveDataSession = useCallback((value) => {
    const next = typeof value === 'function'
      ? value(liveDataSessionRef.current)
      : value;
    liveDataSessionRef.current = next;
    setLiveDataSession(next);
  }, []);
  const terminatePrivateLiveDataSession = useCallback(({
    operationId = null,
    stage = 'failed',
    code = 'private_stream_closed',
    message = null,
    abortLocal = false,
    settleAsCancelled = false,
  } = {}) => {
    const current = liveDataSessionRef.current;
    const expectedOperationId = String(operationId || '').toLowerCase();
    const currentOperationId = String(current?.operationId || '').toLowerCase();
    if (
      expectedOperationId &&
      currentOperationId &&
      expectedOperationId !== currentOperationId
    ) {
      return false;
    }
    if (
      privateSessionActiveRef.current !== true &&
      current?.active !== true
    ) {
      return false;
    }

    const localController = abortControllerRef.current;
    if (abortLocal && localController) {
      try {
        localController.abort();
      } catch {
        // The request may already have settled.
      }
    }
    privateSessionActiveRef.current = false;
    isChatStreamingRef.current = false;
    setIsChatStreaming(false);
    activeRequestIdRef.current = null;
    abortControllerRef.current = null;
    setPendingMessage(null);
    setLiveStreamingMessage(null);
    lastPersistedMessageRef.current = null;
    streamingStateRef.current = null;
    activeTurnChatIdRef.current = null;
    updateLiveDataSession((value) => terminalLiveDataSessionState(value, {
      operationId: expectedOperationId || null,
      stage,
      code,
      message: message || LIVE_DATA_STAGE_LABELS[stage] ||
        LIVE_DATA_STAGE_LABELS.failed,
    }));

    if (settleAsCancelled) {
      setEphemeralMessages((messages) => {
        const index = messages.findLastIndex(
          (candidate) => candidate?.sender === 'ai' && candidate?.isStreaming
        );
        if (index < 0) return messages;
        const next = [...messages];
        next[index] = {
          ...next[index],
          isStreaming: false,
          isCancelled: true,
          metadata: {
            ...(next[index].metadata || {}),
            isStreaming: false,
            isComplete: true,
            isCancelled: true,
          },
        };
        return next;
      });
    }
    return true;
  }, [
    setIsChatStreaming,
    setPendingMessage,
    updateLiveDataSession,
  ]);
  const activeRequestIdRef = useRef(null);
  const [sessionId, setSessionId] = useState(null); // Claude Agent SDK session ID for conversation continuity
  // Which chat the current sessionId was created under. Backend sessions are
  // chat-scoped (a session created under chat A 404s when messaged under
  // chat B), so reusing the id across a chat switch forces the 404 ->
  // stale-recovery path on every send. Tag ownership at create time and
  // treat a mismatched id as absent instead of reusing it.
  const sessionChatIdRef = useRef(null);
  const sessionDocumentBindingRef = useRef({
    sessionId: null,
    documentId: null,
  });
  // Cross-app connector (Conductor) presence. `conductorOnline` means THIS pane
  // is registered in the user's room (stream attached, snapshot received).
  // `conductorPeers` are the user's OTHER live panes ({ agentId, surface, label }),
  // at most one per surface (the backend enforces newest-wins per surface).
  const [conductorOnline, setConductorOnline] = useState(false);
  const [conductorPeers, setConductorPeers] = useState([]);
  // Cross-app waiting window: set when this pane fires send_message at a peer
  // (conductor_message_sent) and cleared when the peer's reply arrives
  // (conductor_message_received) or the peer drops. While set, the composer
  // shows an ongoing-work state (red button + "Waiting for Excel...") even
  // though the LOCAL turn has legitimately ended (send_message is
  // fire-and-forget, same as the reference implementation).
  const [awaitingPeerLabel, setAwaitingPeerLabel] = useState(null);
  const awaitingPeerTimerRef = useRef(null);
  // Late-assigned: finalizes any open inline peer-status lines ("Excel
  // working...") in the transcript when the wait ends without a reply. The
  // helper is defined further down (it needs chat refs declared later).
  const finalizePeerStatusesRef = useRef(null);
  const setAwaitingPeer = useCallback((label) => {
    if (awaitingPeerTimerRef.current) {
      clearTimeout(awaitingPeerTimerRef.current);
      awaitingPeerTimerRef.current = null;
    }
    setAwaitingPeerLabel(label);
    if (label) {
      // Safety valve: a peer that never replies must not pin the UI forever.
      awaitingPeerTimerRef.current = setTimeout(() => {
        setAwaitingPeerLabel(null);
        finalizePeerStatusesRef.current?.('ended');
      }, 180000);
    }
  }, []);

  // User-initiated dismissal of the waiting UI (the composer's stop control
  // while a peer app works). Local only: the peer keeps working and its
  // reply still lands as an injected turn.
  const cancelPeerWait = useCallback(() => {
    setAwaitingPeer(null);
    finalizePeerStatusesRef.current?.('ended');
  }, [setAwaitingPeer]);
  const conductorConnectRef = useRef(null); // in-flight eager-connect promise (dedup + send-path reuse)
  const conductorReconnectAttemptsRef = useRef(0);
  const [conductorReconnectEpoch, setConductorReconnectEpoch] = useState(0);
  // Chat id minted by the connector's eager connect (see connectConductor).
  // Tracked separately from chatIdOverrideRef so the catch-up effect below
  // only ever clears the CONNECTOR's override, never handleSend's (which owns
  // its own set/clear lifecycle around a live turn).
  const conductorMintedChatRef = useRef(null);
  const [requestStartTime, setRequestStartTime] = useState(null);

  const {
    currentMessages,
    addMessage,
    addMessageToChat,
    applyServerTranscriptMode,
    currentChatId,
    chats,
    scopedTranscriptState,
    switchToChat,
  } = chatData;

  // Keep a ref to always have access to the latest messages (avoids stale closure issues)
  const currentMessagesRef = useRef(currentMessages);
  useEffect(() => {
    currentMessagesRef.current = currentMessages;
  }, [currentMessages]);

  // Same for the active chat id: the SSE reader is a long-lived closure that
  // outlives many renders, so conductor handlers must read the CURRENT chat
  // through this ref, never through captured props.
  //
  // USER-INTENT TRACKING for auto-follow: a chat switch this code did NOT
  // initiate (history sidebar tap) is a deliberate user action, and so is a
  // local send. Auto-follow defers to recent user intent instead of yanking
  // the view; it does NOT gate on whether the viewed chat has user messages
  // (an idle pane parked on any old chat must still follow new activity).
  const expectedSwitchRef = useRef(null); // chat id of a programmatic switch in flight
  const manualChatActionAtRef = useRef(0); // ms timestamp of the user's last deliberate chat action
  const markProgrammaticSwitch = useCallback((chatId) => {
    expectedSwitchRef.current = chatId;
  }, []);
  const currentChatIdRef = useRef(currentChatId);
  useEffect(() => {
    const previousChatId = currentChatIdRef.current;
    currentChatIdRef.current = currentChatId;
    if (currentChatId && currentChatId !== previousChatId) {
      if (expectedSwitchRef.current === currentChatId) {
        expectedSwitchRef.current = null;
      } else {
        manualChatActionAtRef.current = Date.now();
      }
    }
  }, [currentChatId]);

  const beginEphemeralScope = useCallback(({
    documentId,
    chatId,
    sessionId: scopeSessionId,
  }) => {
    const current = ephemeralScopeRef.current;
    if (
      current.documentId &&
      (
        current.documentId !== documentId ||
        current.chatId !== chatId
      )
    ) {
      setEphemeralMessages([]);
    }
    ephemeralScopeRef.current = {
      documentId,
      chatId,
      sessionId: scopeSessionId || null,
    };
  }, []);

  useEffect(() => {
    const scope = ephemeralScopeRef.current;
    if (!scope.documentId) return;
    const scopedChatId = currentChatId || `private-${scope.documentId}`;
    if (
      (
        documentPrivacyResolution === 'error' ||
        documentPrivacy?.documentId
      ) &&
      (
        documentPrivacy?.documentId !== scope.documentId ||
        scopedChatId !== scope.chatId
      )
    ) {
      terminatePrivateLiveDataSession({
        operationId: liveDataSessionRef.current?.operationId || null,
        stage: 'cancelled',
        code: 'document_scope_changed',
        message: 'The live data request stopped because the Office file changed',
        abortLocal: true,
      });
      setEphemeralMessages([]);
      ephemeralScopeRef.current = {
        documentId: null,
        chatId: null,
        sessionId: null,
      };
      setLiveDataMode(false);
      updateLiveDataSession(null);
      return;
    }
    const activeSessionId = sessionStreamRef.current.sessionId || sessionId;
    if (
      activeSessionId &&
      scope.sessionId &&
      activeSessionId !== scope.sessionId
    ) {
      if (privateSessionActiveRef.current) {
        ephemeralScopeRef.current = {
          ...scope,
          sessionId: activeSessionId,
        };
      } else {
        setEphemeralMessages([]);
        ephemeralScopeRef.current = {
          documentId: null,
          chatId: null,
          sessionId: null,
        };
      }
    } else if (activeSessionId && !scope.sessionId) {
      ephemeralScopeRef.current = {
        ...scope,
        sessionId: activeSessionId,
      };
    }
  }, [
    currentChatId,
    documentPrivacy?.documentId,
    documentPrivacyResolution,
    sessionId,
    terminatePrivateLiveDataSession,
    updateLiveDataSession,
  ]);

  // INLINE PEER STATUS LINES: "Excel connected" -> "Excel working..." ->
  // "Excel finished", rendered as part of the shared transcript (not just
  // composer chrome). The pane that dispatched the cross-app request owns the
  // line: it writes a small system message into the conversation chat locally
  // AND directly to Firestore (bypassing the throttled sync loop, whose
  // watermark would skip a state flip on an unchanged timestamp). Every other
  // pane receives the identical line through the live chat mirror.
  const conductorStatusRef = useRef(new Map()); // agentKey -> { id, chatId, label, timestamp, state, timer }
  const writeConductorStatusRef = useRef(null);
  const writeConductorStatus = useCallback((agentKey, label, state) => {
    const presentationIdForWrite = currentPresentation?.id;
    if (!presentationIdForWrite || !agentKey) return;

    let entry = conductorStatusRef.current.get(agentKey);
    if (!entry) {
      // Only a fresh dispatch ('connected') opens a new line; a late finish
      // or drop for an unknown handoff has nothing to update.
      if (state !== 'connected') return;
      entry = {
        id: `peer-status-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        chatId: chatIdOverrideRef.current || currentChatIdRef.current,
        label: label || agentKey,
        timestamp: new Date().toISOString(),
        state: null,
        timer: null
      };
      conductorStatusRef.current.set(agentKey, entry);
    }
    if (!entry.chatId) {
      conductorStatusRef.current.delete(agentKey);
      return;
    }
    if (entry.timer) {
      clearTimeout(entry.timer);
      entry.timer = null;
    }
    entry.state = state;
    if (label) entry.label = label;

    const statusMessage = sanitizeMessage({
      id: entry.id,
      sender: 'system',
      app,
      timestamp: entry.timestamp,
      metadata: { peerStatus: { label: entry.label, state } },
      components: []
    });
    try {
      addMessageToChat(entry.chatId, statusMessage);
    } catch (statusErr) {
      console.warn('[hooks] Failed to write peer status line locally:', statusErr?.message);
    }
    if (currentUser?.uid) {
      const targetChat = chats.find((chat) => chat.id === entry.chatId);
      upsertChatMessage(
        currentUser.uid,
        entry.chatId,
        { ...statusMessage, rev: Date.now() },
        targetChat
      );
    }

    if (state === 'connected') {
      // Stage to "working" moments later: delivery is fire-and-forget, so
      // 'connected' is the honest delivery ack and 'working' the steady state.
      entry.timer = setTimeout(() => {
        entry.timer = null;
        const current = conductorStatusRef.current.get(agentKey);
        if (current && current.state === 'connected') {
          writeConductorStatusRef.current?.(agentKey, current.label, 'working');
        }
      }, 900);
    }
    if (state === 'finished' || state === 'disconnected' || state === 'ended') {
      conductorStatusRef.current.delete(agentKey);
    }
  }, [addMessageToChat, app, chats, currentPresentation?.id, currentUser?.uid]);
  useEffect(() => {
    writeConductorStatusRef.current = writeConductorStatus;
  }, [writeConductorStatus]);

  // Close every open line (peer never replied, user dismissed the wait, or
  // this pane was superseded). Assigned to the early ref so setAwaitingPeer's
  // timeout (declared above) can reach it.
  const finalizeAllConductorStatuses = useCallback((state = 'ended') => {
    for (const agentKey of [...conductorStatusRef.current.keys()]) {
      writeConductorStatusRef.current?.(agentKey, null, state);
    }
  }, []);
  useEffect(() => {
    finalizePeerStatusesRef.current = finalizeAllConductorStatuses;
  }, [finalizeAllConductorStatuses]);

  // Loop telemetry is optional - initialize with empty handlers if not provided
  const handleLoopTelemetryUpdate = () => {};
  const resetLoopTelemetry = () => {};
const trackAIChat = analytics?.trackAIChat;

const {
  input,
  setInput,
  appendInput,
  selectedFiles,
  setAttachmentsFromFiles,
  clearAttachments,
  resetTextareaHeight
} = composer;

  // Fresh composer draft for the long-lived SSE reader: auto-follow must not
  // switch the view away from a message the user is mid-typing.
  const composerDraftRef = useRef('');
  useEffect(() => {
    composerDraftRef.current = typeof input === 'string' ? input : '';
  }, [input]);

  const addSanitizedMessage = useCallback(async (message) => {
    // Stamp every persisted message with the Office app that produced it so
    // a shared cross-app chat can render "PowerPoint" / "Excel" dividers
    // when the author app changes.
    const sanitized = sanitizeMessage({ app, ...message });
    // Routing order: the new-chat override (mint in flight), then the active
    // turn's HOME chat (immutable per turn, so mid-turn view switches cannot
    // misroute the response), then the currently viewed chat for idle-time
    // writes (stale-timer finalizers etc.).
    const targetChatId = chatIdOverrideRef.current || activeTurnChatIdRef.current;
    if (targetChatId && currentPresentation?.id) {
      return addMessageToChat(targetChatId, sanitized);
    }
    return addMessage(sanitized);
  }, [addMessage, addMessageToChat, currentPresentation?.id, app]);

  const enqueueMessageUpdate = useCallback((task, waitForCompletion = false) => {
    if (typeof task !== 'function') {
      return Promise.resolve();
    }

    messageWriteQueueRef.current = messageWriteQueueRef.current
      .then(() => Promise.resolve(task()))
      .then((result) => {
        if (typeof window !== 'undefined') {
          window.setTimeout(() => scrollToLatestMessage('auto'), 0);
        } else {
          scrollToLatestMessage('auto');
        }
        return result;
      })
      .catch((error) => {
        console.error('❌ Failed to process chat message update:', error);
      });

    return waitForCompletion ? messageWriteQueueRef.current : messageWriteQueueRef.current.catch(() => { });
  }, [scrollToLatestMessage]);

  const handleCreditsUpdate = useCallback((payload) => {
    if (
      !payload ||
      privateSessionActiveRef.current ||
      ephemeralScopeRef.current.documentId ||
      documentPrivacy?.state === 'licensed_data'
    ) {
      return;
    }

    const creditsData = {
      creditsRemaining: payload.creditsRemaining ?? payload.remainingCredits ?? payload.creditBalance ?? null,
      creditsConsumed: payload.creditsConsumed ?? null,
      deltaCostUSD: payload.deltaCostUSD ?? payload.costUSD ?? null,
      updatedAt: payload.updatedAt || payload.timestamp || new Date().toISOString()
    };

    console.log('[Credits][SSE] Received credits update:', creditsData);

    // Helper to find and update the last AI message
    const tryUpdateMessage = () => {
      // Use ref to get the LATEST messages (avoids stale closure when credits arrive after finalization)
      const messages = currentMessagesRef.current || [];
      const lastAiMessage = messages.filter(m => m.sender === 'ai').pop();

      console.log('[Credits][SSE] Last AI message:', lastAiMessage?.id, 'Messages count:', messages.length);

      if (lastAiMessage && typeof creditsData.creditsConsumed === 'number') {
        console.log('[Credits][SSE] Updating message with credits:', lastAiMessage.id);
        const updatedMessage = {
          ...lastAiMessage,
          metadata: {
            ...lastAiMessage.metadata,
            latestCredits: creditsData
          }
        };
        // Use enqueueMessageUpdate to safely update the message
        enqueueMessageUpdate(() => addSanitizedMessage(updatedMessage));
        return true;
      }
      return false;
    };

    // Try immediately first
    if (tryUpdateMessage()) {
      return;
    }

    // If message not found, the finalized message might not be in React state yet
    // (credits_update can arrive before React's useEffect updates currentMessagesRef)
    // Retry after a short delay to allow React to flush updates
    console.log('[Credits][SSE] Message not found, retrying after delay...');
    setTimeout(() => {
      if (!tryUpdateMessage()) {
        console.log('[Credits][SSE] Cannot update - no AI message found after retry');
      }
    }, 100);
  }, [
    enqueueMessageUpdate,
    addSanitizedMessage,
    documentPrivacy?.state,
  ]);

  const flushMessageQueue = useCallback(() => {
    return messageWriteQueueRef.current.catch(() => { });
  }, []);

  const primeStreamingMessage = useCallback(async (
    aiMessageId,
    startTimeOverride = null,
    { ephemeral = false, privacyPending = false } = {},
  ) => {
    const initialState = streamProcessor.initializeState(aiMessageId);
    const startTimestamp = Number.isFinite(startTimeOverride)
      ? startTimeOverride
      : (initialState.metadata?.startTime ?? Date.now());

    // Always store startTime as ISO string for consistency
    const startTimeISO = typeof startTimestamp === 'number'
      ? new Date(startTimestamp).toISOString()
      : startTimestamp;

    const stateWithOverrides = {
      ...initialState,
      metadata: {
        ...initialState.metadata,
        statusLabel: INITIAL_STREAM_STATUS,
        statusHint: null,
        isStreaming: true,
        startTime: startTimeISO
      }
    };

    let mutableState = stateWithOverrides;
    const updateState = (nextState) => {
      mutableState = nextState;
      streamingStateRef.current = {
        ...streamingStateRef.current,
        state: mutableState,
        updateState
      };
    };

    streamingStateRef.current = {
      state: mutableState,
      updateState
    };

    const provisionalMessage = streamProcessor.createMessage(mutableState);
    provisionalMessage.isStreaming = true;
    finalizedMessageIdsRef.current.delete(provisionalMessage.id);
    // This pane is the live author of this message: the sync loop may
    // live-publish it each tick, and mirror merges must never overwrite it.
    const memoryOnly = turnUsesMemoryOnlySink({
      ephemeral,
      privacyPending,
    });
    if (!memoryOnly) smartCache.claimLiveMessage(provisionalMessage.id);

    // Set live streaming message immediately so timer shows up right away
    setLiveStreamingMessage(provisionalMessage);
    await routeTurnSink(memoryOnly, {
      privateSink: () => upsertEphemeralMessage(provisionalMessage),
      persistentSink: async () => {
        lastPersistedMessageRef.current = provisionalMessage;
        await enqueueMessageUpdate(
          () => addSanitizedMessage(provisionalMessage),
          true
        );
      },
    });

    return mutableState;
  }, [addSanitizedMessage, enqueueMessageUpdate, upsertEphemeralMessage]);

  const createPendingTurn = useCallback((turnMeta) => {
    let entryRef = null;

    const turnPromise = new Promise((resolve, reject) => {
      entryRef = {
        ...turnMeta,
        resolve,
        reject,
        assigned: false
      };

      if (pendingTurnWaitersRef.current.length > 0) {
        const notify = pendingTurnWaitersRef.current.shift();
        notify(entryRef);
      } else {
        pendingTurnQueueRef.current.push(entryRef);
      }
    });

    // Attach a silent .catch() so the browser doesn't fire `unhandledrejection`
    // if the promise is rejected (e.g. by handleStopRequest) during the window
    // between pending-turn creation and the caller's first `await`. The
    // caller still awaits the original `turnPromise` and sees the rejection
    // through its own try/catch — this silent handler is only to mark the
    // promise as "observed" for the error-overlay's purposes.
    turnPromise.catch(() => { /* observed; real handling happens in handleSend */ });

    const cancel = (error) => {
      if (!entryRef) {
        return;
      }

      if (entryRef.assigned) {
        return;
      }

      const queue = pendingTurnQueueRef.current;
      const index = queue.indexOf(entryRef);
      if (index !== -1) {
        queue.splice(index, 1);
        entryRef.reject(error || new Error('Turn cancelled'));
      }
    };

    return {
      entry: entryRef,
      promise: turnPromise,
      cancel
    };
  }, []);

  const awaitNextTurnEntry = useCallback(() => {
    if (pendingTurnQueueRef.current.length > 0) {
      const entry = pendingTurnQueueRef.current.shift();
      if (entry) {
        entry.assigned = true;
      }
      return Promise.resolve(entry);
    }

    return new Promise((resolve) => {
      pendingTurnWaitersRef.current.push((entry) => {
        if (entry) {
          entry.assigned = true;
        }
        resolve(entry);
      });
    });
  }, []);

  const rejectAllPendingTurns = useCallback((error) => {
    if (activeTurnRef.current) {
      try {
        activeTurnRef.current.reject(error);
      } catch (_) {
        // ignore
      }
      activeTurnRef.current = null;
    }

    while (pendingTurnQueueRef.current.length > 0) {
      const entry = pendingTurnQueueRef.current.shift();
      if (entry) {
        try {
          entry.reject(error);
        } catch (_) {
          // ignore
        }
      }
    }

    while (pendingTurnWaitersRef.current.length > 0) {
      const notify = pendingTurnWaitersRef.current.shift();
      try {
        notify(null);
      } catch (_) {
        // ignore
      }
    }
  }, []);

  // Helper to check if message content actually changed (excluding timer)
  const hasContentChanged = useCallback((prevMsg, newMsg) => {
    if (!prevMsg || !newMsg) return true;
    if (prevMsg.components.length !== newMsg.components.length) return true;

    // Compare all components except timer
    const prevNonTimer = prevMsg.components.filter(c => c.type !== 'timer');
    const newNonTimer = newMsg.components.filter(c => c.type !== 'timer');

    if (prevNonTimer.length !== newNonTimer.length) return true;

    // Deep compare non-timer components
    return JSON.stringify(prevNonTimer) !== JSON.stringify(newNonTimer);
  }, []);

  // Track last persisted message to avoid unnecessary saves
  const lastPersistedMessageRef = useRef(null);

  // Heartbeat for streaming UI updates (timer, progress)
  useEffect(() => {
    if (!isChatStreaming) {
      // Do NOT clear the live streaming reply here. The finalized assistant
      // message is committed asynchronously (queued write), so clearing the
      // live copy the instant streaming stops blanks the reply until that
      // commit lands (the 2nd-turn blank-out bug). The handoff effect below
      // drops the live copy only once it's present in currentMessages.
      lastPersistedMessageRef.current = null;
      return undefined;
    }
    if (
      privateSessionActiveRef.current ||
      documentPrivacy?.state === 'licensed_data'
    ) {
      return undefined;
    }

    const interval = setInterval(() => {
      const streamingState = streamingStateRef.current;
      const activeState = streamingState?.state;
      if (!activeState?.metadata?.isStreaming) {
        return;
      }

      // Create message with updated timer
      const heartbeatMessage = streamProcessor.createMessage(activeState);
      heartbeatMessage.isStreaming = true;

      // Always update local UI state for immediate timer feedback
      setLiveStreamingMessage(heartbeatMessage);

      // Only persist if actual content changed (not just timer)
      if (hasContentChanged(lastPersistedMessageRef.current, heartbeatMessage)) {
        lastPersistedMessageRef.current = heartbeatMessage;
        enqueueMessageUpdate(() => addSanitizedMessage(heartbeatMessage));
      }
    }, 1000);

    return () => {
      clearInterval(interval);
      // Only stop the timer ticking here; keep the live reply on screen (see
      // the note above) so the handoff effect can clear it once persisted.
      lastPersistedMessageRef.current = null;
    };
  }, [
    isChatStreaming,
    addSanitizedMessage,
    documentPrivacy?.state,
    enqueueMessageUpdate,
    hasContentChanged,
  ]);

  // Hand the live streaming reply off to the persisted list WITHOUT a blank
  // gap: keep the live copy visible until the finalized assistant message lands
  // in currentMessages, then drop it. (Other paths still clear it directly: a
  // new send, an interrupt, and the user-message handoff.)
  useEffect(() => {
    const live = liveStreamingMessage;
    if (!live || isChatStreaming) return undefined;
    if (currentMessages.some((m) => m.id === live.id)) {
      setLiveStreamingMessage(null);
      lastPersistedMessageRef.current = null;
    }
    return undefined;
  }, [isChatStreaming, currentMessages, liveStreamingMessage]);

  // Terminate streaming on page unload only (not on state changes)
  useEffect(() => {
    const handleTerminateStreaming = () => {
      if (abortControllerRef.current) {
        try { abortControllerRef.current.abort('page_unload'); } catch {}
      }
      streamingStateRef.current = null;
    };

    window.addEventListener('beforeunload', handleTerminateStreaming);
    window.addEventListener('pagehide', handleTerminateStreaming);

    return () => {
      window.removeEventListener('beforeunload', handleTerminateStreaming);
      window.removeEventListener('pagehide', handleTerminateStreaming);
    };
  }, []); // Empty deps — mount/unmount only, no re-runs on state changes

  // P1 FIX: On load, ensure all completed AI messages have timers stopped
  // CRITICAL: Cap duration for stale sessions to prevent showing hours of runtime
  // This fixes the "52 hours" bug where abandoned sessions show unrealistic durations
  useEffect(() => {
    if (
      privateSessionActiveRef.current ||
      documentPrivacy?.state === 'licensed_data'
    ) {
      return;
    }
    if (isChatStreaming) {
      return; // Don't interfere with active streaming
    }

    // Maximum reasonable duration for a single turn (10 minutes display cap)
    // Even long AI operations rarely exceed this in wall-clock time
    const MAX_REASONABLE_DURATION_SECONDS = 10 * 60; // 10 minutes

    // If startTime is older than this threshold and no completedAt, it's definitely stale
    // Reduced from 10 min to 5 min for faster stale detection
    const STALE_SESSION_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes

    // STRONGER FILTER: Find ALL AI messages that:
    // 1. Have isStreaming !== false (undefined counts as potentially streaming)
    // 2. OR have no completedAt but have a startTime (incomplete state)
    // 3. AND haven't already been finalized by this effect
    const messagesToFinalize = currentMessages.filter(msg => {
      if (msg?.sender !== 'ai') return false;
      if (finalizedMessageIdsRef.current.has(msg.id)) return false;

      // Never "finalize" a message another app is live-streaming into this
      // shared chat (it arrives via the mirror still marked streaming). Its
      // author pane finalizes it; writing a finalized copy from here would
      // clobber the live turn remotely.
      if (msg?.app && msg.app !== app) return false;

      const hasStreamingFlag = msg?.metadata?.isStreaming !== false || msg?.isStreaming !== false;
      const hasIncompleteState = msg?.metadata?.startTime && !msg?.metadata?.completedAt;
      const hasTimerWithoutCompletion = msg?.components?.some(
        c => c?.type === 'timer' && !c?.data?.completedAt
      );

      return hasStreamingFlag || hasIncompleteState || hasTimerWithoutCompletion;
    });

    if (messagesToFinalize.length === 0) {
      return;
    }

    console.log(`🛑 Stopping ${messagesToFinalize.length} timer(s) on load`);

    // Fix each message: set isStreaming: false and freeze the duration
    messagesToFinalize.forEach(msg => {
      const startTime = msg.metadata?.startTime;
      const existingDuration = msg.metadata?.durationSeconds;
      const hasCompletedAt = !!msg.metadata?.completedAt;

      // Calculate raw duration
      let calculatedDuration = 0;
      let isStaleSession = false;

      if (startTime) {
        const startMs = new Date(startTime).getTime();
        if (!Number.isNaN(startMs)) {
          calculatedDuration = Math.max(0, Math.floor((Date.now() - startMs) / 1000));

          // Detect stale sessions - if startTime is old and no completedAt, session was abandoned
          isStaleSession = !hasCompletedAt && (Date.now() - startMs > STALE_SESSION_THRESHOLD_MS);

          if (isStaleSession) {
            console.log(`⏱️ Stale session detected for message ${msg.id}:`, {
              startTime,
              calculatedDuration,
              maxDuration: MAX_REASONABLE_DURATION_SECONDS,
              willCapTo: Math.min(calculatedDuration, MAX_REASONABLE_DURATION_SECONDS)
            });
          }
        }
      }

      // CRITICAL: Always cap duration, and prefer existing duration if available
      // For stale sessions, use the cap; for normal sessions, use existing or capped calculated
      let durationSeconds;
      if (typeof existingDuration === 'number' && existingDuration > 0) {
        // Use existing but still cap it
        durationSeconds = Math.min(existingDuration, MAX_REASONABLE_DURATION_SECONDS);
      } else if (isStaleSession) {
        // Stale session - cap aggressively
        durationSeconds = Math.min(calculatedDuration, MAX_REASONABLE_DURATION_SECONDS);
      } else {
        // Normal case - use calculated with cap
        durationSeconds = Math.min(calculatedDuration, MAX_REASONABLE_DURATION_SECONDS);
      }

      const completedAt = msg.metadata?.completedAt || new Date().toISOString();

      const finalizedMessage = {
        ...msg,
        isStreaming: false,
        metadata: {
          ...msg.metadata,
          isStreaming: false,
          isComplete: true,
          durationSeconds,
          completedAt
        },
        components: msg.components?.map(comp => {
          if (comp?.type === 'timer') {
            return {
              ...comp,
              data: {
                ...comp.data,
                isStreaming: false,
                isComplete: true,
                isActive: false,
                durationSeconds,
                elapsedSeconds: durationSeconds,
                completedAt: comp.data?.completedAt || completedAt
              }
            };
          }
          // Also mark thinking blocks as complete
          if (comp?.type === 'thinking' && comp?.isStreaming !== false) {
            return {
              ...comp,
              isStreaming: false,
              isComplete: true
            };
          }
          return comp;
        })
      };

      finalizedMessageIdsRef.current.add(msg.id);
      addSanitizedMessage(finalizedMessage).catch(err =>
        console.error('Failed to finalize timer:', err)
      );
    });
  }, [
    currentMessages,
    isChatStreaming,
    addSanitizedMessage,
    app,
    documentPrivacy?.state,
  ]);

  useEffect(() => {
    if (
      privateSessionActiveRef.current ||
      documentPrivacy?.state === 'licensed_data'
    ) {
      return;
    }
    if (isChatStreaming) {
      return;
    }

    const lastAiMessageIndex = currentMessages.findLastIndex((message) => message?.sender === 'ai');
    if (lastAiMessageIndex === -1) {
      return;
    }

    const lastAiMessage = currentMessages[lastAiMessageIndex];
    if (!lastAiMessage?.metadata?.isStreaming) {
      return;
    }

    // A peer app's live turn mirrored into this chat is not ours to finalize
    // (see the matching guard in the bulk finalizer above).
    if (lastAiMessage?.app && lastAiMessage.app !== app) {
      return;
    }

    if (finalizedMessageIdsRef.current.has(lastAiMessage.id)) {
      return;
    }

    const parseTimestamp = (value) => {
      if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
      }
      if (typeof value === 'string') {
        const parsed = Date.parse(value);
        return Number.isFinite(parsed) ? parsed : null;
      }
      return null;
    };

    const startMs =
      parseTimestamp(lastAiMessage.metadata.startTime) ??
      parseTimestamp(lastAiMessage.components?.find((component) => component?.type === 'timer')?.data?.startTime);

    const endMs = Date.now();
    const durationSeconds = startMs
      ? Math.max(0, Math.floor((endMs - startMs) / 1000))
      : (lastAiMessage.metadata?.durationSeconds ?? 0);

    const completedAtISO = new Date(endMs).toISOString();
    const updatedMetadata = {
      ...lastAiMessage.metadata,
      isStreaming: false,
      endTime: endMs,
      completedAt: completedAtISO,
      durationSeconds,
      statusLabel: lastAiMessage.metadata?.statusLabel || 'Request processed',
      statusHint: lastAiMessage.metadata?.statusHint || null
    };

    const updatedComponents = Array.isArray(lastAiMessage.components)
      ? lastAiMessage.components.map((component) => {
          if (!component || component.type !== 'timer') {
            return component;
          }
          return {
            ...component,
            data: {
              ...(component.data || {}),
              startTime: component.data?.startTime || lastAiMessage.metadata?.startTime,
              elapsedSeconds: durationSeconds || component.data?.elapsedSeconds || 0,
              isStreaming: false,
              completedAt: component.data?.completedAt || completedAtISO,
              statusLabel: component.data?.statusLabel || updatedMetadata.statusLabel,
              statusHint: component.data?.statusHint || updatedMetadata.statusHint
            }
          };
        })
      : lastAiMessage.components;

    const finalizedMessage = {
      ...lastAiMessage,
      components: updatedComponents,
      metadata: updatedMetadata,
      isStreaming: false
    };

    finalizedMessageIdsRef.current.add(finalizedMessage.id);
    if (lastPersistedMessageRef.current?.id === finalizedMessage.id) {
      lastPersistedMessageRef.current = finalizedMessage;
    }

    enqueueMessageUpdate(() => addSanitizedMessage(finalizedMessage), true).catch(() => {});
  }, [
    addSanitizedMessage,
    currentMessages,
    documentPrivacy?.state,
    enqueueMessageUpdate,
    isChatStreaming,
    app,
  ]);

  const handleDraftEnvelopeEvent = useCallback((type, payload) => {
    if (!payload) {
      return;
    }

    setDraftEnvelopeLog(prev => {
      const entry = {
        id: payload.envelopeId || `${type}-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        type,
        payload,
        timestamp: payload.timestamp || new Date().toISOString()
      };
      const next = [...prev, entry];
      return next.slice(-50);
    });
  }, []);

  const saveRequestDuration = useCallback(async () => {
    if (
      privateSessionActiveRef.current ||
      documentPrivacy?.state === 'licensed_data'
    ) {
      return;
    }
    if (requestStartTime && currentPresentation?.id) {
      const finalDuration = Math.floor((Date.now() - requestStartTime) / 1000);
      try {
        const lastUserMessageIndex = currentMessages.findLastIndex(m => m.sender === 'user');
        if (lastUserMessageIndex >= 0) {
          const lastUserMessage = currentMessages[lastUserMessageIndex];
          const updatedMessage = {
            ...lastUserMessage,
            requestDuration: finalDuration
          };
          await addSanitizedMessage(updatedMessage);
        }

        const lastAiMessageIndex = currentMessages.findLastIndex(m => m.sender === 'ai');
        if (lastAiMessageIndex >= 0) {
          const lastAiMessage = currentMessages[lastAiMessageIndex];
          if (Array.isArray(lastAiMessage.components)) {
            const startTimeISO = lastAiMessage.metadata?.startTime || new Date(requestStartTime).toISOString();
            const completedAtISO = new Date().toISOString();

            let timerUpdated = false;
            const updatedComponents = lastAiMessage.components.map((component) => {
              if (component?.type === 'timer') {
                const existingSeconds = component.data?.elapsedSeconds ?? 0;
                const existingStreaming = component.data?.isStreaming;
                if (existingSeconds !== finalDuration || existingStreaming !== false) {
                  timerUpdated = true;
                  return {
                    ...component,
                    data: {
                      ...(component.data || {}),
                      startTime: startTimeISO,
                      elapsedSeconds: finalDuration,
                      durationSeconds: finalDuration,
                      isStreaming: false,
                      completedAt: completedAtISO
                    }
                  };
                }
              }
              return component;
            });

            const hasTimer = updatedComponents.some(component => component?.type === 'timer');

            if (hasTimer && timerUpdated) {
              await addSanitizedMessage({
                ...lastAiMessage,
                components: updatedComponents,
                isStreaming: false,
                metadata: {
                  ...(lastAiMessage.metadata || {}),
                  statusLabel: 'Request processed',
                  statusHint: null,
                  startTime: startTimeISO,
                  completedAt: completedAtISO,
                  durationSeconds: finalDuration,
                  isStreaming: false
                }
              });
            } else if (!hasTimer) {
              const timerComponent = {
                type: 'timer',
                data: {
                  startTime: startTimeISO,
                  elapsedSeconds: finalDuration,
                  durationSeconds: finalDuration,
                  isStreaming: false,
                  completedAt: completedAtISO,
                  statusLabel: 'Request processed',
                  statusHint: null
                },
                timestamp: completedAtISO
              };

              await addSanitizedMessage({
                ...lastAiMessage,
                components: [timerComponent, ...updatedComponents],
                isStreaming: false,
                metadata: {
                  ...(lastAiMessage.metadata || {}),
                  statusLabel: 'Request processed',
                  statusHint: null,
                  startTime: startTimeISO,
                  completedAt: completedAtISO,
                  durationSeconds: finalDuration,
                  isStreaming: false
                }
              });
            }
          }
        }
      } catch (error) {
        console.error('Failed to update message with duration:', error);
      }
    }
  }, [
    addSanitizedMessage,
    currentMessages,
    currentPresentation?.id,
    documentPrivacy?.state,
    requestStartTime,
  ]);

  useEffect(() => {
    if (pendingMessage && currentMessages.some(msg => msg.id === pendingMessage.id)) {
      setPendingMessage(null);
      setLiveStreamingMessage(null); // Also clear live streaming state
      lastPersistedMessageRef.current = null; // Reset persistence tracking
    }
  }, [currentMessages, pendingMessage]);

  // Track the previous presentation ID to detect presentation switches
  // Track if we've done initial load to differentiate null->A (initial) vs A->null->A (navigation)
  // HARD REFRESH FIX: Skip loading sessionId from chat metadata after reset
  // This gives us "hard refresh" behavior - fresh session on every navigation
  const skipSessionIdLoadRef = useRef(true); // consumed on the first run, see below

  // Presentation ID is always plugin-{uid} — never changes within a session.
  // No presentation-switch reset needed (removed: was causing false resets
  // during auth token refresh which cycles currentUser null→user).

  // Load sessionId from active chat when chat changes
  // HARD REFRESH FIX: Skip loading if we've just reset (to simulate hard refresh behavior)
  useEffect(() => {
    if (!currentChatId || !chats) {
      return;
    }

    // ONE SHOT. The flag keeps the INITIAL load from adopting a session, so a
    // reload starts fresh. It is not meant to disable session restore for the
    // life of the pane, and nothing ever cleared it, so everything below was
    // unreachable. The effect of that: opening an earlier chat never restored
    // its session, the send path saw a chat mismatch and minted a new Cloud Run
    // session on every message, and the resulting stream teardown looked like a
    // dead second turn.
    if (skipSessionIdLoadRef.current) {
      skipSessionIdLoadRef.current = false;
      console.log('🔒 Skipping sessionId load from chat (initial load only):', {
        chatId: currentChatId,
        reason: 'skip_flag_consumed'
      });
      return;
    }

    const activeChat = chats.find(chat => chat.id === currentChatId);
    const chatSessionId = activeChat?.sessionId || null;

    // Adopt a stored session, never clear one. This effect also runs on every
    // `chats` update, and a chat record that has not yet caught up carries no
    // sessionId. Assigning that null would drop a session mid-turn, which is a
    // worse failure than the stale restore the skip flag guards against. When
    // there is nothing stored, the send path's chat-ownership check still mints
    // a fresh session, which is the behaviour that was already correct.
    if (chatSessionId && chatSessionId !== sessionId) {
      setSessionId(chatSessionId);
      console.log('🔐 Session ID loaded from chat:', {
        chatId: currentChatId,
        sessionId: chatSessionId
      });
    }
  }, [currentChatId, chats, sessionId]);

  useEffect(() => {
    if (!autoScrollRef.current) {
      return;
    }

    const container = messagesContainerRef.current;
    if (!container) {
      return;
    }

    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [currentMessages]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) {
      return undefined;
    }

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
      autoScrollRef.current = distanceFromBottom <= 80;
    };

    handleScroll();
    container.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [messagesContainerRef.current]);

  const handleStreamResponse = useCallback(async (response, { sessionId, onConnected, controller } = {}) => {
    console.log('🌊 Persistent stream handler started', { sessionId });

    if (!response || !response.ok) {
      const errorText = response ? await response.text() : 'No response body';

      if (response && (response.status === 429 || errorText.includes('Rate limit exceeded'))) {
        const rateLimitMatch = errorText.match(/(\d+)\s*requests\/month/);
        const limit = rateLimitMatch ? rateLimitMatch[1] : '10';

        const error = new Error(`Rate limit exceeded. ${limit} requests/month.`);
        error.code = 'RATE_LIMIT_EXCEEDED';
        error.rateLimitInfo = {
          requestsLimit: parseInt(limit, 10),
          requestsUsed: parseInt(limit, 10),
          resetsAt: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1).toISOString()
        };
        throw error;
      }

      throw new Error(`HTTP error! status: ${response?.status} - ${errorText}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    let state = null;
    let mutableState = null;
    let aiMessageId = null;
    let currentTurnEntry = null;
    let sessionTimeoutId = null;
    let sessionEventReceived = false;
    // Set when a Conductor peer announces an incoming injected turn on this
    // session (conductor_message_received). It tells initializeTurnState to
    // synthesize a turn entry for the server-initiated turn that follows, since
    // no local user message created one. See the Conductor handling in the loop.
    const pendingConductorTurns = [];

    const clearSessionTimeout = () => {
      if (sessionTimeoutId) {
        clearTimeout(sessionTimeoutId);
        sessionTimeoutId = null;
      }
    };

    const SESSION_TIMEOUT_MS = 5000;

    let setStreamingState = (nextState) => {
      mutableState = nextState;
      streamingStateRef.current = {
        ...streamingStateRef.current,
        state: mutableState,
        updateState: setStreamingState
      };
    };

    const initializeTurnState = async () => {
      if (state) {
        return true;
      }

      // A peer agent (Conductor) injected a turn on this session. No local user
      // message created a pending turn entry, so awaitNextTurnEntry would block
      // forever and the injected turn would never render. When a
      // conductor_message_received event announced this incoming turn and no
      // local entry is queued, synthesize a server-initiated entry so the turn
      // renders like a normal one. (Gated on the announcement flag so a stray
      // event without a pending turn still hits the original skip path.)
      if (pendingConductorTurns.length > 0 && pendingTurnQueueRef.current.length === 0) {
        const conductorTurn = pendingConductorTurns.shift();
        // ONE CHAT ACROSS APPS: route this injected turn into the SENDER'S
        // chat. Reuses the connector-mint override machinery: the catch-up
        // effect clears the override once currentChatId lands on the target.
        // The view switch is a pure viewport change: the live chat mirror
        // hydrates the shared chat's history the moment the view lands on it,
        // so no explicit boundary pull is needed here.
        const sourceChatId = conductorTurn.sourceChatId || null;
        if (sourceChatId && sourceChatId !== currentChatIdRef.current) {
          chatIdOverrideRef.current = sourceChatId;
          conductorMintedChatRef.current = sourceChatId;
          try {
            markProgrammaticSwitch(sourceChatId);
            if (typeof switchToChat === 'function') switchToChat(sourceChatId);
          } catch (switchErr) {
            console.warn('[hooks] Conductor: failed to switch to source chat:', switchErr?.message);
          }
        }
        createPendingTurn({
          aiMessageId:
            conductorTurn.assistantMessageId
            || generateMessageId(),
          requestId: `conductor-${Date.now()}`,
          // Injected turns live in the CONVERSATION chat, never the pane's
          // own plumbing chat or whatever the user is currently viewing.
          chatId: sourceChatId || currentChatIdRef.current,
          // Initial dispatches run as hidden tool workers. A reply returning
          // to the requesting surface is the visible continuation of the same
          // Restack chat.
          hidden: conductorTurn.reply !== true,
        });
        // No local send flips the composer into its busy state for an injected
        // turn, so do it here; finalizeCurrentTurn clears it like any turn.
        if (conductorTurn.reply === true) {
          setIsChatStreaming(true);
          setAwaitingPeer(null);
        }
      }

      // No turn is queued: this is the eager/idle stream path (the pane
      // attached its SSE stream for cross-app registration before any user
      // message). NEVER park here: awaitNextTurnEntry would block this reader
      // BEFORE its read loop starts, so the pane would ignore every event on
      // the stream, including an injected conductor turn's tool_call_request
      // events (each then dies at the backend's 90s timeout and the peer
      // reports a fake "disconnection"). Legitimate turns always queue their
      // entry before their events arrive (sendMessageToAI calls
      // createPendingTurn before POST; conductor turns synthesize one above),
      // so an empty queue here never belongs to a real turn. Same escape as
      // bad33e81, lost in the bcc6b28b revert / de7aecf8 reland cycle.
      if (pendingTurnQueueRef.current.length === 0) {
        return false;
      }
      if (!isTurnReadyForStreamEvents(pendingTurnQueueRef.current[0])) {
        return false;
      }

      currentTurnEntry = await awaitNextTurnEntry();
      if (!currentTurnEntry) {
        return false;
      }

      activeTurnRef.current = currentTurnEntry;
      // Pin the turn's HOME chat before promoting an attested user message.
      // The entry's chat is immutable for the lifetime of the turn.
      activeTurnChatIdRef.current = currentTurnEntry.chatId
        || chatIdOverrideRef.current
        || activeTurnChatIdRef.current
        || currentChatIdRef.current;
      await attestPendingTurnForPersistence(currentTurnEntry, {
        persistUserMessage: (message) => addSanitizedMessage(message),
        removeMemoryMessages: (messageIds) => {
          const ids = new Set(messageIds);
          setEphemeralMessages((current) =>
            current.filter((message) => !ids.has(message.id))
          );
        },
      });
      if (
        !currentTurnEntry.ephemeral &&
        currentTurnEntry.analytics &&
        currentTurnEntry.analyticsCaptured !== true
      ) {
        currentTurnEntry.analyticsCaptured = true;
        trackAIChat?.('send_message', {
          messageLength: currentTurnEntry.analytics.messageLength,
          chatType: 'ai_chat',
          hasAttachments: currentTurnEntry.analytics.hasAttachments,
        });
        capture('message_sent', {
          message_length: currentTurnEntry.analytics.messageLength,
          model: currentTurnEntry.analytics.model,
        });
        startTimer('ai_response');
      }
      sessionEventReceived = false;

      // Any initializing turn means the session is busy. Ordinary sends set
      // this in handleSend (idempotent here), but queued-steer, conductor,
      // and compact turns have no local send, so set it at the source.
      if (!currentTurnEntry.hidden) {
        isChatStreamingRef.current = true;
        setIsChatStreaming(true);
      }

      const existingState = streamingStateRef.current?.state;
      const hasMatchingState = existingState && existingState.aiMessageId === currentTurnEntry.aiMessageId;

      const nextState = hasMatchingState
        ? {
            ...existingState,
            metadata: {
              ...existingState.metadata,
              isStreaming: true,
              statusLabel: existingState.metadata?.statusLabel || INITIAL_STREAM_STATUS,
              statusHint: existingState.metadata?.statusHint || null
            }
          }
        : streamProcessor.initializeState(currentTurnEntry.aiMessageId);

      state = nextState;
      mutableState = nextState;
      aiMessageId = nextState.aiMessageId;
      // Live authorship for the turn's bubble (see primeStreamingMessage);
      // conductor-injected and queued turns have no prime step, so claim here.
      if (!currentTurnEntry.hidden && !currentTurnEntry.ephemeral) {
        smartCache.claimLiveMessage(aiMessageId);
      }

      setStreamingState = (nextValue) => {
        mutableState = nextValue;
        streamingStateRef.current = {
          ...streamingStateRef.current,
          state: mutableState,
          updateState: setStreamingState
        };
      };

      streamingStateRef.current = {
        state: mutableState,
        updateState: setStreamingState
      };

      if (!currentTurnEntry.hidden && currentTurnEntry.ephemeral) {
        const message = streamProcessor.createMessage(mutableState);
        message.isStreaming = true;
        upsertEphemeralMessage(message);
      } else if (!currentTurnEntry.hidden) {
        await enqueueMessageUpdate(() => addSanitizedMessage(streamProcessor.createMessage(mutableState)), true);
      }

      clearSessionTimeout();
      sessionTimeoutId = setTimeout(() => {
        if (!sessionEventReceived) {
          console.warn('⚠️ Session ID timeout: No session event received within 5 seconds');
          console.warn('⚠️ Session continuity may be broken - subsequent requests may fork the conversation');
        }
      }, SESSION_TIMEOUT_MS);

      return true;
    };

    // Office.js code execution handler — when the backend sends office_code SSE events,
    // evaluate the JS inside PowerPoint.run() via the bridge (legacy fire-and-forget path)
    const handleOfficeCode = async (code, meta) => {
      if (!powerpointBridge) {
        console.warn('[hooks] office_code event received but no PowerPoint bridge available');
        return;
      }
      try {
        console.log('[hooks] Executing Office.js code via bridge', { codeLength: code?.length, meta });
        await powerpointBridge.executeCode(code);
      } catch (error) {
        console.error('[hooks] Office.js code execution failed:', error);
      }
    };

    // Two-way tool call handler: backend MCP server sends tool_call_request via SSE,
    // frontend executes in PowerPoint.run(), then POSTs result back to resolve the MCP promise
    const confirmCallDurableOfficeOperationsComplete = async (callId) => {
      const durableOperations =
        lockerOfficeOperationsByCallRef.current.get(callId) || [];
      if (durableOperations.length === 0) return null;

      const journal = getLockerOfficeJournal();
      const remaining = [];
      for (const operation of durableOperations) {
        let completed = false;
        let outcome = RECONCILE_OUTCOMES.UNRECONCILED;
        const retryDelaysMs = [0, 250, 750, 2000, 5000];
        for (const retryDelayMs of retryDelaysMs) {
          if (retryDelayMs > 0) {
            await new Promise((resolve) => setTimeout(resolve, retryDelayMs));
          }
          try {
            const answer =
              await confirmDurableOfficeOperationComplete(operation);
            completed = answer?.completed === true;
            outcome = answer?.outcome || outcome;
          } catch {
            completed = false;
            outcome = RECONCILE_OUTCOMES.UNRECONCILED;
          }
          if (completed) break;
        }
        // The background flush needs the same answer this loop just got, or
        // its own ceiling never advances for an operation polled here first.
        if (!completed) {
          try {
            journal.recordReconcileOutcome(
              operation.operationId,
              outcome
            );
          } catch {
            // Recovery storage may be unwritable; the fence is still closed.
          }
        }
        if (completed) {
          try {
            const entry = journal.list().find(
              (candidate) =>
                candidate.operationId === operation.operationId
            );
            if (entry) journal.removeConfirmedComplete(entry);
          } catch {
            remaining.push(operation);
          }
        } else {
          remaining.push(operation);
        }
      }
      if (remaining.length > 0) {
        lockerOfficeOperationsByCallRef.current.set(callId, remaining);
        return false;
      }
      lockerOfficeOperationsByCallRef.current.delete(callId);
      return true;
    };

    const postToolResult = async (callId, result, error = null) => {
      const durableOperations =
        lockerOfficeOperationsByCallRef.current.get(callId) || [];
      const preStartAbandoned =
        preStartAbandonedOfficeCallIdsRef.current.has(callId);
      assertPreStartAbandonedError({
        preStartAbandoned,
        error,
      });
      if (shouldSuppressCancelledOfficeResult({
        cancelled:
          cancelledRemoteToolCallIdsRef.current.has(callId),
        preStartAbandoned,
      })) {
        console.log('[hooks] Suppressed result for cancelled connected Office tool:', callId);
        if (durableOperations.length > 0) {
          const journal = getLockerOfficeJournal();
          for (const operation of durableOperations) {
            markOfficeOperationResolutionRequired(
              operation,
              journal
            );
          }
          throw resolutionRequiredError(
            'Secure Office operation result delivery was cancelled'
          );
        }
        return false;
      }
      try {
        const token = currentUser ? await currentUser.getIdToken() : null;
        const streamingBaseUrl = process.env.REACT_APP_STREAMING_SERVICE_URL || 'https://claude-streaming-service-103376361923.us-central1.run.app';
        const delivery = await deliverOfficeToolResult({
          url: `${streamingBaseUrl}/sessions/${sessionId}/tool-results`,
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          payload: {
            callId,
            result,
            error,
            ...(preStartAbandoned
              ? { preStartAbandoned: true }
              : {}),
            ...(durableOperations.length > 0
              ? { lockerOfficeOperations: durableOperations }
              : {}),
          },
          requireOfficeResultDelivered:
            durableOperations.length > 0,
          requirePreStartAbandoned: preStartAbandoned,
        });
        const completionPlan = planOfficeToolResultCompletion({
          accepted: delivery.accepted,
          durableOperationCount: durableOperations.length,
          preStartAbandoned,
        });
        if (delivery.accepted) {
          if (completionPlan.markResultDelivered) {
            const journal = getLockerOfficeJournal();
            for (const operation of durableOperations) {
              journal.markResultDelivered(operation.operationId);
            }
          }
          if (completionPlan.clearPreStartReceipt) {
            lockerOfficeOperationsByCallRef.current.delete(callId);
            preStartAbandonedOfficeCallIdsRef.current.delete(callId);
            reconciledPreStartOfficeCallIdsRef.current.add(callId);
            setTimeout(() => {
              reconciledPreStartOfficeCallIdsRef.current.delete(callId);
            }, 10 * 60 * 1000);
          }
          return true;
        }
        if (completionPlan.suppressQuiescence) {
          if (preStartAbandoned) {
            throw suppressQuiescenceError(
              'Pre-start Office error delivery was not confirmed',
              'locker_office_pre_start_result_unconfirmed'
            );
          }
          const journal = getLockerOfficeJournal();
          for (const operation of durableOperations) {
            markOfficeOperationResolutionRequired(
              operation,
              journal
            );
          }
          throw resolutionRequiredError(
            'Secure Office operation result delivery was not confirmed'
          );
        }
        console.error('[hooks] Tool result POST was not accepted:', {
          callId,
          status: delivery.status,
          unknown: delivery.unknown === true,
        });
        return false;
      } catch (postError) {
        if (postError?.suppressToolResult === true) {
          throw postError;
        }
        console.error('[hooks] Failed to post tool result:', postError);
        if (durableOperations.length > 0) {
          const journal = getLockerOfficeJournal();
          for (const operation of durableOperations) {
            markOfficeOperationResolutionRequired(
              operation,
              journal
            );
          }
          throw resolutionRequiredError(
            'Secure Office operation result delivery was not confirmed'
          );
        }
        return false;
      }
    };

    const postToolQuiesced = async (callId) => {
      if (reconciledPreStartOfficeCallIdsRef.current.has(callId)) {
        reconciledPreStartOfficeCallIdsRef.current.delete(callId);
        return true;
      }
      const durableOperations =
        lockerOfficeOperationsByCallRef.current.get(callId) || [];
      if (durableOperations.length > 0) {
        return confirmCallDurableOfficeOperationsComplete(callId);
      }

      try {
        const token = currentUser ? await currentUser.getIdToken() : null;
        const streamingBaseUrl = process.env.REACT_APP_STREAMING_SERVICE_URL || 'https://claude-streaming-service-103376361923.us-central1.run.app';
        const retryDelaysMs = [0, 250, 750, 2000, 5000];
        for (const retryDelayMs of retryDelaysMs) {
          if (retryDelayMs > 0) {
            await new Promise((resolve) => setTimeout(resolve, retryDelayMs));
          }
          try {
            const { response } = await fetchWithDeadline({
              url: `${streamingBaseUrl}/sessions/${sessionId}/tool-quiesced`,
              options: {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  ...(token && { Authorization: `Bearer ${token}` }),
                },
                body: JSON.stringify({ callId }),
              },
            });
            if (response.ok) return true;
            if (
              response.status !== 408
              && response.status !== 429
              && response.status < 500
            ) {
              return false;
            }
          } catch {
            // A late Office promise may settle while the pane reconnects.
            // Retry the completion acknowledgement with the same call id.
          }
        }
      } catch (error) {
        console.warn('[hooks] Office quiescence acknowledgement failed:', {
          callId,
          code: error?.code || error?.name || 'quiescence_failed',
        });
      }
      return false;
    };

    const markOfficeOperationResolutionRequired = (
      operation,
      journal
    ) => {
      let entry = operation;
      try {
        entry = journal.markRunning(operation.operationId);
      } catch {
        // Keep the server-issued identity even if local recovery storage is
        // no longer writable. The server fence remains fail-closed.
      }
      const existing = unresolvedOfficeRecoveryRef.current;
      if (
        !existing.some(
          (candidate) =>
            candidate.operationId === operation.operationId
        )
      ) {
        setOfficeRecoveryEntries([
          ...existing,
          entry,
        ]);
      }
    };

    const suppressQuiescenceError = (
      message,
      code = 'locker_office_result_unresolved'
    ) => {
      const error = new Error(message);
      error.code = code;
      error.suppressToolResult = true;
      return error;
    };

    const resolutionRequiredError = (message) =>
      suppressQuiescenceError(
        message,
        'locker_office_resolution_required'
      );

    const abandonPreRunningOfficeOperations = async (
      operations,
      journal
    ) =>
      abandonPreparedOfficeOperations({
        operations,
        abandonServerOperation: (operation) =>
          transitionDurableOfficeOperation(
            operation,
            'abandon',
            3
          ),
        abandonLocalOperation:
          typeof journal?.abandonSafe === 'function'
            ? (operation) =>
              journal.abandonSafe(operation.operationId)
            : null,
        handleRunningOperation:
          journal
            ? (operation) =>
              markOfficeOperationResolutionRequired(
                operation,
                journal
              )
            : null,
        handleLocalCleanupError: (operation, error) => {
          console.warn(
            '[hooks] Server abandoned Office operation but local recovery cleanup failed:',
            {
              operationId: operation.operationId,
              code:
                error?.code
                || error?.name
                || 'locker_office_local_cleanup_failed',
            }
          );
        },
      });

    const handleToolCallRequest = async (callId, toolName, args, requestMetadata = {}) => {
      if (cancelledRemoteToolCallIdsRef.current.has(callId)) {
        console.log('[hooks] Skipped cancelled connected Office tool before dispatch:', { callId, toolName });
        return;
      }
      let privateDispatch = null;
      if (shouldGuardRemoteToolCall(requestMetadata)) {
        try {
          const marker = await readValidatedConnectorDocumentPrivacy(
            financeOfficeControllerRef.current
          );
          setDocumentPrivacy(marker);
          documentPrivacyRef.current = marker;
          assertRemoteNonPrivateToolAllowed(requestMetadata, marker);
        } catch (remoteToolError) {
          console.error('[hooks] Rejected connected Office tool:', {
            callId,
            toolName,
            errorType: remoteToolError?.name || 'Error',
          });
          await postToolResult(
            callId,
            null,
            'Connected Office operation unavailable'
          );
          return;
        }
      }
      if (requestMetadata.private === true) {
        const controller = financeOfficeControllerRef.current;
        try {
          if (!controller) {
            throw new Error('Private Office document guard is unavailable');
          }
          const marker = requestMetadata.financeOfficeProtocol === true
            ? await controller.getDocumentPrivacyState()
            : await controller.getConnectorDocumentPrivacyState();
          setDocumentPrivacy(marker);
          documentPrivacyRef.current = marker;
          privateDispatch = validatePrivateOfficeEventMetadata(
            app,
            toolName,
            requestMetadata,
            marker,
            args,
            liveDataSessionRef.current?.active === true
              ? liveDataSessionRef.current.operationId
              : null
          );
        } catch (privateToolError) {
          console.error('[hooks] Rejected private Office tool:', {
            callId,
            toolName,
            private: true,
            errorType: privateToolError?.name || 'Error',
          });
          await postToolResult(callId, null, 'Private Office operation failed');
          return;
        }
      }
      if (privateDispatch?.financeOfficeProtocol === true) {
        const controller = financeOfficeControllerRef.current;
        if (!controller?.handles(toolName)) {
          await postToolResult(callId, null, 'Private Office operation failed');
          return;
        }
        try {
          const result = await controller.execute(toolName, args, {
            signal: requestMetadata.privateAbortSignal,
            privateRequest: true,
          });
          if (
            result.status === 'committed' ||
            result.status === 'committed_after_cancel' ||
            result.status === 'indeterminate'
          ) {
            const marker = await controller.getDocumentPrivacyState();
            setDocumentPrivacy(marker);
            documentPrivacyRef.current = marker;
          }
          await postToolResult(callId, JSON.stringify(result));
        } catch (error) {
          if (error?.suppressToolResult === true) {
            throw error;
          }
          console.error('[hooks] Typed private Office operation failed', {
            callId,
            toolName,
            private: true,
            code: error?.code || 'finance_office_failed',
          });
          await postToolResult(callId, null, 'Private Office operation failed');
        }
        return;
      }
      // ask_user_question doesn't touch PowerPoint. It renders an options
      // card and resolves when the user answers. Intercept BEFORE the bridge
      // check (and before the default case, which would executeCode).
      if (toolName === 'ask_user_question') {
        const questions = Array.isArray(args?.questions) ? args.questions : [];
        if (questions.length === 0) {
          // The tool schema requires 1-4 questions, so reaching this branch
          // means the pane received a payload the server never sent. Name the
          // shape: the alternative is a card that silently never appears.
          console.error('[hooks] ask_user_question arrived without questions', {
            callId,
            argsType: Array.isArray(args) ? 'array' : typeof args,
            argKeys: args && typeof args === 'object' && !Array.isArray(args)
              ? Object.keys(args)
              : null,
          });
          await postToolResult(callId, null, 'ask_user_question called with no questions');
          return;
        }
        setPendingUserQuestion({
          callId,
          questions,
          submit: (answers) => postToolResult(callId, JSON.stringify(answers))
        });
        return;
      }

      // propose_doc_edits (Word) stages substantive edits as review cards instead
      // of writing directly. Validate findability, then render ProposeEditsCard and
      // wait for the user to Apply / Dismiss each one. Intercepted like
      // ask_user_question; the model gets the review outcome as the tool result
      // (our equivalent of the reference <user_reviewed_edits>).
      if (toolName === 'propose_doc_edits') {
        const proposed = Array.isArray(args?.edits) ? args.edits : [];
        if (proposed.length === 0) {
          await postToolResult(callId, null, 'propose_doc_edits called with no edits');
          return;
        }
        if (!powerpointBridge) {
          await postToolResult(callId, null, 'Office bridge not available');
          return;
        }
        try {
          // Validate each old_text is uniquely findable now (applies nothing).
          const validation = await powerpointBridge.proposeDocEdits(args);
          const validById = new Map(
            (validation?.edits || []).map((e) => [String(e.id), e])
          );
          // Give every edit a stable id (positional fallback) so the card rows and
          // the validation results key off the same value.
          const editsWithIds = proposed.map((e, i) => ({ ...e, id: e.id || `edit-${i + 1}` }));
          const invalid = editsWithIds
            .filter((e) => {
              const v = validById.get(String(e.id));
              return v && v.valid === false;
            })
            .map((e) => ({ id: e.id, error: validById.get(String(e.id))?.error || 'Not uniquely findable.' }));

          const review = createQuiescedOfficeReview({
            applyEdit: ({ old_text, new_text }) =>
              powerpointBridge.editDocText({
                old_text,
                new_text,
              }),
            submitOutcome: (outcome) =>
              postToolResult(
                callId,
                JSON.stringify({
                  status: 'reviewed',
                  ...outcome,
                })
              ),
          });
          setPendingProposeEdits((previous) => {
            previous?.cancel?.('superseded');
            return {
              callId,
              edits: editsWithIds,
              invalid,
              // Keep the tracked tool open until every accepted Word.run
              // promise settles, including Apply All.
              apply: review.apply,
              submit: review.submit,
              cancel: review.cancel,
            };
          });
          await review.completion;
        } catch (err) {
          if (err?.suppressToolResult === true) {
            throw err;
          }
          await postToolResult(callId, null, err.message || 'propose_doc_edits validation failed');
        }
        return;
      }

      if (!powerpointBridge) {
        await postToolResult(callId, null, 'Office bridge not available');
        return;
      }

      // apply_template (all hosts): resolve the user's ACTIVE template
      // (sidebar selection or firm default), fetch the file from Storage,
      // and insert it into the open document via the host bridge. The
      // backend tool is a thin trigger; template resolution is client-side
      // because the selection lives in the user's workspace doc.
      if (toolName === 'apply_template') {
        try {
          const activeTemplate = await getActiveTemplate(currentUser?.uid, app);
          if (!activeTemplate?.downloadUrl) {
            await postToolResult(callId, null,
              'No template is selected. Ask the user to pick one under Workflows > Templates in the sidebar (or upload one), then try again.');
            return;
          }
          if (!templateMatchesApp(activeTemplate.fileName || activeTemplate.name, app)) {
            await postToolResult(callId, null,
              `The selected template "${activeTemplate.name}" (${activeTemplate.fileName || 'unknown type'}) is not a ${app} file. ` +
              `This app needs ${(TEMPLATE_EXTENSIONS_BY_APP[app] || []).join(' or ')}. Tell the user which template is selected and ask them to pick a matching one.`);
            return;
          }
          const fileResp = await fetch(activeTemplate.downloadUrl);
          if (!fileResp.ok) {
            await postToolResult(callId, null, `Could not download the template file (HTTP ${fileResp.status}). Ask the user to re-upload it.`);
            return;
          }
          const base64 = arrayBufferToBase64(await fileResp.arrayBuffer());
          const applyScope = requestMetadata.originTurnId || activeRequestIdRef.current || callId;
          const applied = await powerpointBridge.applyTemplateFromBase64(base64, { applyScope });
          await postToolResult(callId, JSON.stringify({ template: activeTemplate.name, ...applied }));
        } catch (templateError) {
          if (templateError?.suppressToolResult === true) {
            throw templateError;
          }
          console.error('[hooks] apply_template failed:', templateError);
          await postToolResult(callId, null, templateError.message || 'apply_template failed');
        }
        return;
      }

      try {
        console.log('[hooks] Two-way tool call:', { callId, toolName, app, argsKeys: Object.keys(args || {}) });
        let result;

        // Excel surface: route to the ExcelBridge methods. `powerpointBridge`
        // holds whichever host bridge is active (see useOffice().bridge), so it
        // is the ExcelBridge instance here.
        if (app === 'excel') {
          switch (toolName) {
            case 'get_initial_state':
              result = await powerpointBridge.getInitialState();
              break;
            case 'execute_office_js':
              result = await powerpointBridge.executeCode(args.code);
              break;
            case 'get_cell_ranges':
              result = await powerpointBridge.getCellRanges(args);
              break;
            case 'screenshot_range':
              result = await powerpointBridge.screenshotRange(args);
              break;
            case 'get_range_as_csv':
              result = await powerpointBridge.getRangeAsCsv(args);
              break;
            case 'search_data':
              result = await powerpointBridge.searchData(args);
              break;
            case 'get_all_objects':
              result = await powerpointBridge.getAllObjects(args);
              break;
            case 'set_cell_range':
              result = await powerpointBridge.setCellRange(args);
              break;
            case 'clear_cell_range':
              result = await powerpointBridge.clearCellRange(args);
              break;
            case 'copy_to':
              result = await powerpointBridge.copyTo(args);
              break;
            case 'modify_sheet_structure':
              result = await powerpointBridge.modifySheetStructure(args);
              break;
            case 'modify_workbook_structure':
              result = await powerpointBridge.modifyWorkbookStructure(args);
              break;
            case 'resize_range':
              result = await powerpointBridge.resizeRange(args);
              break;
            case 'modify_object':
              result = await powerpointBridge.modifyObject(args);
              break;
            default:
              console.warn('[hooks] Unknown Excel tool name, falling back to executeCode:', toolName);
              result = await powerpointBridge.executeCode(args.code);
              break;
          }
          await postToolResult(callId, result != null ? JSON.stringify(result) : 'Success');
          return;
        }

        // Word surface: route to the WordBridge methods. `powerpointBridge` holds
        // whichever host bridge is active (see useOffice().bridge), so it is the
        // WordBridge instance here. propose_doc_edits / explain_edits do not touch
        // the document (no review-card UI yet); we acknowledge them so the model
        // can summarize for the user and proceed.
        if (app === 'word') {
          switch (toolName) {
            case 'get_initial_state':
              result = await powerpointBridge.getInitialState();
              break;
            case 'execute_office_js':
              result = await powerpointBridge.executeCode(args.code);
              break;
            case 'edit_doc_text':
              result = await powerpointBridge.editDocText(args);
              break;
            case 'edit_doc_list':
              result = await powerpointBridge.editDocList(args);
              break;
            case 'read_doc_section':
              result = await powerpointBridge.readDocSection(args);
              break;
            case 'collapse_blank_paragraphs':
              result = await powerpointBridge.collapseBlankParagraphs(args);
              break;
            case 'verify_doc':
              result = await powerpointBridge.verifyDoc();
              break;
            case 'screenshot_document':
              result = await powerpointBridge.screenshotDocument(args);
              break;
            // propose_doc_edits is intercepted earlier (renders the review card),
            // and explain_edits resolves server-side (the verifier), so neither
            // reaches this switch.
            default:
              console.warn('[hooks] Unknown Word tool name, falling back to executeCode:', toolName);
              result = await powerpointBridge.executeCode(args.code);
              break;
          }
          await postToolResult(callId, result != null ? JSON.stringify(result) : 'Success');
          return;
        }

        switch (toolName) {
          case 'get_initial_state':
            result = await powerpointBridge.getInitialState();
            break;

          case 'execute_office_js':
            result = await powerpointBridge.executeCode(args.code);
            break;

          case 'screenshot_slide':
            result = await powerpointBridge.screenshotSlide(args.slide_index);
            break;

          case 'edit_slide_xml':
            result = await powerpointBridge.editSlideXml(
              args.slide_index,
              args.code
            );
            break;

          case 'edit_slide_chart':
            result = await powerpointBridge.editSlideChart(
              args.slide_index,
              args.code
            );
            break;

          case 'edit_slide_master':
            result = await powerpointBridge.editSlideMaster(args.code);
            break;

          case 'read_slide_text':
            result = await powerpointBridge.readSlideText(args.slide_index, args.shape_id);
            break;

          case 'edit_slide_text':
            result = await powerpointBridge.editSlideText(
              args.slide_index,
              args.shape_id,
              args.code
            );
            break;

          case 'duplicate_slide':
            result = await powerpointBridge.duplicateSlide(args.slide_index);
            break;

          case 'verify_slides':
            result = await powerpointBridge.verifySlides();
            break;

          case 'read_deck_content':
            result = await powerpointBridge.readDeckContent();
            break;

          case 'list_slide_shapes':
            result = await powerpointBridge.listSlideShapes(args.slide_index);
            break;

          case 'insert_icon':
            // svg content is resolved by the backend's icon library and
            // forwarded in args; this leg embeds it into the slide.
            result = await powerpointBridge.insertIcon(args);
            break;

          default:
            console.warn('[hooks] Unknown tool name:', toolName);
            result = await powerpointBridge.executeCode(args.code);
            break;
        }

        await postToolResult(callId, result != null ? JSON.stringify(result) : 'Success');
      } catch (error) {
        if (error?.suppressToolResult === true) {
          throw error;
        }
        const privateRequest = requestMetadata.private === true;
        const safeError = privateRequest
          ? 'Private Office operation failed'
          : (error.message || 'Execution failed');
        console.error('[hooks] Tool call execution failed:', privateRequest
          ? { callId, toolName, private: true, errorType: error?.name || 'Error' }
          : { callId, toolName, error: safeError });
        await postToolResult(callId, null, safeError);
      }
    };

    const enqueueToolCallRequest = async (callId, toolName, args, requestMetadata = {}) => {
      const durableOperations = normalizeLockerOfficeOperations(
        requestMetadata.lockerOfficeOperations
      );
      if (durableOperations.length > 1) {
        throw resolutionRequiredError(
          'Secure Office operation batching is unavailable'
        );
      }
      const admissionIdentity = officeToolAdmissionIdentity({
        toolName,
        durableOperations,
      });
      const existingRun =
        officeToolRunsByCallRef.current.get(callId);
      if (existingRun) {
        if (
          existingRun.admissionIdentity !== admissionIdentity
        ) {
          throw new Error('Secure Office operation identity changed');
        }
        return existingRun.promise;
      }
      let resolveAdmission;
      let rejectAdmission;
      const admissionPromise = new Promise((resolve, reject) => {
        resolveAdmission = resolve;
        rejectAdmission = reject;
      });
      const admissionState = {
        settled: false,
      };
      const trackedRecord = Object.freeze({
        promise: admissionPromise,
        durableOperations,
        admissionIdentity,
        admissionState,
      });
      officeToolRunsByCallRef.current.set(callId, trackedRecord);
      const scheduleAdmissionCleanup = () => {
        setTimeout(() => {
          if (
            officeToolRunsByCallRef.current.get(callId)
            === trackedRecord
            && !preStartAbandonedOfficeCallIdsRef.current.has(callId)
          ) {
            officeToolRunsByCallRef.current.delete(callId);
          }
        }, 10 * 60 * 1000);
      };
      admissionPromise.then(
        () => {
          admissionState.settled = true;
          scheduleAdmissionCleanup();
        },
        () => {
          admissionState.settled = true;
          scheduleAdmissionCleanup();
        }
      );
      try {
      let journal = null;
      if (durableOperations.length > 0) {
        if (!currentUser?.uid) {
          throw new Error('Secure Office operation identity is unavailable');
        }
        const existing =
          lockerOfficeOperationsByCallRef.current.get(callId);
        if (
          existing
          && JSON.stringify(existing) !== JSON.stringify(durableOperations)
        ) {
          throw new Error('Secure Office operation identity changed');
        }
        lockerOfficeOperationsByCallRef.current.set(
          callId,
          durableOperations
        );
        let preparedEntries;
        try {
          journal = getLockerOfficeJournal();
          preparedEntries = durableOperations.map(
            (operation) => journal.recordPrepared({
              ...operation,
              userId: currentUser.uid,
              callId,
            })
          );
        } catch (preparationError) {
          const abandoned =
            await abandonPreRunningOfficeOperations(
              durableOperations,
              journal
            );
          if (!abandoned) {
            throw resolutionRequiredError(
              'Secure Office operation preparation could not be reconciled'
            );
          }
          preStartAbandonedOfficeCallIdsRef.current.add(callId);
          throw createPreStartAbandonedFailureError({
            cause: preparationError,
          });
        }
        const runningEntry = preparedEntries.find(
          (entry) => entry.status === 'running'
        );
        if (runningEntry) {
          markOfficeOperationResolutionRequired(
            runningEntry,
            journal
          );
          throw resolutionRequiredError(
            'A prior secure Office operation has an unknown outcome'
          );
        }
        if (
          preparedEntries.some(
            (entry) =>
              entry.status === 'result_delivered'
              || entry.status === 'settled'
          )
        ) {
          throw resolutionRequiredError(
            'The prior Office result is waiting for turn completion'
          );
        }
        try {
          for (const operation of durableOperations) {
            const persisted =
              await transitionDurableOfficeOperation(
                operation,
                'client-persisted',
                3
              );
            journal.markClientPersisted(operation.operationId);
            if (persisted.state === 'running') {
              markOfficeOperationResolutionRequired(
                operation,
                journal
              );
              throw resolutionRequiredError(
                'A prior secure Office operation has an unknown outcome'
              );
            }
          }
        } catch (persistenceError) {
          if (persistenceError?.suppressToolResult === true) {
            throw persistenceError;
          }
          const abandoned =
            await abandonPreRunningOfficeOperations(
              durableOperations,
              journal
            );
          if (!abandoned) {
            throw resolutionRequiredError(
              'Secure Office operation delivery could not be reconciled'
            );
          }
          preStartAbandonedOfficeCallIdsRef.current.add(callId);
          throw createPreStartAbandonedFailureError({
            cause: persistenceError,
          });
        }
      }
      const isPrivate = requestMetadata.private === true;
      const privateAbortSignal = isPrivate
        ? privateOfficeCancellationRegistryRef.current.register(callId)
        : null;
      const dispatchMetadata = isPrivate
        ? { ...requestMetadata, privateAbortSignal }
        : requestMetadata;
      const dispatch = async () => {
        if (cancelledRemoteToolCallIdsRef.current.has(callId)) {
          console.log('[hooks] Skipped cancelled connected Office tool in queue:', { callId, toolName });
          if (durableOperations.length > 0) {
            const abandoned =
              await abandonPreRunningOfficeOperations(
                durableOperations,
                journal
              );
            if (!abandoned) {
              throw resolutionRequiredError(
                'Cancelled Office operation requires review'
              );
            }
            preStartAbandonedOfficeCallIdsRef.current.add(callId);
            throw createPreStartAbandonedCancellationError();
          }
          return undefined;
        }
        if (durableOperations.length > 0) {
          const [operation] = durableOperations;
          let started;
          try {
            started = await transitionDurableOfficeOperation(
              operation,
              'start',
              2
            );
          } catch (startError) {
            const abandoned =
              await abandonPreRunningOfficeOperations(
                durableOperations,
                journal
              );
            if (abandoned) {
              preStartAbandonedOfficeCallIdsRef.current.add(callId);
              throw createPreStartAbandonedFailureError({
                cause: startError,
              });
            }
            markOfficeOperationResolutionRequired(
              operation,
              journal
            );
            throw resolutionRequiredError(
              'Secure Office operation start could not be confirmed'
            );
          }
          journal.markRunning(operation.operationId);
          if (
            started?.started !== true
            || started?.duplicate === true
            || started?.state !== 'running'
          ) {
            markOfficeOperationResolutionRequired(
              operation,
              journal
            );
            throw resolutionRequiredError(
              'Secure Office operation start was already recorded'
            );
          }
        }
        return handleToolCallRequest(callId, toolName, args, dispatchMetadata);
      };
      const run = officeToolQueueRef.current.then(
        dispatch,
        dispatch
      );
      officeToolQueueRef.current = run.catch(() => {});
      let requiresExplicitResolution = false;
      const trackedRun = run
        .catch((error) => {
          if (shouldSuppressOfficeQuiescence(error)) {
            requiresExplicitResolution = true;
          }
          throw error;
        })
        .finally(() => {
          if (!requiresExplicitResolution) {
            void postToolQuiesced(callId);
          }
          if (isPrivate) {
            privateOfficeCancellationRegistryRef.current.release(
              callId
            );
          }
        });
      trackedRun.then(resolveAdmission, rejectAdmission);
      return admissionPromise;
      } catch (error) {
        rejectAdmission(error);
        return admissionPromise;
      }
    };

    // NOTE: permission-approval UI was removed. The backend runs
    // permissionMode: 'dontAsk' with a fixed allowedTools allowlist, so no
    // permission_request events are ever emitted. The postPermissionResponse
    // POST and handlePermissionRequest stub that used to live here have been
    // deleted. If we ever reintroduce canUseTool, wire a fresh handler.

    const context = {
      // Which Office host this pane is. The stream processor must not infer it
      // from tool names: execute_office_js is shared by all three, and it is
      // the tool whose card said "Working on slide" on a workbook.
      app,
      onOfficeCode: handleOfficeCode,
      onToolCallRequest: enqueueToolCallRequest,
      onLoopTelemetry: handleLoopTelemetryUpdate,
      onDraftEnvelope: handleDraftEnvelopeEvent,
      setDraftEnvelopeLog,
      setIsChatStreaming,
      onCreditsUpdate: handleCreditsUpdate,
      selectedSlides,
      onSessionCapture: async (capturedSessionId, sessionData) => {
        sessionEventReceived = true;
        if (sessionTimeoutId) {
          clearSessionTimeout();
        }
      }
    };

    let hasFinalizedTurn = false;

    const finalizeCurrentTurn = async (reason, extra = {}) => {
      if (hasFinalizedTurn) {
        return;
      }
      hasFinalizedTurn = true;

      clearSessionTimeout();
      // A SUPERSEDED reader may not report the turn as over. Attaching a new
      // session deliberately aborts the previous stream, and that abort lands
      // here; clearing unconditionally knocked the composer back to the idle
      // arrow with nothing on screen until the new stream's first event
      // arrived, which read as a dead turn. A caller that passes no controller
      // keeps the previous unconditional behaviour.
      //
      // Superseded means a DIFFERENT controller now holds the ref. A reader
      // whose stream is simply gone does not count: the ref is reset to a null
      // controller when a stream closes, and again when an SDK process exit
      // resets the session mid-turn, and in both cases this reader is the last
      // word on the turn. Requiring positive ownership there left the composer
      // stuck on Stop for the rest of the session with nothing able to clear
      // it, which is the same contradiction from the other side: a stop button
      // for a turn that has already ended.
      const streamOwner = sessionStreamRef.current?.controller || null;
      const supersededByNewerStream = Boolean(
        controller && streamOwner && streamOwner !== controller
      );
      if (!supersededByNewerStream) {
        context.setIsChatStreaming(false);
        // Keep the ref in lockstep: steering and compact gate on it, and
        // turns they start have no handleSend finally to clear it.
        isChatStreamingRef.current = false;
      }
      // Any unanswered ask_user_question / propose_doc_edits card is moot once the
      // turn ends (stopped, errored, or completed), so clear it so it can't dangle.
      setPendingUserQuestion(null);
      clearPendingProposeEdits(reason);

      if (!currentTurnEntry?.ephemeral) {
        captureWithDuration('ai_response', { reason });
      }

      let publicExtra = extra || {};
      let turnResolution = 'resolve';
      let turnError = null;

      if (extra && typeof extra === 'object') {
        const { _turnResolution, _turnError, ...rest } = extra;
        publicExtra = rest;
        if (_turnResolution === 'reject') {
          turnResolution = 'reject';
        }
        if (_turnError) {
          turnError = _turnError;
        }
      }

      if (mutableState) {
        const startTimeMs = mutableState.metadata?.startTime
          ? (typeof mutableState.metadata.startTime === 'number'
            ? mutableState.metadata.startTime
            : Date.parse(mutableState.metadata.startTime))
          : Date.now();
        const finalDuration = Number.isNaN(startTimeMs)
          ? mutableState.metadata?.durationSeconds || 0
          : Math.max(0, Math.floor((Date.now() - startTimeMs) / 1000));

        const startTimeISO = mutableState.metadata?.startTime || new Date().toISOString();
        const completedAtISO = new Date().toISOString();

        const hasTimer = mutableState.components.some(component => component?.type === 'timer');
        const finalComponents = hasTimer
          ? mutableState.components.map(component => {
              if (component?.type === 'timer') {
                return {
                  ...component,
                  data: {
                    ...(component.data || {}),
                    startTime: startTimeISO,
                    elapsedSeconds: finalDuration,
                    durationSeconds: finalDuration,
                    isStreaming: false,
                    completedAt: completedAtISO,
                    statusLabel: component.data?.statusLabel || 'Request processed',
                    statusHint: null
                  }
                };
              }
              return component;
            })
          : [
              {
                type: 'timer',
                data: {
                  startTime: startTimeISO,
                  elapsedSeconds: finalDuration,
                  durationSeconds: finalDuration,
                  isStreaming: false,
                  completedAt: completedAtISO,
                  statusLabel: 'Request processed',
                  statusHint: null
                },
                timestamp: completedAtISO
              },
              ...mutableState.components
            ];

        // DEBUG: Log latestCredits before creating final message
        console.log('[Credits][Finalize] mutableState.metadata.latestCredits:', mutableState.metadata?.latestCredits);

        const finalMessage = streamProcessor.createMessage({
          ...mutableState,
          components: finalComponents,
          metadata: {
            ...mutableState.metadata,
            isStreaming: false,
            startTime: startTimeISO,
            completedAt: completedAtISO,
            durationSeconds: finalDuration,
            endTime: Date.now()
          }
        });

        // DEBUG: Log credits in final message
        console.log('[Credits][Finalize] finalMessage.metadata.latestCredits:', finalMessage.metadata?.latestCredits);

        // DEBUG: Log components before saving to verify research component is included
        const researchComponents = finalMessage.components?.filter(c => c.type === 'research');
        console.log('💾 FINAL MESSAGE BEFORE SAVE:', {
          totalComponents: finalMessage.components?.length || 0,
          componentTypes: finalMessage.components?.map(c => c.type),
          researchComponentsCount: researchComponents.length,
          researchComponentData: researchComponents.map(c => ({
            hasData: !!c.data,
            hasReport: !!(c.data?.report || c.data?.fullReport),
            reportLength: (c.data?.report || c.data?.fullReport || '').length,
            dataKeys: Object.keys(c.data || {})
          }))
        });

        if (mutableState.metadata?.isCancelled) {
          finalMessage.isCancelled = true;
        }
        if (mutableState.metadata?.hasError) {
          finalMessage.hasError = true;
        }

        // Pin the live copy to the fully finalized reply so it stays on screen,
        // complete, through the handoff while the (queued, async) persist lands.
        // The handoff effect drops it once its id appears in currentMessages.
        if (!currentTurnEntry?.hidden && currentTurnEntry?.ephemeral) {
          setLiveStreamingMessage(finalMessage);
          upsertEphemeralMessage(finalMessage);
        } else if (!currentTurnEntry?.hidden) {
          setLiveStreamingMessage(finalMessage);
          await enqueueMessageUpdate(() => addSanitizedMessage(finalMessage), true);
        }
      }

      // The finalized copy is persisted (rev-stamped newer than any uploaded
      // streaming copy), so live authorship can end: mirror merges take over.
      if (!currentTurnEntry?.ephemeral) {
        smartCache.releaseAllLiveMessages();
      }
      activeTurnChatIdRef.current = null;

      streamingStateRef.current = null;

      if (currentTurnEntry) {
        try {
          if (turnResolution === 'reject') {
            const rejectionError = turnError || new Error(reason || 'stream_cancelled');
            currentTurnEntry.reject(rejectionError);
          } else {
            currentTurnEntry.resolve({
              type: reason,
              sessionId,
              ...publicExtra
            });
          }
        } catch (resolveError) {
          console.warn('⚠️ Failed to resolve pending turn:', resolveError);
        }
      }

      activeTurnRef.current = null;
      currentTurnEntry = null;
      state = null;
      mutableState = null;
      aiMessageId = null;
      sessionEventReceived = false;
    };

    try {
      while (true) {
        const { value, done } = await reader.read();
        if (done) {
          console.log('📡 SSE stream ended (done=true)');
          if (liveDataSessionRef.current?.active === true) {
            terminatePrivateLiveDataSession({
              operationId: liveDataSessionRef.current.operationId,
              stage: 'failed',
              code: 'private_stream_closed',
              message: 'The private live data connection closed before completion',
              abortLocal: true,
            });
          }
          // A stream that closes UNDER an active turn is a transport loss,
          // not a completion: mark the bubble and reject the pending turn so
          // the partial reply is never recorded as a clean success (and the
          // sender sees an error + retry instead of silence).
          if (currentTurnEntry && mutableState) {
            mutableState = {
              ...mutableState,
              metadata: {
                ...mutableState.metadata,
                hasError: true,
                statusLabel: 'Connection lost',
                statusHint: 'The stream closed before the turn finished.'
              }
            };
          }
          await finalizeCurrentTurn('stream_closed', {
            reason: 'reader_done',
            ...(currentTurnEntry ? {
              _turnResolution: 'reject',
              _turnError: Object.assign(new Error('Connection lost before the turn finished'), { code: 'STREAM_CLOSED' })
            } : {})
          });
          break;
        }

        const chunk = decoder.decode(value, { stream: true });
        console.log('📡 SSE chunk received:', { chunkLength: chunk.length });
        buffer += chunk;

        const lines = buffer.split('\n\n');
        buffer = lines.pop();
        console.log('📡 Split into', lines.length, 'complete lines, buffer remainder:', buffer.length, 'chars');

        for (const line of lines) {
          if (!line.startsWith('data:')) {
            console.log('  ↳ Skipping (no data: prefix)');
            continue;
          }

          const jsonStr = line.replace(/^data:\s*/, '');
          if (jsonStr === '[DONE]') {
            console.log('  ↳ Stream complete signal received');
            await finalizeCurrentTurn('done');
            streamingStateRef.current = null;
            return { type: 'stream_complete' };
          }

          let event;
          try {
            event = parseSSEEvent(jsonStr);
            console.log('  ↳ Parsed event type:', event?.type);
          } catch (parseError) {
            console.error('  ↳ Error parsing SSE event:', {
              name: parseError?.name || 'Error',
              inputLength: jsonStr.length,
            });
            continue;
          }

          if (!event) {
            console.log('  ↳ parseSSEEvent returned null');
            continue;
          }

          if (event.private === true) {
            const pendingPrivateEntry = currentTurnEntry ||
              activeTurnRef.current ||
              (
                !isTurnReadyForStreamEvents(
                  pendingTurnQueueRef.current[0]
                )
                  ? null
                  : pendingTurnQueueRef.current[0]
              ) ||
              null;
            const privateClassification = classifyPrivateStreamEvent(event, {
              currentDocumentId: documentPrivacyRef.current?.documentId || null,
              hasPendingTurn: Boolean(pendingPrivateEntry),
              activePrivateTurn:
                currentTurnEntry?.ephemeral === true ||
                activeTurnRef.current?.ephemeral === true,
              activeLiveDataOperationId:
                liveDataSessionRef.current?.active === true
                  ? liveDataSessionRef.current.operationId
                  : null,
              registeredPrivateCall:
                Boolean(event.callId) &&
                privateOfficeCancellationRegistryRef.current.has(event.callId),
            });
            if (!privateClassification.accepted) {
              console.warn('[hooks] Ignored unowned private stream event', {
                eventType: event.type || null,
                reason: privateClassification.reason,
              });
              if (event.type === 'tool_call_request' && event.callId) {
                await postToolResult(
                  event.callId,
                  null,
                  'Private Office operation failed'
                );
              }
              continue;
            }
            if (privateClassification.activate) {
              privateSessionActiveRef.current = true;
              const privateDocumentId =
                documentPrivacyRef.current.documentId.toLowerCase();
              beginEphemeralScope({
                documentId: privateDocumentId,
                chatId: pendingPrivateEntry?.chatId ||
                  currentChatIdRef.current ||
                  `private-${privateDocumentId}`,
                sessionId: sessionId ||
                  sessionStreamRef.current.sessionId ||
                  null,
              });
              const wasPersistent =
                pendingPrivateEntry?.ephemeral !== true;
              if (
                upgradeTurnEntryToEphemeral(pendingPrivateEntry) &&
                wasPersistent
              ) {
                const pendingMessageId =
                  pendingPrivateEntry.aiMessageId || aiMessageId;
                if (pendingMessageId) {
                  smartCache.releaseLiveMessage(pendingMessageId);
                }
                if (state && !pendingPrivateEntry.hidden) {
                  const privateMessage =
                    streamProcessor.createMessage(mutableState || state);
                  privateMessage.isStreaming = true;
                  upsertEphemeralMessage(privateMessage);
                }
              }
            }
          }

          if (event.type === 'connector_worker_progress') {
            event = normalizeConnectorWorkerProgress(event);
            if (!event) {
              console.warn('[hooks] Ignored invalid connector worker progress event');
              continue;
            }
          }

          if (event.type === 'connection' && event.status === 'established') {
            onConnected?.(event);
            continue;
          }

          if (
            event.type === 'finance_live_data_progress' ||
            event.type === 'finance_live_data_completed' ||
            event.type === 'finance_live_data_failed'
          ) {
            const liveEvent = normalizeFinanceLiveDataEvent(event);
            const activeLiveData = liveDataSessionRef.current;
            if (
              !liveEvent ||
              !activeLiveData ||
              liveEvent.operationId !== activeLiveData.operationId
            ) {
              console.warn('[hooks] Ignored invalid or unowned live data event');
              continue;
            }
            if (liveEvent.active) {
              updateLiveDataSession((current) => ({
                ...current,
                ...liveEvent,
              }));
            } else {
              terminatePrivateLiveDataSession({
                operationId: liveEvent.operationId,
                stage: liveEvent.stage,
                code: liveEvent.code || null,
                message: liveEvent.message,
              });
              const controller = financeOfficeControllerRef.current;
              if (liveEvent.type === 'finance_live_data_completed' && controller) {
                controller.getDocumentPrivacyState()
                  .then((marker) => {
                    setDocumentPrivacy(marker);
                    documentPrivacyRef.current = marker;
                  })
                  .catch(() => {});
              }
            }
            continue;
          }

          // A stopped originating turn can cancel a connected Office request
          // while this pane is idle or while the request waits behind another
          // Office operation. Record the tombstone before every turn-state
          // gate. Queued work will skip dispatch; already-running Office.js is
          // allowed to settle, but its late result is suppressed centrally.
          if (event.type === 'tool_call_cancel' && event.callId) {
            if (
              event.private === true &&
              (
                !PRIVATE_OPERATION_ID_RE.test(event.documentId || '') ||
                event.documentId.toLowerCase() !==
                  documentPrivacyRef.current?.documentId?.toLowerCase()
              )
            ) {
              console.warn('[hooks] Ignored private Office cancellation for another document');
              continue;
            }
            cancelledRemoteToolCallIdsRef.current.add(event.callId);
            privateOfficeCancellationRegistryRef.current.cancel(event.callId, event.reason || 'server_cancelled');
            setPendingUserQuestion((pending) => pending?.callId === event.callId ? null : pending);
            setPendingProposeEdits((pending) => {
              if (pending?.callId !== event.callId) return pending;
              pending.cancel?.(event.reason || 'server_cancelled');
              return null;
            });
            setTimeout(() => {
              cancelledRemoteToolCallIdsRef.current.delete(event.callId);
            }, 10 * 60 * 1000);
            continue;
          }

          // DEFENSIVE PRE-CHECK: if handleStopRequest nulled activeTurnRef out
          // from under us, our closure vars (state, mutableState, aiMessageId,
          // currentTurnEntry) are stale and still point at the stopped turn's
          // bubble. Any subsequent event — even a leftover from the aborted
          // turn — will merge into that bubble unless we reset here first.
          //
          // This is event-agnostic: it runs regardless of whether the backend
          // emitted a turn_interrupted status event. If state is set but the
          // outside world (handleStopRequest) cleared the active turn ref,
          // force a clean slate so the next event triggers initializeTurnState()
          // and a fresh AI message bubble.
          if (state && !activeTurnRef.current) {
            console.log('🛑 Active turn cleared externally — resetting closure state');
            clearSessionTimeout();
            if (!currentTurnEntry?.ephemeral) {
              smartCache.releaseAllLiveMessages();
            }
            activeTurnChatIdRef.current = null;
            state = null;
            mutableState = null;
            aiMessageId = null;
            currentTurnEntry = null;
            hasFinalizedTurn = false;
            streamingStateRef.current = null;
          }

          // Handle credits_update events even after turn is finalized
          // These are post-turn billing events that update the already-finalized message
          if (!state && event.type === 'status' && event.stage === 'credits_update') {
            console.log('💰 [Credits] Received post-turn credits_update, updating finalized message');
            if (typeof context.onCreditsUpdate === 'function') {
              context.onCreditsUpdate(event);
            }
            continue;
          }

          // Handle turn_interrupted — sent by runTurn()'s catch block when a
          // user stop aborts the current turn. We must reset the closure
          // state here; otherwise the next user message's events would feed
          // into the stale turn-1 bubble instead of creating a fresh one.
          //
          // handleStopRequest() has already written an updated bubble with
          // isCancelled=true for immediate UI feedback, so we do NOT call
          // finalizeCurrentTurn() (which would write another final message
          // and risk overwriting that cancellation). Just reset and bail.
          if (event.type === 'status' && event.stage === 'turn_interrupted') {
            console.log('🛑 Received turn_interrupted — resetting stream state for next turn');
            clearSessionTimeout();

            // A locally-stopped turn already cleared this in handleStopRequest
            // (idempotent here), but an injected conductor turn has no local
            // stop path, so clear the busy composer for that case too.
            setIsChatStreaming(false);
            isChatStreamingRef.current = false;

            // The interrupted turn's pendingToolCalls map is dead server-side,
            // so any question / review card it spawned now points at a callId
            // that can never be resolved. Leaving the card up invites answers
            // that silently 404. Clear them with the turn.
            setPendingUserQuestion(null);
            clearPendingProposeEdits('turn_interrupted');

            // If handleStopRequest hasn't already cleared it, reject the
            // pending turn so handleSend's awaiter unblocks.
            if (currentTurnEntry) {
              try {
                currentTurnEntry.reject(new Error('Turn interrupted by user'));
              } catch (_) { /* ignore if already settled */ }
            }

            // Reset every closure variable so the next event triggers
            // initializeTurnState() for a fresh turn with a new aiMessageId.
            if (!currentTurnEntry?.ephemeral) {
              smartCache.releaseAllLiveMessages();
            }
            activeTurnChatIdRef.current = null;
            activeTurnRef.current = null;
            currentTurnEntry = null;
            state = null;
            mutableState = null;
            aiMessageId = null;
            sessionEventReceived = false;
            hasFinalizedTurn = false;
            streamingStateRef.current = null;
            continue;
          }

          // Conductor chrome events: a peer agent is interacting with this
          // session. conductor_message_received announces an incoming injected
          // turn, so flag it (initializeTurnState then synthesizes a turn entry
          // for the turn that follows). These are not themselves turn events, so
          // they must not open a bubble; handle them before the !state gate.
          if (event.type === 'conductor_message_received') {
            let conductorPrivacy = documentPrivacyRef.current;
            if (powerpointBridge && financeOfficeControllerRef.current) {
              try {
                conductorPrivacy =
                  await financeOfficeControllerRef.current.getDocumentPrivacyState();
                setDocumentPrivacy(conductorPrivacy);
                documentPrivacyRef.current = conductorPrivacy;
              } catch {
                conductorPrivacy = null;
              }
            }
            if (shouldSuppressConductorTurn({
              documentMarker: conductorPrivacy,
              privateSessionActive: privateSessionActiveRef.current,
              officePresent: Boolean(powerpointBridge),
              financeConnectorsEnabled: FINANCE_CONNECTORS_UI_ENABLED,
            })) {
              pendingConductorTurns.length = 0;
              setAwaitingPeer(null);
              console.warn(
                '[hooks] Suppressed Conductor turn for a private Office document'
              );
              continue;
            }
            pendingConductorTurns.push(event);
            // ONE CHAT ACROSS APPS: remember the sender's chat so the
            // injected turn renders THERE (chats are user-scoped and shared
            // across panes), not in this pane's own connector chat. Applied
            // at synthesis time (initializeTurnState), when any in-flight
            // local turn has finished and the override cannot misroute it.
            // No boundary pull needed: the live chat mirror already feeds
            // this pane the shared chat's messages as they land.
            // The peer we were waiting on has come back (its reply IS this
            // incoming message); release the waiting UI and close its inline
            // status line in the transcript.
            console.log('[hooks] Conductor: incoming request from peer', event.from || '');
            continue;
          }
          if (event.type === 'conductor_message_sent') {
            // A reply hop back to the requesting app is not a user-facing
            // dispatch: no waiting UI, no status line (the requester's own
            // line flips to "finished" when the reply lands there).
            if (event.reply) {
              continue;
            }
            // This pane dispatched work to a peer; hold the ongoing-work UI
            // until the peer replies (or drops), and drop an inline status
            // line into the transcript so the handoff is visible in the flow
            // (mirrored identically into every pane viewing this chat).
            setAwaitingPeer('Working');
            continue;
          }
          // A peer pane started working on a chat: auto-follow this pane's
          // VIEW onto it so every open pane shows the one shared transcript.
          // Pure view change. The guard is ACTIVE LOCAL ENGAGEMENT, not the
          // viewed chat's history: an idle pane parked on any older chat must
          // still follow, but a pane with a local turn running, a draft in
          // the composer, or a recent deliberate chat action (manual switch
          // or send) is never yanked away.
          if (event.type === 'conductor_chat_activity') {
            const activityChatId = event.chatId || null;
            const AUTO_FOLLOW_INTENT_GRACE_MS = 45000;
            const userEngaged =
              isChatStreamingRef.current ||
              (composerDraftRef.current || '').trim() !== '' ||
              (Date.now() - manualChatActionAtRef.current) < AUTO_FOLLOW_INTENT_GRACE_MS;
            if (
              activityChatId &&
              activityChatId !== currentChatIdRef.current &&
              !userEngaged
            ) {
              try {
                markProgrammaticSwitch(activityChatId);
                if (typeof switchToChat === 'function') switchToChat(activityChatId);
              } catch (followErr) {
                console.warn('[hooks] Conductor: auto-follow failed:', followErr?.message);
              }
            }
            continue;
          }
          // Connector presence events drive the header status indicator.
          // snapshot: sent once on attach = this pane is registered + who else
          // is here. online/offline: peers arriving/leaving afterwards.
          // superseded: a newer pane of the same app took over this pane's spot.
          if (event.type === 'conductor_peers_snapshot') {
            setConductorOnline(true);
            setConductorPeers(Array.isArray(event.peers) ? event.peers : []);
            continue;
          }
          if (event.type === 'conductor_peer_online') {
            setConductorPeers((prev) => [
              ...prev.filter((p) => p.agentId !== event.agentId && (!event.surface || p.surface !== event.surface)),
              { agentId: event.agentId, surface: event.surface, label: event.label },
            ]);
            continue;
          }
          if (event.type === 'conductor_peer_offline') {
            setConductorPeers((prev) => prev.filter((p) => p.agentId !== event.agentId));
            // A peer we were waiting on cannot reply anymore; say so in the
            // transcript instead of leaving a "working..." line dangling.
            if (event.agentId) {
              writeConductorStatusRef.current?.(event.agentId, null, 'disconnected');
            }
            setAwaitingPeer(null);
            continue;
          }
          if (event.type === 'conductor_superseded') {
            setConductorOnline(false);
            setConductorPeers([]);
            setAwaitingPeer(null);
            // Replies will land on the newer pane of this surface, not here.
            finalizePeerStatusesRef.current?.('ended');
            continue;
          }
          if (
            event.type === 'conductor_waiting' ||
            event.type === 'conductor_artifact_shared'
          ) {
            continue;
          }

          const hasEventReadyTurn =
            pendingTurnQueueRef.current.some(
              isTurnReadyForStreamEvents
            );

          // Office tool execution is transport work, not chat-turn rendering.
          // A connected peer can proxy a typed tool call into this pane while
          // its SSE stream is idle and no local model turn exists. Execute the
          // request before the idle-stream guard so Excel, PowerPoint, or Word
          // can act as a remote Office.js executor without synthesizing a hidden
          // assistant turn. Local-turn requests use the same path; their visible
          // tool_call event is rendered separately in the originating stream.
          if (event.type === 'tool_call_request') {
            if (shouldInitializeTurnBeforeToolDispatch(event, {
              hasTurnState: Boolean(state || activeTurnRef.current),
              hasEventReadyTurn,
              hasPendingConductorTurn:
                pendingConductorTurns.length > 0,
            })) {
              hasFinalizedTurn = false;
              const prepared = await initializeTurnState();
              if (!prepared) {
                await postToolResult(
                  event.callId,
                  null,
                  'Office tool execution could not be attached to its turn'
                );
                continue;
              }
            }
            void enqueueToolCallRequest(event.callId, event.toolName, event.args || {}, {
              remote: event.remote === true,
              private: event.private === true,
              operationId: event.operationId || null,
              documentId: event.documentId || null,
              financeOfficeProtocol: event.financeOfficeProtocol === true,
              originTurnId: event.originTurnId || null,
              originApp: event.originApp || null,
              lockerOfficeOperations: event.lockerOfficeOperations,
            })
              .catch(async (toolRequestError) => {
                const privateRequest = event.private === true;
                console.error('[hooks] Tool call request failed:', privateRequest
                  ? { callId: event.callId, toolName: event.toolName, private: true, errorType: toolRequestError?.name || 'Error' }
                  : toolRequestError);
                if (toolRequestError?.suppressToolResult === true) {
                  return;
                }
                if (
                  toolRequestError?.preStartResultReconciled === true
                ) {
                  return;
                }
                const deliverFailureResult = async () => {
                  try {
                    const posted = await postToolResult(
                      event.callId,
                      null,
                      privateRequest
                        ? 'Private Office operation failed'
                        : (toolRequestError?.message || 'Office tool execution failed')
                    );
                    if (posted) {
                      if (
                        toolRequestError?.preStartAbandoned === true
                      ) {
                        toolRequestError.preStartResultReconciled = true;
                      }
                      void postToolQuiesced(event.callId);
                    }
                  } catch (resultError) {
                    console.warn(
                      '[hooks] Office tool failure result remains unresolved:',
                      {
                        callId: event.callId,
                        code:
                          resultError?.code
                          || resultError?.name
                          || 'office_result_unresolved',
                      }
                    );
                  }
                };
                if (toolRequestError?.preStartAbandoned === true) {
                  await runOfficeResultSingleFlight(
                    preStartOfficeResultPostsRef.current,
                    event.callId,
                    deliverFailureResult
                  );
                  return;
                }
                await deliverFailureResult();
              })
              .finally(() => {
                cancelledRemoteToolCallIdsRef.current.delete(event.callId);
                if (
                  event.private === true &&
                  !privateOfficeCancellationRegistryRef.current.hasAny() &&
                  liveDataSessionRef.current?.active !== true &&
                  activeTurnRef.current?.ephemeral !== true
                ) {
                  privateSessionActiveRef.current = false;
                }
              });
            continue;
          }
          // Steering ack: the backend accepted a mid-turn message into the
          // running turn. NOT skipped: it flows into streamProcessor, which
          // appends a steered_user_message component to the live bubble so
          // the user's text is visible inside the stream (repository writes
          // made mid-turn are not reliably reflected while rendering).

          // IDLE-STREAM GUARD (load-bearing): on an eagerly-attached stream
          // (connector presence, no message sent yet) the backend emits chrome
          // like the "Session ready" status with NO local turn pending. Passing
          // that to initializeTurnState would PARK the reader on an empty turn
          // queue and wedge the next real turn (the "Thinking 0s" regression).
          // A legitimate turn ALWAYS queues its entry before its events arrive
          // (sendMessageToAI calls createPendingTurn before POST; conductor
          // turns set pendingConductorTurn above), so an empty queue here can
          // only ever be idle chrome: skip it, never park.
          if (
            !state &&
            pendingConductorTurns.length === 0 &&
            !hasEventReadyTurn &&
            !activeTurnRef.current
          ) {
            continue;
          }

          if (!state) {
            // Reset the turn finalization guard for the new turn
            // This allows finalizeCurrentTurn to run for each turn in persistent sessions
            hasFinalizedTurn = false;

            const prepared = await initializeTurnState();
            if (!prepared) {
              console.warn('⚠️ Received event without pending turn; skipping', {
                eventType: event?.type || null,
              });
              continue;
            }
          }

          const prevState = mutableState;
          mutableState = await streamProcessor.processEvent(event, mutableState, context);

          streamingStateRef.current = {
            state: mutableState,
            updateState: setStreamingState
          };

          if (mutableState.aiMessageId) {
            aiMessageId = mutableState.aiMessageId;
          }

          if (streamProcessor.shouldSaveMessage(mutableState, prevState)) {
            const message = streamProcessor.createMessage(mutableState);
            message.isStreaming = !streamProcessor.isStreamComplete(mutableState);
            if (!currentTurnEntry?.hidden && currentTurnEntry?.ephemeral) {
              upsertEphemeralMessage(message);
            } else if (!currentTurnEntry?.hidden) {
              enqueueMessageUpdate(() => addSanitizedMessage(message));
            }
          }

          if (event.type === 'status' && event.stage === 'turn_complete') {
            await finalizeCurrentTurn('turn_complete', { event });
            continue;
          }

          if (event.type === 'complete') {
            await finalizeCurrentTurn('session_complete', { response: event.response, event });
            await flushMessageQueue();
            return event.response || { type: 'session_complete' };
          }

          if (event.type === 'error') {
            const streamError = new Error(event.error?.message || 'Streaming error');
            streamError.code = event.error?.code;

            // Handle SDK process exit errors - reset session state to allow fresh start
            const isProcessExitError = event.error?.shouldResetSession ||
              event.error?.code === 'SDK_PROCESS_EXIT' ||
              event.error?.message?.includes('process exited with code');

            if (isProcessExitError) {
              console.warn('⚠️ SDK process exit detected - resetting session state for recovery', {
                code: event.error?.code,
                shouldResetSession: event.error?.shouldResetSession,
                message: event.error?.message
              });

              // Clear session state to force new session on next request
              setSessionId(null);
              sessionDocumentBindingRef.current = {
                sessionId: null,
                documentId: null,
              };

              // Reset session stream ref
              sessionStreamRef.current = {
                sessionId: null,
                controller: null,
                promise: null,
                startPromise: null,
                isActive: false,
                streamUrl: null
              };

              // CRITICAL: Clear sessionId from chat metadata to prevent reload on navigation
              // This ensures when user navigates away and back, they won't reload the stale session
              if (
                !currentTurnEntry?.ephemeral &&
                currentPresentation?.id &&
                currentChatIdRef.current &&
                repository
              ) {
                try {
                  repository.updateChat(
                    currentPresentation.id,
                    currentChatIdRef.current,
                    {
                      sessionId: null,
                      sessionClearedAt: new Date().toISOString(),
                      sessionClearReason: 'sdk_process_exit'
                    }
                  ).catch(err => console.warn('⚠️ Failed to clear stale sessionId from chat:', err));
                  console.log('🧹 Cleared stale sessionId from chat metadata');
                } catch (clearErr) {
                  console.warn('⚠️ Failed to clear stale sessionId from chat:', clearErr);
                }
              }
            }

            await finalizeCurrentTurn('error', { error: event.error });
            rejectAllPendingTurns(streamError);
            throw streamError;
          }
        }
      }

      streamingStateRef.current = null;
      return { type: 'stream_complete' };
    } catch (error) {
      streamingStateRef.current = null;

      if (error.name === 'AbortError' || error.message === 'AbortError') {
        console.log('✅ Stream processing cancelled');

        if (mutableState) {
          const { componentUpdaters } = await import('./streamProcessor');
          const completedComponents = componentUpdaters.settleAllComponents(mutableState.components || [], false);
          const cancellationTimestamp = new Date().toISOString();

          // Calculate duration from startTime with cap
          const MAX_DURATION_SECONDS = 10 * 60; // 10 minutes cap
          let durationSeconds = 0;
          if (mutableState.metadata?.startTime) {
            const startMs = typeof mutableState.metadata.startTime === 'number'
              ? mutableState.metadata.startTime
              : Date.parse(mutableState.metadata.startTime);
            if (!Number.isNaN(startMs)) {
              durationSeconds = Math.min(
                Math.max(0, Math.floor((Date.now() - startMs) / 1000)),
                MAX_DURATION_SECONDS
              );
            }
          }

          // Update timer components with final duration
          const finalComponents = completedComponents.map(comp => {
            if (comp?.type === 'timer') {
              return {
                ...comp,
                data: {
                  ...comp.data,
                  isStreaming: false,
                  isComplete: true,
                  isActive: false,
                  durationSeconds,
                  elapsedSeconds: durationSeconds,
                  completedAt: cancellationTimestamp
                }
              };
            }
            return comp;
          });

          mutableState = {
            ...mutableState,
            components: [
              ...finalComponents,
              {
                type: 'text',
                content: 'Request cancelled by user.',
                timestamp: cancellationTimestamp
              }
            ],
            metadata: {
              ...mutableState.metadata,
              isStreaming: false,
              isComplete: true,
              isCancelled: true,
              statusLabel: 'Cancelled',
              statusHint: 'Stopped by user',
              completedAt: cancellationTimestamp,
              durationSeconds,
              endTime: Date.now()
            }
          };
        }

        await finalizeCurrentTurn('cancelled', {
          cancelled: true,
          reason: 'user_abort',
          _turnResolution: 'reject',
          _turnError: error
        });

        return { type: 'cancelled' };
      }

      if (mutableState) {
        try {
          const { componentUpdaters } = await import('./streamProcessor');
          const completedComponents = componentUpdaters.settleAllComponents(mutableState.components || [], false);
          const errorTimestamp = new Date().toISOString();

          // Calculate duration from startTime with cap
          const MAX_DURATION_SECONDS = 10 * 60; // 10 minutes cap
          let durationSeconds = 0;
          if (mutableState.metadata?.startTime) {
            const startMs = typeof mutableState.metadata.startTime === 'number'
              ? mutableState.metadata.startTime
              : Date.parse(mutableState.metadata.startTime);
            if (!Number.isNaN(startMs)) {
              durationSeconds = Math.min(
                Math.max(0, Math.floor((Date.now() - startMs) / 1000)),
                MAX_DURATION_SECONDS
              );
            }
          }

          // Update timer components with final duration
          const finalComponents = completedComponents.map(comp => {
            if (comp?.type === 'timer') {
              return {
                ...comp,
                data: {
                  ...comp.data,
                  isStreaming: false,
                  isComplete: true,
                  isActive: false,
                  durationSeconds,
                  elapsedSeconds: durationSeconds,
                  completedAt: errorTimestamp
                }
              };
            }
            return comp;
          });

          const privateErrorMessage = currentTurnEntry?.ephemeral
            ? 'The private request could not be completed.'
            : (error.message || 'Streaming error occurred.');
          mutableState = {
            ...mutableState,
            components: [
              ...finalComponents,
              {
                type: 'text',
                content: privateErrorMessage,
                timestamp: errorTimestamp
              }
            ],
            metadata: {
              ...mutableState.metadata,
              isStreaming: false,
              isComplete: true,
              hasError: true,
              statusLabel: 'Error',
              statusHint: privateErrorMessage,
              completedAt: errorTimestamp,
              durationSeconds,
              endTime: Date.now()
            }
          };
        } catch (metadataError) {
          console.warn('⚠️ Failed to annotate mutable state on error:', metadataError);
        }
      }

      if (!hasFinalizedTurn) {
        await finalizeCurrentTurn('error', {
          error: {
            message: error.message,
            code: error.code
          },
          _turnResolution: 'reject',
          _turnError: error
        });
      }

      rejectAllPendingTurns(error);
      if (currentTurnEntry?.ephemeral || privateSessionActiveRef.current) {
        console.error('❌ Private stream processing failed', {
          code: error?.code || error?.name || 'private_stream_failed',
        });
      } else {
        console.error('❌ Stream processing error:', error);
      }
      if (liveDataSessionRef.current?.active === true) {
        terminatePrivateLiveDataSession({
          operationId: liveDataSessionRef.current.operationId,
          stage: 'failed',
          code: 'private_stream_failed',
          message: 'The private live data connection failed before completion',
          abortLocal: true,
        });
      }
      throw error;
    } finally {
      clearSessionTimeout();

      try {
        await reader.cancel();
        console.log('✅ Stream reader closed');
      } catch (cancelError) {
        // ignore reader cancel errors
      }

      await flushMessageQueue();
    }
  }, [
    addSanitizedMessage,
    awaitNextTurnEntry,
    beginEphemeralScope,
    clearPendingProposeEdits,
    currentPresentation?.id,
    currentUser,
    confirmDurableOfficeOperationComplete,
    enqueueMessageUpdate,
    flushMessageQueue,
    getLockerOfficeJournal,
    handleDraftEnvelopeEvent,
    handleLoopTelemetryUpdate,
    rejectAllPendingTurns,
    repository,
    powerpointBridge,
    selectedSlides,
    setDraftEnvelopeLog,
    setIsChatStreaming,
    setOfficeRecoveryEntries,
    terminatePrivateLiveDataSession,
    trackAIChat,
    transitionDurableOfficeOperation,
    updateLiveDataSession,
    upsertEphemeralMessage,
  ]);



  const ensureSessionStream = useCallback(async ({ sessionId: targetSessionId, streamUrl, authToken, forceReconnect = false }) => {
    if (!targetSessionId || !streamUrl) {
      return;
    }

    const active = sessionStreamRef.current;

    if (!forceReconnect && active.sessionId === targetSessionId) {
      if (active.startPromise) {
        await active.startPromise;
      }
      if (active.isActive) {
        return;
      }
    }

    const freshDocumentPrivacy = FINANCE_CONNECTORS_UI_ENABLED
      ? await readFreshDocumentPrivacy()
      : null;

    if (active.controller) {
      try {
        active.controller.abort();
      } catch (abortError) {
        console.warn('⚠️ Failed to abort previous stream controller:', abortError);
      }
    }

    const controller = new AbortController();

    const headers = buildSessionStreamHeaders({
      authToken,
      documentPrivacy: freshDocumentPrivacy,
    });

  const startPromise = (async () => {
    try {
      // Timeout the fetch in case the proxy buffers the SSE response
      const fetchTimeout = setTimeout(() => controller.abort(), 15000);
      let response;
      try {
        response = await fetch(streamUrl, {
          method: 'GET',
          headers,
          signal: controller.signal
        });
      } finally {
        clearTimeout(fetchTimeout);
      }
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw createStreamAttachError(response.status, payload);
      }

      let resolveReady;
      let rejectReady;
      const readyPromise = new Promise((resolve, reject) => {
        resolveReady = resolve;
        rejectReady = reject;
      });
      const streamPromise = handleStreamResponse(response, {
        sessionId: targetSessionId,
        onConnected: resolveReady,
        controller,
      });
      sessionStreamRef.current.promise = streamPromise;

      streamPromise
        .catch((error) => {
          const msg = error?.message || String(error);
          const isAbort = error?.name === 'AbortError' || msg === 'presentation_switch' || msg.includes('aborted');
          if (!isAbort) {
            if (privateSessionActiveRef.current) {
              console.error('❌ Private persistent stream failed', {
                code: error?.code || error?.name || 'private_stream_failed',
              });
            } else {
              console.error('❌ Persistent stream error:', error);
            }
          }
          rejectAllPendingTurns(error);
        })
        .finally(() => {
          // Generation guard: only THIS attach's cleanup may clear the ref.
          // Matching on sessionId alone let an obsolete attach's teardown
          // erase a newer live stream's registration, orphaning its socket
          // (the next attach then destructively replaced the healthy one).
          const current = sessionStreamRef.current;
          if (current.controller === controller) {
            if (liveDataSessionRef.current?.active === true) {
              terminatePrivateLiveDataSession({
                operationId: liveDataSessionRef.current.operationId,
                stage: 'failed',
                code: 'private_stream_closed',
                message: 'The private live data connection closed before completion',
                abortLocal: true,
              });
            }
            privateOfficeCancellationRegistryRef.current.cancelAll('stream_closed');
            sessionStreamRef.current = {
              sessionId: null,
              controller: null,
              promise: null,
              startPromise: null,
              isActive: false,
              streamUrl: null
            };
            // The stream this pane was registered through is gone: reflect it
            // in the connector indicator so the user can click to reconnect
            // (and the eager effect auto-retries once on this flip).
            setConductorOnline(false);
            setConductorPeers([]);
            setAwaitingPeer(null);
          }
        });

      const readyTimeout = setTimeout(() => {
        rejectReady(new Error('Session stream did not reach the connected state within 15 seconds'));
      }, 15000);
      try {
        await Promise.race([
          readyPromise,
          streamPromise.then(() => {
            throw new Error('Session stream closed before it became ready');
          })
        ]);
      } finally {
        clearTimeout(readyTimeout);
      }
      sessionDocumentBindingRef.current = {
        sessionId: targetSessionId,
        documentId: freshDocumentPrivacy?.documentId || null,
      };
      return true;
    } catch (startError) {
      // Same generation guard on the startup-failure path: an aborted stale
      // attach must not clear the ref a newer attach has already claimed.
      if (sessionStreamRef.current.controller === controller) {
        sessionStreamRef.current = {
          sessionId: null,
          controller: null,
          promise: null,
          startPromise: null,
          isActive: false,
          streamUrl: null
        };
      }
      throw startError;
    }
  })();

    sessionStreamRef.current = {
      sessionId: targetSessionId,
      controller,
      promise: null,
      startPromise,
      isActive: true,
      streamUrl
    };

    await startPromise;
  }, [
    handleStreamResponse,
    readFreshDocumentPrivacy,
    rejectAllPendingTurns,
    setAwaitingPeer,
    terminatePrivateLiveDataSession,
  ]);

  /**
   * Connect this pane to the cross-app connector WITHOUT sending a message.
   * Creates (or adopts) the chat's backend session and attaches its SSE stream;
   * the backend registers the pane into the user's Conductor room on attach.
   * Runs automatically on pane load (effect below) and again when the user
   * clicks the connector icon after a drop. Deduped via conductorConnectRef so
   * a mount effect, a click, and a concurrent send can never create parallel
   * sessions; sendMessageToAI also awaits the in-flight promise and adopts the
   * same session, and the backend's newest-wins registry self-heals any
   * duplicate that slips through.
   * @returns {Promise<string|null>} the connected sessionId (null if not signed in)
   */
  /**
   * Pull the pane's Conductor registration state over REST and reflect it in
   * the header indicator. Called after every stream attach and on every
   * connector-icon click, so the indicator's truth never depends on catching
   * the one SSE snapshot event emitted mid-attach.
   * @returns {Promise<boolean>} true if the pane is registered
   */
  const syncConductorPeers = useCallback(async (sid, authToken) => {
    try {
      const streamingBaseUrl = process.env.REACT_APP_STREAMING_SERVICE_URL || 'https://claude-streaming-service-103376361923.us-central1.run.app';
      const resp = await fetch(`${streamingBaseUrl}/sessions/${sid}/conductor/peers`, {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
      });
      if (!resp.ok) return false;
      const data = await resp.json();
      if (data?.online) {
        setConductorOnline(true);
        setConductorPeers(Array.isArray(data.peers) ? data.peers : []);
        console.log('🔗 Connector: pane registered', { agentId: data.agentId, peers: (data.peers || []).length });
        return true;
      }
      return false;
    } catch (syncErr) {
      console.warn('⚠️ Connector: peers sync failed:', syncErr?.message);
      return false;
    }
  }, []);

  const connectConductor = useCallback(async () => {
    if (!currentUser) return null;
    await flushSettledLockerOfficeOperations();
    let conductorMarker = null;
    if (FINANCE_CONNECTORS_UI_ENABLED) {
      try {
        conductorMarker = await readFreshDocumentPrivacy();
      } catch (error) {
        setConductorOnline(false);
        setConductorPeers([]);
        throw error;
      }
      if (!canConnectConductor(conductorMarker)) {
        setConductorOnline(false);
        setConductorPeers([]);
        setAwaitingPeer(null);
        return null;
      }
    }
    const activeSessionId = sessionStreamRef.current.sessionId;
    const activeSessionMustRotate =
      sessionStreamRef.current.isActive &&
      financeSessionRequiresRotation(
        activeSessionId,
        sessionDocumentBindingRef.current,
        conductorMarker
      );
    if (activeSessionMustRotate) {
      sessionStreamRef.current.controller?.abort();
      setSessionId(null);
      sessionDocumentBindingRef.current = {
        sessionId: null,
        documentId: null,
      };
      setConductorOnline(false);
      setConductorPeers([]);
    } else if (
      sessionStreamRef.current.isActive &&
      activeSessionId
    ) {
      // Stream already up (pane is registered server-side), but the indicator
      // may have missed the attach-time snapshot. A click must repair the
      // header instead of no-opping, so re-sync over REST.
      try {
        const authToken = await currentUser.getIdToken();
        await syncConductorPeers(activeSessionId, authToken);
      } catch { /* indicator stays as-is; next click retries */ }
      return activeSessionId;
    }
    if (conductorConnectRef.current) return conductorConnectRef.current;

    const run = (async () => {
      const streamingBaseUrl = process.env.REACT_APP_STREAMING_SERVICE_URL || 'https://claude-streaming-service-103376361923.us-central1.run.app';
      const authToken = await currentUser.getIdToken();

      // The backend requires a chatId to create a session (integrity guard),
      // but a FRESH pane has no chat until the first send mints one. To
      // connect without a message, mint the chat here exactly the way
      // handleSend does (same id shape, same doc, same switchToChat), so the
      // user's first real message lands in this same chat and an incoming
      // conductor turn has a chat to render into.
      let targetChatId = currentChatId;
      if (!targetChatId) {
        targetChatId = `chat-${Date.now()}`;
        // Route any instantly-injected conductor turn to the minted chat while
        // React state catches up. CLEARED by the catch-up effect once
        // currentChatId lands on this chat: leaving it set would make
        // addSanitizedMessage bypass the live-view state path for every later
        // message (messages persist to the repository but never render - the
        // stuck-placeholder bug).
        chatIdOverrideRef.current = targetChatId;
        conductorMintedChatRef.current = targetChatId;
        try {
          if (currentPresentation?.id) {
            await repository.createChat(currentPresentation.id, {
              id: targetChatId,
              name: 'New chat',
              // Connector-minted: hidden from history until it has content;
              // origin labels it by the app it was started in.
              origin: app,
              conductorMinted: true,
              transcriptMode: TRANSCRIPT_MODE_PENDING,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            });
          }
          if (typeof switchToChat === 'function') {
            markProgrammaticSwitch(targetChatId);
            switchToChat(targetChatId);
          }
        } catch (chatErr) {
          console.warn('⚠️ Connector: failed to pre-create chat:', chatErr?.message);
        }
      }

      const attach = async (sid) => {
        const streamUrl = buildSessionStreamUrl(
          streamingBaseUrl,
          sid,
          targetChatId,
          documentPrivacyRef.current
        );
        await ensureSessionStream({ sessionId: sid, streamUrl, authToken });
        // Confirm registration over REST (belt to the SSE snapshot's braces).
        await syncConductorPeers(sid, authToken);
        return sid;
      };

      // Reuse the chat's existing session when it is still alive; a dead one
      // (backend restart, idle cleanup) falls through to a fresh session.
      // Never reuse a session created under a DIFFERENT chat: backend
      // sessions are chat-scoped, so attaching would bind this chat to the
      // wrong session and 404 on the first message.
      const ownedByThisChat = !sessionChatIdRef.current || !targetChatId || sessionChatIdRef.current === targetChatId;
      if (sessionId && !activeSessionMustRotate && ownedByThisChat) {
        try {
          return await attach(sessionId);
        } catch (staleErr) {
          if (staleErr instanceof FinanceStreamPrivacyError) {
            throw staleErr;
          }
          console.warn('⚠️ Connector: existing session is stale, creating a fresh one:', staleErr?.message);
          setSessionId(null);
          sessionDocumentBindingRef.current = {
            sessionId: null,
            documentId: null,
          };
        }
      }

      const createResponse = await fetch(`${streamingBaseUrl}/sessions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
        body: JSON.stringify({
          chatId: targetChatId,
          clientApp: app,
          transcriptProtocolVersion: SCOPED_TRANSCRIPT_PROTOCOL_VERSION,
          requestOptions: { model: selectedModel || DEFAULT_MODEL, clientApp: app },
        }),
      });
      if (!createResponse.ok) {
        throw new Error(`Connector session create failed: ${createResponse.status}`);
      }
      const sessionData = await createResponse.json();
      const freshSessionId = sessionData.sessionId;
      await applyServerTranscriptMode(targetChatId, sessionData.transcriptMode, {
        sessionId: freshSessionId,
        sessionCreatedAt: new Date().toISOString(),
      });
      setSessionId(freshSessionId);
      sessionChatIdRef.current = targetChatId;
      sessionDocumentBindingRef.current = {
        sessionId: freshSessionId,
        documentId: null,
      };
      return attach(freshSessionId);
    })();

    conductorConnectRef.current = run.finally(() => {
      conductorConnectRef.current = null;
    });
    return conductorConnectRef.current;
  }, [
    app,
    applyServerTranscriptMode,
    currentChatId,
    currentPresentation?.id,
    currentUser,
    ensureSessionStream,
    flushSettledLockerOfficeOperations,
    markProgrammaticSwitch,
    readFreshDocumentPrivacy,
    repository,
    selectedModel,
    sessionId,
    setAwaitingPeer,
    switchToChat,
    syncConductorPeers,
  ]);

  // OVERRIDE CATCH-UP: once React state has switched to the connector-minted
  // chat, drop the override so messages flow through the normal addMessage
  // path (which updates the live view). Only the connector's own override is
  // ever cleared here; handleSend's overrides are owned by its finally block.
  // Safe by construction: any turn in flight at this point started AFTER the
  // mint, so its closures already carry the minted chat as currentChatId.
  useEffect(() => {
    if (
      conductorMintedChatRef.current &&
      currentChatId === conductorMintedChatRef.current &&
      chatIdOverrideRef.current === conductorMintedChatRef.current
    ) {
      chatIdOverrideRef.current = null;
      conductorMintedChatRef.current = null;
    }
  }, [currentChatId]);

  // EAGER CONNECT + AUTO-RECONNECT: register this pane into the connector the
  // moment the add-in is loaded and signed in — no message required. NOT
  // gated on currentChatId: a fresh pane has no chat yet and connectConductor
  // mints one itself. The delay gives an existing chat (restored by the chat
  // layer) time to land so we prefer it over minting; the isActive guard
  // makes re-runs and StrictMode double-mounts no-ops.
  //
  // conductorOnline is a dep ON PURPOSE: the stream teardown flips it to
  // false, re-running this effect, so a dropped SSE connection (proxy idle
  // kill, Cloud Run's one-hour request ceiling, transient network loss)
  // reconnects automatically within the conductor's retention window instead
  // of leaving an idle pane dead until a manual icon click. Failed attempts
  // use bounded exponential backoff and then leave the icon as a manual retry.
  useEffect(() => {
    if (
      !currentUser ||
      !canConnectConductor(
        documentPrivacyRef.current,
        documentPrivacyResolution,
        FINANCE_CONNECTORS_UI_ENABLED
      )
    ) {
      return undefined;
    }
    if (sessionStreamRef.current.isActive) return undefined;
    const attempt = conductorReconnectAttemptsRef.current;
    const delayMs = attempt === 0 ? 1500 : Math.min(1500 * (2 ** attempt), 24000);
    const timer = setTimeout(() => {
      if (sessionStreamRef.current.isActive) return;
      connectConductor().then(() => {
        conductorReconnectAttemptsRef.current = 0;
      }).catch((err) => {
        const nextAttempt = conductorReconnectAttemptsRef.current + 1;
        conductorReconnectAttemptsRef.current = nextAttempt;
        console.warn('⚠️ Connector reconnect failed:', { attempt: nextAttempt, error: err?.message });
        if (nextAttempt < 5) setConductorReconnectEpoch((value) => value + 1);
      });
    }, delayMs);
    return () => clearTimeout(timer);
  }, [
    connectConductor,
    conductorOnline,
    conductorReconnectEpoch,
    currentChatId,
    currentUser,
    documentPrivacy?.state,
    documentPrivacyResolution,
  ]);

  useEffect(() => {
    if (
      canConnectConductor(
        documentPrivacyRef.current,
        documentPrivacyResolution,
        FINANCE_CONNECTORS_UI_ENABLED
      )
    ) {
      return;
    }
    setConductorOnline(false);
    setConductorPeers([]);
    setAwaitingPeer(null);
  }, [
    documentPrivacy?.state,
    documentPrivacyResolution,
    setAwaitingPeer,
  ]);

  const startLiveDataTask = useCallback(async ({
    task,
    operationId,
    documentMarker,
  }) => {
    const controller = financeOfficeControllerRef.current;
    if (
      !FINANCE_CONNECTORS_UI_ENABLED ||
      !controller ||
      !financeOfficeReady ||
      !financeConnectorReady
    ) {
      throw new Error('Live data is not available in this Office file');
    }
    if (
      !documentMarker ||
      documentMarker.version !== 1 ||
      documentMarker.documentId !== documentPrivacy?.documentId
    ) {
      throw new Error('The Office document privacy marker changed');
    }
    const authToken = currentUser ? await currentUser.getIdToken() : null;
    if (!authToken) throw new Error('Sign in before using live data');
    const streamingBaseUrl = process.env.REACT_APP_STREAMING_SERVICE_URL ||
      'https://claude-streaming-service-103376361923.us-central1.run.app';
    const privateModel = selectFinancePrivateModel(selectedModel);
    const privateController = new AbortController();
    abortControllerRef.current = privateController;

    let targetSessionId = sessionStreamRef.current.sessionId;
    const targetChatId = currentChatId ||
      `private-${documentMarker.documentId}`;
    if (!FINANCE_PRIVATE_MODELS.has(selectedModel)) {
      targetSessionId = null;
    }
    if (financeSessionRequiresRotation(
      targetSessionId,
      sessionDocumentBindingRef.current,
      documentMarker
    )) {
      targetSessionId = null;
    }
    if (!targetSessionId) {
      const createResponse = await fetch(`${streamingBaseUrl}/sessions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          chatId: targetChatId,
          clientApp: app,
          transcriptProtocolVersion: SCOPED_TRANSCRIPT_PROTOCOL_VERSION,
          requestOptions: {
            model: privateModel,
            clientApp: app,
          },
        }),
        signal: privateController.signal,
      });
      if (!createResponse.ok) {
        throw new Error(`Private session create failed: ${createResponse.status}`);
      }
      const sessionData = await createResponse.json();
      targetSessionId = sessionData.sessionId;
      if (!targetSessionId) throw new Error('Private session identity is missing');
      setSessionId(targetSessionId);
      sessionChatIdRef.current = targetChatId;
      sessionDocumentBindingRef.current = {
        sessionId: targetSessionId,
        documentId: null,
      };
      const streamUrl = buildSessionStreamUrl(
        streamingBaseUrl,
        targetSessionId,
        targetChatId,
        documentMarker
      );
      await ensureSessionStream({
        sessionId: targetSessionId,
        streamUrl,
        authToken,
      });
    }

    const prepared = await controller.prepareLiveDataTarget(operationId, {
      signal: privateController.signal,
    });
    if (
      prepared.documentPrivacy.documentId !== documentMarker.documentId ||
      prepared.documentPrivacy.state !== documentMarker.state
    ) {
      throw new Error('The Office document privacy marker changed');
    }
    const response = await fetch(
      `${streamingBaseUrl}/sessions/${targetSessionId}/live-data-tasks`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          version: 1,
          operationId,
          documentId: documentMarker.documentId,
          documentPrivacy: documentMarker,
          connectorId: 'daloopa',
          operationClass: 'financial_data_lookup',
          task,
          officeTarget: prepared.officeTarget,
        }),
        signal: privateController.signal,
      }
    );
    const body = await response.json().catch(() => ({}));
    if (
      response.status !== 202 ||
      body.accepted !== true ||
      body.operationId !== operationId
    ) {
      throw new Error(`Live data task was rejected: ${response.status}`);
    }
    sessionDocumentBindingRef.current = {
      sessionId: targetSessionId,
      documentId: documentMarker.documentId,
    };
    return Object.freeze({
      sessionId: targetSessionId,
      operationId,
    });
  }, [
    app,
    currentChatId,
    currentUser,
    documentPrivacy?.documentId,
    ensureSessionStream,
    financeConnectorReady,
    financeOfficeReady,
    selectedModel,
  ]);

  const handleStopRequest = useCallback(async () => {
    // Gate on work actually in flight, not on the rendered flag. The composer
    // shows Stop from the moment of the click, while `isChatStreaming` is
    // briefly knocked back to false when a new session aborts the previous
    // stream. Gating on the state value made Stop a silent no-op in exactly
    // that window: it rendered, it was clickable, and it returned immediately.
    if (!isChatStreamingRef.current && !activeRequestIdRef.current) return;

    const stoppingPrivateSession = privateSessionActiveRef.current;
    const interruptChatId =
      activeTurnChatIdRef.current || currentChatId;
    console.log('🛑 Stopping current turn...');
    privateOfficeCancellationRegistryRef.current.cancelAll('user_stopped');
    if (stoppingPrivateSession) {
      terminatePrivateLiveDataSession({
        operationId: liveDataSessionRef.current?.operationId || null,
        stage: 'cancelled',
        code: 'user_cancelled',
        message: LIVE_DATA_STAGE_LABELS.cancelled,
        abortLocal: true,
        settleAsCancelled: true,
      });
    }

    const interruptSessionId = sessionStreamRef.current.sessionId || sessionId;
    let interruptAcknowledged = false;
    if (!interruptSessionId) {
      console.warn('⚠️ No active session is available to interrupt');
    } else {
      try {
        const authToken = currentUser ? await currentUser.getIdToken() : null;
        const streamingBaseUrl = process.env.REACT_APP_STREAMING_SERVICE_URL || 'https://claude-streaming-service-103376361923.us-central1.run.app';
        const response = await fetch(`${streamingBaseUrl}/sessions/${interruptSessionId}/control`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(authToken && { 'Authorization': `Bearer ${authToken}` })
          },
          body: JSON.stringify({ action: 'interrupt', chatId: interruptChatId })
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok || result.success !== true) {
          console.error('❌ The server did not acknowledge the interrupt', { status: response.status, result });
        } else {
          interruptAcknowledged = true;
          console.log('✅ Current turn interrupted; session remains active');
        }
      } catch (error) {
        console.error('❌ Failed to interrupt the active turn:', error);
      }
    }
    if (!interruptAcknowledged && !stoppingPrivateSession) {
      return;
    }

    // Abort local fetch controller if any
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      debug('✅ Local abort controller triggered');
    }

    if (stoppingPrivateSession) {
      setPendingUserQuestion(null);
      clearPendingProposeEdits('private_turn_stopped');

      if (activeTurnRef.current) {
        try {
          const interruptError = new Error('Turn interrupted by user');
          interruptError.name = 'AbortError';
          activeTurnRef.current.reject(interruptError);
        } catch (_) {
          // The turn may already have settled.
        }
        activeTurnRef.current = null;
      }
      while (pendingTurnQueueRef.current.length > 0) {
        const entry = pendingTurnQueueRef.current.shift();
        try {
          const interruptError = new Error('Turn interrupted by user');
          interruptError.name = 'AbortError';
          entry?.reject(interruptError);
        } catch (_) {
          // The queued turn may already have settled.
        }
      }
      return;
    }

    // Mark current AI message as cancelled with proper duration
    const lastAiMessageIndex = currentMessages.findLastIndex(m => m.sender === 'ai');
    if (lastAiMessageIndex >= 0) {
      const lastAiMessage = currentMessages[lastAiMessageIndex];
      if (lastAiMessage.components && lastAiMessage.components.length > 0) {
        console.log('🛑 Marking components as complete for interrupted turn');

        const { componentUpdaters } = await import('./streamProcessor');
        const completedComponents = componentUpdaters.settleAllComponents(lastAiMessage.components, false);
        const completedAt = new Date().toISOString();

        // Calculate duration from startTime with cap
        const MAX_DURATION_SECONDS = 10 * 60; // 10 minutes cap
        let durationSeconds = 0;
        if (lastAiMessage.metadata?.startTime) {
          const startMs = typeof lastAiMessage.metadata.startTime === 'number'
            ? lastAiMessage.metadata.startTime
            : Date.parse(lastAiMessage.metadata.startTime);
          if (!Number.isNaN(startMs)) {
            durationSeconds = Math.min(
              Math.max(0, Math.floor((Date.now() - startMs) / 1000)),
              MAX_DURATION_SECONDS
            );
          }
        }

        // Update timer components with final duration
        const finalComponents = completedComponents.map(comp => {
          if (comp?.type === 'timer') {
            return {
              ...comp,
              data: {
                ...comp.data,
                isStreaming: false,
                isComplete: true,
                isActive: false,
                durationSeconds,
                elapsedSeconds: durationSeconds,
                completedAt
              }
            };
          }
          return comp;
        });

        const updatedMessage = {
          ...lastAiMessage,
          components: finalComponents,
          isStreaming: false,
          isCancelled: true,
          metadata: {
            ...lastAiMessage.metadata,
            isStreaming: false,
            isComplete: true,
            isCancelled: true,
            durationSeconds,
            completedAt
          }
        };

        await addSanitizedMessage(updatedMessage);
      }
    }

    // Clean up UI state only after the server acknowledges the stop.
      setIsChatStreaming(false);
      isChatStreamingRef.current = false;
      activeRequestIdRef.current = null;
      setPendingMessage(null);
      setLiveStreamingMessage(null);
      lastPersistedMessageRef.current = null;

      // Clear streaming state so any in-flight React batches don't bleed into
      // the next AI message bubble. The backend (runTurn) has now aborted the
      // per-turn AbortController, which ends the SDK iterator cleanly — so no
      // more events will arrive for the interrupted turn. sdkSessionId is
      // preserved on the server, so the next message resumes context.
      streamingStateRef.current = null;

      // The stopped turn is no longer this pane's live authorship; let the
      // mirror own the message again (the cancelled copy just persisted has
      // the freshest rev, so remote echoes cannot regress it).
      smartCache.releaseAllLiveMessages();
      activeTurnChatIdRef.current = null;

      // Stopping the turn kills its pendingToolCalls map server-side, so any
      // open question / review card belongs to a dead callId. Clear the cards
      // immediately (the SSE turn_interrupted handler also does this, but a
      // wedged stream must not leave a tappable card behind).
      setPendingUserQuestion(null);
      clearPendingProposeEdits('turn_stopped');

      // DO NOT tear down sessionStreamRef — keep the persistent SSE pipe alive
      // so the next message reuses the same connection. The new per-turn
      // architecture means each user message triggers a fresh query() on the
      // backend; the SSE connection is independent of query lifetime.

      // Reject the current pending turn so handleSend's awaiter unblocks
      if (activeTurnRef.current) {
        try {
          const interruptError = new Error('Turn interrupted by user');
          interruptError.name = 'AbortError';
          activeTurnRef.current.reject(interruptError);
          activeTurnRef.current = null;
          console.log('✅ Pending turn rejected');
        } catch (rejectError) {
          console.warn('⚠️ Failed to reject pending turn:', rejectError);
        }
      }
      // Drain any queued waiters that were never assigned
      while (pendingTurnQueueRef.current.length > 0) {
        const entry = pendingTurnQueueRef.current.shift();
        if (entry) {
          try {
            const interruptError = new Error('Turn interrupted by user');
            interruptError.name = 'AbortError';
            entry.reject(interruptError);
          } catch (_) { /* ignore */ }
        }
      }

      console.log('🔄 Session + SSE stream preserved for follow-up messages');

      await saveRequestDuration();
  }, [
    isChatStreaming,
    sessionId,  // Added for Cloud Run control endpoint
    currentMessages,
    currentUser,
    currentPresentation?.id,
    currentChatId,  // Added for Cloud Run control endpoint
    addSanitizedMessage,
    clearPendingProposeEdits,
    saveRequestDuration,
    setIsChatStreaming,
    setPendingMessage,
    terminatePrivateLiveDataSession,
    updateLiveDataSession,
  ]);

  // REMOVED: Escape key handler for stopping AI
  // Users should use the stop button in chat UI instead
  // This prevents conflict with slideshow Escape (which exits fullscreen)

  const sendMessageToAI = useCallback(async (messages, requestId, providedChatId = null, aiMessageIdOverride = null, options = {}) => {
    let cancelPendingTurn = null;
    let turnPromiseRef = null;
    const ephemeralTurn = options.ephemeral === true;
    const deliveryContext = {
      transcriptMode: TRANSCRIPT_MODE_ORDINARY,
      dispatchStarted: false,
      responseStatus: null,
      serverErrorCode: null,
      acceptanceConfirmed: false,
    };
    try {
      const lastUserMessage = messages[messages.length - 1];
      const requestedModel = selectedModel || DEFAULT_MODEL;
      const turnModel = ephemeralTurn
        ? selectFinancePrivateModel(requestedModel)
        : requestedModel;

      // CLOUD RUN MIGRATION: Use new session-based API instead of Firebase Functions
      // Production uses direct Cloud Run URL, local dev uses localhost:8080
      const streamingBaseUrl = process.env.REACT_APP_STREAMING_SERVICE_URL || 'https://claude-streaming-service-103376361923.us-central1.run.app';
      console.log('📤 Using Cloud Run streaming service:', streamingBaseUrl);

      const controller = new AbortController();
      abortControllerRef.current = controller;

      let authToken = null;
      if (currentUser) {
        authToken = await currentUser.getIdToken();
      }

      const aiMessageId = aiMessageIdOverride || generateMessageId();

      const {
        entry: pendingTurnEntry,
        promise: pendingTurnPromise,
        cancel,
      } = createPendingTurn({
        aiMessageId,
        requestId,
        // The turn's home chat, immutable for its lifetime (see
        // activeTurnChatIdRef): captured at send time, not at render time.
        chatId: providedChatId || currentChatId || null,
        ephemeral: ephemeralTurn,
        privacyPending: options.privacyPending === true,
        stagedUserMessage: options.privacyPending === true
          ? lastUserMessage
          : null,
        analytics: ephemeralTurn
          ? null
          : {
              messageLength: lastUserMessage?.text?.length || 0,
              hasAttachments: Boolean(
                lastUserMessage?.components?.find(
                  (component) => component.type === 'attachments'
                )
              ),
              model: selectedModel || 'default',
            },
        eventReady: false,
      });
      turnPromiseRef = pendingTurnPromise;
      cancelPendingTurn = cancel;


      resetLoopTelemetry?.();

      // STEP 1: Create or retrieve Cloud Run session.
      //
      // Important: if the caller forced a new chat (options.forceNewSession),
      // ignore any `sessionId` in React state. State updates are asynchronous,
      // so handleSend's `setSessionId(null)` from the new-chat branch hasn't
      // actually taken effect yet when this function runs. The stale closure
      // value would otherwise get used against the fresh chatId and cause a
      // 404 on POST /messages.
      let cloudRunSessionId = options.forceNewSession ? null : sessionId;

      // Backend sessions are chat-scoped: a session created under chat A
      // 404s when messaged under chat B, sending every switched-chat message
      // through the stale-session recovery path (extra session churn, wider
      // race windows). If the id on hand belongs to another chat, treat it
      // as absent and create a fresh session for this chat up front.
      const sendTargetChatId = providedChatId || currentChatId || null;
      const sendTargetChat = sendTargetChatId && currentPresentation?.id
        ? (smartCache.get(currentPresentation.id)?.chats || [])
            .find((chat) => chat.id === sendTargetChatId)
        : null;
      deliveryContext.transcriptMode = transcriptModeForChat(sendTargetChat);
      if (
        cloudRunSessionId &&
        sessionChatIdRef.current &&
        sendTargetChatId &&
        sessionChatIdRef.current !== sendTargetChatId
      ) {
        console.log('🔐 Session belongs to another chat; starting fresh for this chat', {
          sessionChatId: sessionChatIdRef.current,
          sendTargetChatId
        });
        cloudRunSessionId = null;
      }
      if (
        cloudRunSessionId &&
        financeSessionRequiresRotation(
          cloudRunSessionId,
          sessionDocumentBindingRef.current,
          options.documentPrivacy
        )
      ) {
        console.log('🔐 Office document changed; starting a fresh session');
        cloudRunSessionId = null;
      }
      let wasStaleSession = false; // Track if we had to recover from stale session

      console.log('🔐 DEBUG: Session state at start of sendMessageToAI:', {
        hasExistingSession: !!cloudRunSessionId,
        sessionId: cloudRunSessionId,
        chatId: providedChatId || currentChatId,
        providedChatId,
        currentChatId,
        forceNewSession: !!options.forceNewSession,
        selectedModel
      });

      // Helper function to create a new session
      const createNewSession = async () => {
        console.log('🔐 Creating new Cloud Run session...');

        const targetChatId = providedChatId || currentChatId || null;

        const createSessionPayload = {
          chatId: targetChatId,
          clientApp: app,  // 'powerpoint' | 'excel': selects the backend prompt + MCP server
          transcriptProtocolVersion: SCOPED_TRANSCRIPT_PROTOCOL_VERSION,
          requestOptions: {
            model: turnModel,
            clientApp: app
          }
        };

        console.log('🔐 DEBUG: Creating session with payload:', {
          chatId: createSessionPayload.chatId,
          model: turnModel
        });

        const createHeaders = {
          'Content-Type': 'application/json'
        };

        if (authToken) {
          createHeaders['Authorization'] = `Bearer ${authToken}`;
        }

        const createResponse = await fetch(`${streamingBaseUrl}/sessions`, {
          method: 'POST',
          headers: createHeaders,
          body: JSON.stringify(createSessionPayload),
          signal: controller.signal
        });

        if (!createResponse.ok) {
          const errorText = await createResponse.text();
          throw new Error(`Failed to create session: ${createResponse.status} - ${errorText}`);
        }

        const sessionData = await createResponse.json();
        const newSessionId = sessionData.sessionId;
        deliveryContext.transcriptMode = sessionData.transcriptMode;

        console.log('✅ Cloud Run session created:', {
          sessionId: newSessionId,
          streamUrl: sessionData.streamUrl,
          messageUrl: sessionData.messageUrl
        });

        console.log('🔐 DEBUG: Session created successfully, storing in state:', {
          sessionId: newSessionId,
          willPersistTo: {
            chatId: providedChatId || currentChatId
          }
        });

        const chatIdToPersist = providedChatId || currentChatId;
        if (!ephemeralTurn && chatIdToPersist && currentPresentation?.id) {
          await applyServerTranscriptMode(chatIdToPersist, sessionData.transcriptMode, {
            sessionId: newSessionId,
            sessionCreatedAt: new Date().toISOString(),
          });
        }

        setSessionId(newSessionId);
        sessionChatIdRef.current = targetChatId;
        sessionDocumentBindingRef.current = {
          sessionId: newSessionId,
          documentId: null,
        };

        return newSessionId;
      };

      // If the eager connector connect is mid-flight (pane just loaded), adopt
      // its session instead of racing it with a second create. Two sessions for
      // one pane was exactly the duplicate-agent bug; the backend's newest-wins
      // registry would self-heal it, but not creating the duplicate is better.
      if (!cloudRunSessionId && !options.forceNewSession && conductorConnectRef.current) {
        try {
          cloudRunSessionId = await conductorConnectRef.current;
          console.log('🔐 Adopted connector session:', cloudRunSessionId);
        } catch { /* eager connect failed; fall through to a fresh session */ }
      }

      // If no existing session, create one
      if (!cloudRunSessionId) {
        cloudRunSessionId = await createNewSession();
      } else {
        console.log('🔐 Using existing session:', cloudRunSessionId);
      }

      // Helper to check if error indicates a stale/dead session
      const isStaleSessionError = (error) => {
        const message = error?.message || '';
        const code = error?.code || '';
        return (
          // Network errors
          message.includes('Failed to fetch') ||
          message.includes('ERR_CONNECTION_REFUSED') ||
          message.includes('NetworkError') ||
          message.includes('net::ERR_') ||
          (error?.name === 'TypeError' && message.includes('fetch')) ||
          // Session gone (404 from Cloud Run after idle cleanup)
          message.includes('Session not found') ||
          message.includes('404') ||
          // SDK process exit errors (stale session where SDK died)
          message.includes('process exited with code') ||
          message.includes('Claude Code process exited') ||
          code === 'SDK_PROCESS_EXIT' ||
          code === 'SESSION_ERROR'
        );
      };

      let streamUrl = buildSessionStreamUrl(
        streamingBaseUrl,
        cloudRunSessionId,
        providedChatId || currentChatId,
        options.documentPrivacy || documentPrivacyRef.current
      );

      try {
        await ensureSessionStream({
          sessionId: cloudRunSessionId,
          streamUrl,
          authToken
        });
      } catch (streamError) {
        // Check if this is a stale session error (old session from previous server restart)
        if (sessionId && isStaleSessionError(streamError)) {
          console.warn('⚠️ Stale session detected - connection failed. Creating new session...', {
            staleSessionId: cloudRunSessionId,
            error: streamError.message
          });

          // Clear the stale session from state
          setSessionId(null);
          sessionDocumentBindingRef.current = {
            sessionId: null,
            documentId: null,
          };
          wasStaleSession = true;

          // Clear from chat metadata
          const staleChatId = providedChatId || currentChatId;
          if (!ephemeralTurn && staleChatId && currentPresentation?.id) {
            try {
              // updateChat is (presentationId, chatId, updates); the old 2-arg
              // call resolved no document and silently persisted nothing.
              await repository.updateChat(currentPresentation.id, staleChatId, {
                sessionId: null,
                staleSessionCleared: new Date().toISOString(),
                staleSessionId: cloudRunSessionId // Keep record of old session for debugging
              });
              console.log('🧹 Cleared stale sessionId from chat metadata');
            } catch (clearErr) {
              console.warn('⚠️ Failed to clear stale sessionId from chat:', clearErr);
            }
          }

          // Create a new session
          cloudRunSessionId = await createNewSession();

          // Update stream URL with new session ID
          streamUrl = buildSessionStreamUrl(
            streamingBaseUrl,
            cloudRunSessionId,
            providedChatId || currentChatId,
            options.documentPrivacy || documentPrivacyRef.current
          );

          // Retry connecting with new session
          try {
            await ensureSessionStream({
              sessionId: cloudRunSessionId,
              streamUrl,
              authToken
            });
            console.log('✅ Successfully connected with new session after stale session recovery');
          } catch (retryError) {
            // Retry also failed - this is a real error
            if (cancelPendingTurn) {
              cancelPendingTurn(retryError);
              cancelPendingTurn = null;
            }
            throw retryError;
          }
        } else {
          // Not a stale session error - propagate as-is
          if (cancelPendingTurn) {
            cancelPendingTurn(streamError);
            cancelPendingTurn = null;
          }
          throw streamError;
        }
      }

      // STEP 2: Prepare message to send via queue
      const SLIDING_WINDOW_SIZE = 40;
      const recentMessages = messages.slice(-SLIDING_WINDOW_SIZE);
      console.log(`📊 Using sliding window: ${recentMessages.length} of ${messages.length} messages`);

      const lastMessage = recentMessages[recentMessages.length - 1];

      // Build message content from last user message
      let messageContent = lastMessage.text || '';
      const images = [];

      if (lastMessage.components && lastMessage.components.length > 0) {
        const attachmentComp = lastMessage.components.find(c => c.type === 'attachments');
        if (attachmentComp && attachmentComp.data) {
          for (const file of attachmentComp.data) {
            if (file.contentType === 'image' && file.base64) {
              images.push({
                type: 'image_url',
                image_url: {
                  url: file.base64,
                  detail: 'auto'
                }
              });
            }
          }
        }

        const componentContent = serializeComponentsToText(lastMessage.components);
        if (componentContent) {
          messageContent = messageContent ? `${messageContent}\n${componentContent}` : componentContent;
        }
      }

      // STEP 2.5: Inject Office host context blocks if a bridge is available.
      // The tag names are host-specific so each system prompt finds the block it
      // expects: PowerPoint/Excel read <initial_state> + <user_context>; Word
      // reads <doc_state> + <user_selection> (matching the Word reference + prompt).
      // PowerPoint and Excel file state is stable enough to collect once per
      // chat. Word state is refreshed because document content, tracked changes,
      // and the selected-template blank-document guard can change each turn.
      // Current selection is refreshed on each send. Run these sequentially because
      // Office.run calls share a host queue and can deadlock when started in
      // parallel on Office web.
      let currentOfficeInitialState = null;
      if (powerpointBridge) {
        try {
          const stateKey = `${activeTurnChatIdRef.current || providedChatId || currentChatId || 'new'}:${app}`;
          let initialState = ephemeralTurn
            ? null
            : initialOfficeStateCacheRef.current.get(stateKey);
          if (app === 'word' || ephemeralTurn || !initialOfficeStateCacheRef.current.has(stateKey)) {
            initialState = await withTimeout(
              powerpointBridge.getInitialState(),
              7000,
              'Office initial state'
            );
            if (!ephemeralTurn) {
              initialOfficeStateCacheRef.current.set(stateKey, initialState);
            }
          }
          currentOfficeInitialState = initialState;
          const userContext = await withTimeout(
            powerpointBridge.getUserContext(),
            4000,
            'Office selection context'
          );

          const isWord = app === 'word';
          const stateTag = isWord ? 'doc_state' : 'initial_state';
          const ctxTag = isWord ? 'user_selection' : 'user_context';

          let contextBlocks = '';
          if (initialState) {
            contextBlocks += `\n\n<${stateTag}>\n${JSON.stringify(initialState, null, 2)}\n</${stateTag}>`;
          }
          if (userContext) {
            contextBlocks += `\n\n<${ctxTag}>\n${JSON.stringify(userContext, null, 2)}\n</${ctxTag}>`;
          }

          if (contextBlocks) {
            messageContent = messageContent + contextBlocks;
            console.log('[hooks] Injected Office context blocks', {
              app,
              hasInitialState: !!initialState,
              hasUserContext: !!userContext
            });
          }
        } catch (contextError) {
          console.warn('[hooks] Failed to gather Office context:', contextError.message);
        }
      }

      // Selected templates (Workflows tab template library): tell the agent
      // which template governs the originating file. If PowerPoint is a
      // connected output rather than the originating app, also expose its
      // selection so the same turn can apply that design through the typed
      // connected PowerPoint tool. Best-effort and cached; the firm default
      // template flows through the same selection.
      if (currentUser?.uid) {
        try {
          const templateApps = app === 'powerpoint' ? ['powerpoint'] : [app, 'powerpoint'];
          const activeTemplates = await Promise.all(
            templateApps.map((templateApp) => getActiveTemplate(currentUser.uid, templateApp))
          );
          for (const [templateIndex, templateApp] of templateApps.entries()) {
            const activeTemplate = activeTemplates[templateIndex];
            if (
              !activeTemplate?.name ||
              !activeTemplate?.downloadUrl ||
              !templateMatchesApp(activeTemplate.fileName || activeTemplate.name, templateApp)
            ) {
              continue;
            }

            let selectedTemplateInstruction;
            if (templateApp === 'powerpoint' && app !== 'powerpoint') {
              selectedTemplateInstruction = `The user selected "${activeTemplate.name}" (file: ${activeTemplate.fileName}) as the visual authority for work created in the connected PowerPoint presentation. Before creating or visually restyling connected PowerPoint slides, call mcp__connected_powerpoint__apply_template once in this turn. It returns a clean template-bound working slide without retaining source/example slides. Use its imported layouts and inherited theme, preserve existing user slides unless explicitly asked to retemplate them, and do not overwrite the selected theme with edit_slide_master.`;
            } else if (templateApp === 'powerpoint') {
              selectedTemplateInstruction = `The user explicitly selected "${activeTemplate.name}" (file: ${activeTemplate.fileName}) as the visual authority for new PowerPoint work. This selection overrides styling already present in the open deck. Before creating or visually restyling slides, call apply_template once in this turn even when the deck already has a usable theme. apply_template imports the selected theme, master, and layouts and returns a clean template-bound working slide without retaining source/example slides. Use the returned template layouts and inherited theme styling. Preserve existing user slides unless the user explicitly asks to retemplate them, and do not invent or overwrite the selected theme with edit_slide_master.`;
            } else if (templateApp === 'word') {
              const wordCount = Number(currentOfficeInitialState?.wordCount);
              const characterCount = Number(currentOfficeInitialState?.characterCount);
              const wordDocumentIsBlank = Boolean(
                currentOfficeInitialState &&
                Number.isFinite(wordCount) &&
                Number.isFinite(characterCount) &&
                wordCount === 0 &&
                characterCount === 0
              );
              selectedTemplateInstruction = wordDocumentIsBlank
                ? `The user explicitly selected "${activeTemplate.name}" (file: ${activeTemplate.fileName}) for this blank Word document. Call apply_template once before drafting, then use the inserted structure and native Word styles.`
                : `The user selected "${activeTemplate.name}" (file: ${activeTemplate.fileName}), but the current Word document is populated or its blank state could not be confirmed. Do not call apply_template because Word would append a second template body. Preserve the open document's structure and styles. Apply this template only in a confirmed blank document.`;
            } else {
              selectedTemplateInstruction = `The user explicitly selected "${activeTemplate.name}" (file: ${activeTemplate.fileName}) as the template override for this app. Call apply_template before building new content and follow the selected file's structure and formatting. Preserve existing user content unless the user explicitly asks to replace or retemplate it.`;
            }
            messageContent += `\n\n<selected_template app="${templateApp}">\n${selectedTemplateInstruction}\n</selected_template>`;
          }
        } catch { /* template context is best-effort */ }
      }

      // STEP 2.7: Upload source documents so the AI can navigate them via the
      // list_uploaded_files / read_uploaded_file / grep_uploaded_files tools.
      // We send the text the client already extracted (mode=text). This is
      // BEST-EFFORT and FAIL-SAFE — any error here must never block the message.
      if (!ephemeralTurn) {
        try {
        const docAttachComp = lastMessage.components?.find((c) => c.type === 'attachments');
        const nonImage = (docAttachComp?.data || []).filter((f) => f.contentType !== 'image');
        const hasText = (f) => typeof f.content === 'string' && f.content.trim().length > 0;
        const docs = nonImage.filter(hasText);
        const unreadable = nonImage.filter((f) => !hasText(f));
        const uploadedNames = [];
        for (const doc of docs) {
          try {
            const uploadUrl = `${streamingBaseUrl}/sessions/${cloudRunSessionId}/uploads`
              + `?mode=text&fileName=${encodeURIComponent(doc.name || 'document.txt')}`
              + `&chatId=${encodeURIComponent(providedChatId || currentChatId || '')}`;
            const uploadResp = await fetch(uploadUrl, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/octet-stream',
                ...(authToken && { Authorization: `Bearer ${authToken}` })
              },
              body: doc.content.slice(0, 500000)
            });
            if (uploadResp.ok) uploadedNames.push(doc.name || 'document.txt');
            else console.warn('[hooks] document upload failed', doc.name, uploadResp.status);
          } catch (perFileErr) {
            console.warn('[hooks] document upload error', doc.name, perFileErr.message);
          }
        }
        for (const doc of unreadable) {
          const rawFile = (options.rawSourceFiles || []).find((file) => file.name === doc.name);
          if (!rawFile) continue;
          try {
            const uploadUrl = `${streamingBaseUrl}/sessions/${cloudRunSessionId}/uploads`
              + `?mode=raw&fileName=${encodeURIComponent(doc.name || 'document')}`
              + `&chatId=${encodeURIComponent(providedChatId || currentChatId || '')}`;
            const uploadResp = await fetch(uploadUrl, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/octet-stream',
                ...(authToken && { Authorization: `Bearer ${authToken}` })
              },
              body: await rawFile.arrayBuffer()
            });
            if (uploadResp.ok) uploadedNames.push(doc.name || 'document');
            else console.warn('[hooks] raw document upload failed', doc.name, uploadResp.status);
          } catch (perFileError) {
            console.warn('[hooks] raw document upload error', doc.name, perFileError.message);
          }
        }
        if (uploadedNames.length > 0) {
          messageContent += `\n\n[Source documents available for this chat: ${uploadedNames.join(', ')}. Use list_uploaded_files / grep_uploaded_files / read_uploaded_file to search and re-read them as needed.]`;
        }
        const stillUnreadable = unreadable.filter((f) => !uploadedNames.includes(f.name));
        if (stillUnreadable.length > 0) {
          const names = stillUnreadable.map((f) => f.name || 'file').join(', ');
          messageContent += `\n\n[Note: these attachments could not be read here (unsupported format): ${names}. Tell the user you couldn't read them and ask them to paste the text or share a PDF / Excel / CSV instead.]`;
        }
        } catch (uploadStepErr) {
          console.warn('[hooks] document upload step skipped:', uploadStepErr.message);
        }
      }

      // STEP 3: Send message to session (POST /messages starts a new runTurn)
      //
      // `model` is sent per-turn so users can switch between Fast / Pro / Pro+
      // between messages within the same session. The backend passes this as
      // modelOverride to runTurn() which builds a fresh SDK query for each turn.
      const enqueuePayload = withDocumentPrivacyPayload({
        chatId: providedChatId || currentChatId || null,
        clientRequestId: requestId,
        clientMessageId: lastUserMessage?.id || null,
        clientAssistantMessageId: aiMessageId,
        displayText: boundedTranscriptDisplayText(options.displayText),
        message: {
          role: 'user',
          content: messageContent
        },
        model: turnModel,
      }, options.documentPrivacy);

      const enqueueHeaders = {
        'Content-Type': 'application/json'
      };

      if (authToken) {
        enqueueHeaders['Authorization'] = `Bearer ${authToken}`;
      }

      console.log('📤 About to enqueue message:', {
        sessionId: cloudRunSessionId,
        chatId: providedChatId || currentChatId,
        messageLength: messageContent.length,
        hasImages: images.length > 0,
        model: turnModel,
      });

      pendingTurnEntry.eventReady = true;
      deliveryContext.dispatchStarted = true;
      deliveryContext.responseStatus = null;
      deliveryContext.serverErrorCode = null;
      let enqueueResponse = await fetch(`${streamingBaseUrl}/sessions/${cloudRunSessionId}/messages`, {
        method: 'POST',
        headers: enqueueHeaders,
        body: JSON.stringify(enqueuePayload),
        signal: controller.signal
      });
      deliveryContext.responseStatus = enqueueResponse.status;

      // A 409 means the backend knows the session document but its in-memory
      // stream handlers are not attached. Force one fresh SSE handshake and
      // retry the same message once. This also repairs sessions created by an
      // older client that hit the partial-reconnect race.
      if (enqueueResponse.status === 409) {
        pendingTurnEntry.eventReady = false;
        const disconnectedBody = await enqueueResponse.text();
        deliveryContext.serverErrorCode = responseErrorCode(disconnectedBody);
        const lifecycleDecision = classifyInitialMessageDeliveryFailure(
          deliveryContext
        );
        if (lifecycleDecision.suppressFreshRetry) {
          const lifecycleError = new Error(
            lifecycleDecision.message
          );
          lifecycleError.code = deliveryContext.serverErrorCode;
          throw lifecycleError;
        }
        console.warn('⚠️ Session stream was not ready; reconnecting before retry', {
          sessionId: cloudRunSessionId,
          responseBody: disconnectedBody.slice(0, 200)
        });
        await ensureSessionStream({
          sessionId: cloudRunSessionId,
          streamUrl,
          authToken,
          forceReconnect: true
        });
        pendingTurnEntry.eventReady = true;
        deliveryContext.dispatchStarted = true;
        deliveryContext.responseStatus = null;
        deliveryContext.serverErrorCode = null;
        enqueueResponse = await fetch(`${streamingBaseUrl}/sessions/${cloudRunSessionId}/messages`, {
          method: 'POST',
          headers: enqueueHeaders,
          body: JSON.stringify(enqueuePayload),
          signal: controller.signal
        });
        deliveryContext.responseStatus = enqueueResponse.status;
      }

      // Defense-in-depth: if POST /messages returns 404 "Session not found"
      // (because cloudRunSessionId is stale and ensureSessionStream didn't
      // surface the mismatch quickly enough), recover by creating a fresh
      // session and retrying once. This catches the case where React state
      // lags the actual chat context, or where a previously-working session
      // was cleaned up by a Cloud Run restart.
      if (enqueueResponse.status === 404 && !wasStaleSession) {
        pendingTurnEntry.eventReady = false;
        const staleBody = await enqueueResponse.text();
        console.warn('⚠️ POST /messages returned 404 — recovering with fresh session', {
          staleSessionId: cloudRunSessionId,
          responseBody: staleBody.slice(0, 200)
        });

        wasStaleSession = true;
        setSessionId(null);
        sessionDocumentBindingRef.current = {
          sessionId: null,
          documentId: null,
        };

        // Create a new session for the current chatId and retry once
        cloudRunSessionId = await createNewSession();

        // Re-establish the SSE stream for the new session
        const newStreamUrl = buildSessionStreamUrl(
          streamingBaseUrl,
          cloudRunSessionId,
          providedChatId || currentChatId,
          options.documentPrivacy || documentPrivacyRef.current
        );
        try {
          await ensureSessionStream({
            sessionId: cloudRunSessionId,
            streamUrl: newStreamUrl,
            authToken
          });
        } catch (reStreamError) {
          throw new Error(`Failed to reconnect stream after stale-session recovery: ${reStreamError?.message || reStreamError}`);
        }

        // Retry the enqueue
        pendingTurnEntry.eventReady = true;
        deliveryContext.dispatchStarted = true;
        deliveryContext.responseStatus = null;
        deliveryContext.serverErrorCode = null;
        enqueueResponse = await fetch(`${streamingBaseUrl}/sessions/${cloudRunSessionId}/messages`, {
          method: 'POST',
          headers: enqueueHeaders,
          body: JSON.stringify(enqueuePayload),
          signal: controller.signal
        });
        deliveryContext.responseStatus = enqueueResponse.status;
      }

      if (!enqueueResponse.ok) {
        const errorText = await enqueueResponse.text();
        deliveryContext.serverErrorCode = responseErrorCode(errorText);
        const enqueueError = new Error(
          `Failed to enqueue message: ${enqueueResponse.status} - ${errorText}`
        );
        enqueueError.code = deliveryContext.serverErrorCode || undefined;
        throw enqueueError;
      }

      const enqueueData = await enqueueResponse.json();
      deliveryContext.acceptanceConfirmed = true;
      console.log('✅ Message enqueued:', enqueueData);
      if (
        enqueueData.assistantMessageId
        && enqueueData.assistantMessageId !== aiMessageId
      ) {
        throw new Error(
          'The secure transcript returned a different assistant message identity.'
        );
      }
      if (!ephemeralTurn && enqueueData.transcriptMode) {
        const targetChatId = providedChatId || currentChatId;
        await applyServerTranscriptMode(targetChatId, enqueueData.transcriptMode, {
          sessionId: cloudRunSessionId,
        });
      }
      if (options.documentPrivacy?.documentId) {
        sessionDocumentBindingRef.current = {
          sessionId: cloudRunSessionId,
          documentId: options.documentPrivacy.documentId,
        };
      }

      cancelPendingTurn = null;

      return {
        turnPromise: turnPromiseRef,
        aiMessageId,
        sessionId: cloudRunSessionId
      };
    } catch (error) {
      const surfacedError =
        error instanceof Error
          ? error
          : new Error(String(error || 'Secure request delivery failed'));
      const deliveryDecision = classifyInitialMessageDeliveryFailure(
        deliveryContext
      );
      if (deliveryDecision.suppressFreshRetry) {
        surfacedError.suppressFreshRetry = true;
        surfacedError.deliveryDecision = deliveryDecision;
        surfacedError.code = deliveryDecision.errorCode;
      }
      if (cancelPendingTurn) {
        cancelPendingTurn(surfacedError);
        cancelPendingTurn = null;
      }

      if (isConfirmedPreDispatchAbort(
        surfacedError,
        deliveryDecision
      )) {
        debug('✅ Fetch request was aborted');
        return {
          turnPromise: null,
          aiMessageId: null,
          cancelled: true
        };
      }
      if (ephemeralTurn) {
        console.error('❌ Private message enqueue failed', {
          code:
            surfacedError.code
            || surfacedError.name
            || 'private_enqueue_failed',
        });
      } else {
        console.error('❌ Error sending message:', surfacedError);
      }
      throw surfacedError;
    }
  }, [
    app,
    applyServerTranscriptMode,
    createPendingTurn,
    currentChatId,
    currentPresentation?.id,
    currentUser,
    ensureSessionStream,
    powerpointBridge,
    selectedModel,
    sessionId,
    resetLoopTelemetry,
    repository
  ]);

  const handleSend = useCallback(async (messageOverride = null, options = {}) => {
    if (scopedTranscriptState === 'locked') {
      console.warn('Secure transcript access must be restored before sending.');
      return;
    }
    if (unresolvedOfficeRecoveryRef.current.length > 0) {
      console.warn(
        '[hooks] Review and explicitly resolve the prior Office operation before sending another instruction.'
      );
      return;
    }
    if (
      unresolvedInitialDeliveryRef.current?.chatId
      && unresolvedInitialDeliveryRef.current.chatId === currentChatId
    ) {
      console.warn(
        '[hooks] Refresh this secure chat before sending another instruction because the prior delivery outcome is unresolved.'
      );
      return;
    }
    console.log('🚀 handleSend called:', {
      messageOverrideType: typeof messageOverride,
      inputLength: typeof input === 'string' ? input.length : 0,
      hasFiles: selectedFiles.length > 0,
      currentPresentationId: currentPresentation?.id,
      currentMessagesCount: currentMessages.length,
      isChatStreaming,
      optionKeys: Object.keys(options || {}),
    });

    const cleanMessageOverride = messageOverride && typeof messageOverride === 'string'
      ? messageOverride.trim()
      : null;

    const userMessage = cleanMessageOverride || input.trim();

    if (!userMessage && selectedFiles.length === 0) {
      console.log('❌ No message or files to send, returning');
      return;
    }

    if (userMessage && userMessage.length < 3 && selectedFiles.length === 0 && !options?.clarificationResponse) {
      console.log('❌ Message too short (minimum 3 characters), returning');
      return;
    }

    // A pending ask_user_question owns the turn: the SDK is blocked inside that
    // tool call, so a steering message queues behind it and never unblocks the
    // agent. Deliver typed text as the tool answer instead. Steering semantics
    // are unchanged for every other case, including when attachments are
    // present (those need the normal upload path, so they fall through).
    const pendingQuestion = pendingUserQuestionRef.current;
    if (shouldAnswerPendingQuestionWithText({
      pendingQuestion,
      text: userMessage,
      hasAttachments: selectedFiles.length > 0,
    })) {
      console.log('[hooks] Routing composer text to the pending question:', pendingQuestion.callId);
      setInput('');
      resetTextareaHeight();
      answerUserQuestion(
        buildPendingQuestionTextAnswer(pendingQuestion, userMessage)
      );
      return;
    }

    if (privateSessionActiveRef.current) {
      console.warn('[hooks] A private Office operation is already active');
      return;
    }

    let latestDocumentPrivacy = null;
    if (FINANCE_CONNECTORS_UI_ENABLED && powerpointBridge) {
      try {
        latestDocumentPrivacy = await readFreshDocumentPrivacy();
      } catch (error) {
        console.warn('[hooks] Could not read the Office document privacy marker', {
          code: error?.code || 'finance_office_unavailable',
        });
        updateLiveDataSession({
          type: 'finance_live_data_failed',
          operationId: null,
          stage: 'failed',
          message: 'The live data document boundary is unavailable',
          active: false,
          code: 'finance_office_unavailable',
        });
        return;
      }
    }

    const directLiveDataTurn = liveDataMode === true;
    const ephemeralTurn =
      directLiveDataTurn ||
      latestDocumentPrivacy?.state === 'licensed_data';
    const privacyPending = shouldStageTurnForPrivacy({
      ephemeral: ephemeralTurn,
    });
    if (ephemeralTurn && selectedFiles.length > 0) {
      updateLiveDataSession({
        type: 'finance_live_data_failed',
        operationId: null,
        stage: 'failed',
        message: 'Remove attachments before starting a live data request',
        active: false,
        code: 'attachments_not_allowed',
      });
      return;
    }
    if (
      directLiveDataTurn &&
      (
        !FINANCE_CONNECTORS_UI_ENABLED ||
        !financeOfficeReady ||
        !financeConnectorReady ||
        !latestDocumentPrivacy
      )
    ) {
      updateLiveDataSession({
        type: 'finance_live_data_failed',
        operationId: null,
        stage: 'failed',
        message: 'Live data is not available in this Office file',
        active: false,
        code: 'finance_office_unavailable',
      });
      return;
    }

    if (ephemeralTurn) {
      beginEphemeralScope({
        documentId: latestDocumentPrivacy.documentId,
        chatId: currentChatId ||
          `private-${latestDocumentPrivacy.documentId}`,
        sessionId: sessionStreamRef.current.sessionId || sessionId,
      });
    }

    const sourceFilesSnapshot = [...selectedFiles];
    setInput('');
    clearAttachments();

    resetTextareaHeight();

    setTimeout(() => {
      if (messagesContainerRef.current && messagesEndRef.current) {
        const container = messagesContainerRef.current;

        const scrollHeight = container.scrollHeight;
        const containerHeight = container.clientHeight;
        container.scrollTo({
          top: scrollHeight - containerHeight,
          behavior: 'smooth'
        });
      }
    }, 200);

    let processedFiles = [];
    if (sourceFilesSnapshot.length > 0) {
      console.log('📎 Processing', sourceFilesSnapshot.length, 'attached files for AI...');
      processedFiles = await Promise.all(sourceFilesSnapshot.map(async (file) => {
        const lowerName = (file.name || '').toLowerCase();
        const isSpreadsheet = file.type.includes('sheet')
          || file.type === 'application/vnd.ms-excel'
          || file.type === 'text/csv'
          || lowerName.endsWith('.xlsx')
          || lowerName.endsWith('.xls')
          || lowerName.endsWith('.csv');

        const fileData = {
          name: file.name,
          type: file.type,
          size: file.size,
          contentType: file.type.startsWith('image/') ? 'image' :
            file.type === 'application/pdf' ? 'pdf' :
              isSpreadsheet ? 'spreadsheet' :
                file.type.startsWith('text/') ? 'text' : 'other'
        };

        if (file.type.startsWith('image/')) {
          try {
            const base64 = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = (e) => resolve(e.target.result);
              reader.onerror = reject;
              reader.readAsDataURL(file);
            });
            fileData.base64 = base64;
          } catch (error) {
            console.error('Failed to convert image to base64:', error);
            fileData.error = error.message;
          }
        } else if (file.type === 'application/pdf') {
          try {
            console.log('📄 Starting PDF text extraction for:', file.name, 'Size:', file.size);
            const { extractTextFromPDF } = await import('./utils');
            const pdfText = await extractTextFromPDF(file);
            fileData.content = pdfText;
          } catch (error) {
            console.error('❌ Failed to extract PDF text:', error);
            fileData.content = `[Failed to extract text from PDF: ${error.message}]`;
          }
        } else if (isSpreadsheet) {
          // Excel / CSV branch — mirrors the PDF pattern. SheetJS is loaded
          // lazily so the landing-page bundle isn't bloated for users who
          // never attach spreadsheets. The agent receives one CSV block per
          // sheet and can reason about the values with normal text tools.
          try {
            console.log('📊 Starting spreadsheet extraction for:', file.name, 'Size:', file.size);
            const { extractTextFromSpreadsheet } = await import('./utils');
            const sheetText = await extractTextFromSpreadsheet(file);
            fileData.content = sheetText;
          } catch (error) {
            console.error('❌ Failed to extract spreadsheet text:', error);
            fileData.content = `[Failed to extract text from spreadsheet: ${error.message}]`;
          }
        } else if (file.type.startsWith('text/') || file.name.endsWith('.txt')) {
          try {
            const text = await file.text();
            fileData.content = text;
          } catch (error) {
            console.error('Failed to read text file:', error);
            fileData.content = `[Failed to read text file: ${error.message}]`;
          }
        }

        return fileData;
      }));

      console.log('📎 File processing completed:', {
        totalFiles: processedFiles.length,
        fileTypes: processedFiles.map(f => ({ name: f.name, type: f.contentType })),
        imagesWithBase64: processedFiles.filter(f => f.contentType === 'image' && f.base64).length,
        pdfsWithText: processedFiles.filter(f => f.contentType === 'pdf' && f.content).length,
        spreadsheetsWithText: processedFiles.filter(f => f.contentType === 'spreadsheet' && f.content).length,
        textFiles: processedFiles.filter(f => f.contentType === 'text' && f.content).length,
        errors: processedFiles.filter(f => f.error).length
      });
    }

    const finalUserMessage = userMessage;
    const activeSteerSessionId =
      sessionStreamRef.current.sessionId || sessionId;
    const attachmentKey = steerAttachmentKey(sourceFilesSnapshot);
    const unresolvedSteer = unresolvedSteerDeliveryRef.current;
    const unresolvedSteerPlan = planUnresolvedSteerRetry({
      unresolved: unresolvedSteer,
      currentChatId,
      activeSessionId: activeSteerSessionId,
      displayText: finalUserMessage,
      attachmentKey,
    });
    const reusingUnresolvedSteer =
      unresolvedSteerPlan.reuseExact;

    if (unresolvedSteerPlan.blockFreshRequest) {
      setInput(userMessage);
      setAttachmentsFromFiles(sourceFilesSnapshot);
      console.warn(
        '[hooks] The previous steering delivery is still unknown. Reconcile that exact request before sending different instructions.'
      );
      return;
    }

    const userMessageId = reusingUnresolvedSteer
      ? unresolvedSteer.identity.userMessageId
      : `msg-${Date.now()}-${Math.random()}`;
    const requestId = reusingUnresolvedSteer
      ? unresolvedSteer.identity.requestId
      : `req-${Date.now()}-${Math.random()}`;
    const aiMessageId = reusingUnresolvedSteer
      ? unresolvedSteer.identity.proposedAssistantMessageId
      : generateMessageId();
    activeRequestIdRef.current = requestId;
    const messageStartTime = reusingUnresolvedSteer
      ? unresolvedSteer.messageStartTime
      : Date.now();

    const newUserMessage = reusingUnresolvedSteer
      ? unresolvedSteer.newUserMessage
      : {
      id: userMessageId,
      sender: 'user',
      timestamp: new Date().toISOString(),
      requestStartTime: messageStartTime,
      components: [
        { type: 'text', content: finalUserMessage, timestamp: new Date().toISOString() },
        ...(options?.clarificationResponse ? [{
          type: 'clarification_response',
          data: {
            clarificationId: options.clarificationResponse.id || null,
            prompt: options.clarificationResponse.prompt || '',
            response: options.clarificationResponse.response || finalUserMessage,
            value: options.clarificationResponse.value || finalUserMessage
          },
          timestamp: new Date().toISOString()
        }] : []),
        ...(processedFiles.length > 0 ? [{
          type: 'attachments',
          data: processedFiles,
          timestamp: new Date().toISOString()
        }] : [])
      ]
    };

    if (directLiveDataTurn) {
      const operationId = createPrivateRequestId();
      privateSessionActiveRef.current = true;
      isChatStreamingRef.current = true;
      setIsChatStreaming(true);
      setRequestStartTime(null);
      upsertEphemeralMessage(newUserMessage);
      updateLiveDataSession({
        type: 'finance_live_data_progress',
        operationId,
        stage: 'starting',
        message: LIVE_DATA_STAGE_LABELS.starting,
        active: true,
      });
      try {
        await startLiveDataTask({
          task: finalUserMessage,
          operationId,
          documentMarker: latestDocumentPrivacy,
        });
      } catch (error) {
        terminatePrivateLiveDataSession({
          operationId,
          stage: 'failed',
          message: LIVE_DATA_STAGE_LABELS.failed,
          code: error?.name === 'AbortError' ? 'request_cancelled' : 'request_rejected',
          abortLocal: true,
        });
        upsertEphemeralMessage({
          id: aiMessageId,
          sender: 'ai',
          timestamp: new Date().toISOString(),
          components: [{
            type: 'text',
            content: 'The live data request could not be started. Check the connector and try again.',
            timestamp: new Date().toISOString(),
          }],
          isStreaming: false,
          isError: true,
        });
        console.warn('[hooks] Live data request did not start', {
          code: error?.code || error?.name || 'request_rejected',
        });
      }
      return;
    }

    // CROSS-CHAT SEND GUARD: a turn is running, but the user is VIEWING a
    // different chat than the turn's home. Steering would inject this text
    // into the other chat's running turn, and a parallel normal send would
    // tear down that turn's SSE stream (one stream per pane). Neither is
    // acceptable: keep the draft in the composer until the turn finishes or
    // is stopped.
    if (
      isChatStreamingRef.current &&
      !options.forceNewSession &&
      activeTurnChatIdRef.current &&
      currentChatId &&
      currentChatId !== activeTurnChatIdRef.current
    ) {
      setInput(userMessage);
      console.warn('[hooks] A turn is running in another chat; wait for it to finish (or stop it) before sending here.');
      return;
    }

    // MID-TURN STEERING: a turn is already running for this chat. Instead of
    // blocking (the old behavior), POST the message now: the backend pushes
    // it into the LIVE query's input stream (SDK streaming input), so the
    // agent sees it with full context, mid-turn or as the immediate next
    // turn. The current AI bubble keeps streaming; no new turn lifecycle is
    // opened here unless the backend reports it started/queued a fresh turn.
    if (
      ephemeralTurn &&
      isChatStreamingRef.current &&
      !options.forceNewSession
    ) {
      setInput(userMessage);
      console.warn('[hooks] Private turns do not accept mid-turn steering');
      return;
    }

    if (
      (
        isChatStreamingRef.current
        && !options.forceNewSession
      )
      || reusingUnresolvedSteer
    ) {
      if (!currentChatId) return;
      const steerSessionId = sessionStreamRef.current.sessionId || sessionId;
      if (steerSessionId) {
        // The mid-turn path has no attachment pipeline: fold text-bearing
        // files into the steer content; images cannot be delivered
        // mid-turn, so tell the model to ask for a resend if it needs them.
        let steerContent = finalUserMessage;
        const steerTextFiles = processedFiles.filter((f) => f.content);
        const steerImageCount = processedFiles.filter((f) => f.contentType === 'image').length;
        if (steerTextFiles.length > 0) {
          steerContent += '\n\n' + steerTextFiles
            .map((f) => `<attached_file name="${f.name}">\n${f.content}\n</attached_file>`)
            .join('\n\n');
        }
        if (steerImageCount > 0) {
          steerContent += `\n\n[The user also attached ${steerImageCount} image(s), which cannot be delivered while a turn is running. Ask the user to resend them after this reply if you need them.]`;
        }

        let steerAuthToken = null;
        let steerAuthReady = true;
        try {
          steerAuthToken = currentUser ? await currentUser.getIdToken() : null;
        } catch (steerAuthError) {
          steerAuthReady = false;
          console.warn('[hooks] Steering authentication failed before delivery', {
            message: steerAuthError?.message || 'authentication_failed',
          });
        }
        if (steerAuthReady) {
          const steerBaseUrl = process.env.REACT_APP_STREAMING_SERVICE_URL || 'https://claude-streaming-service-103376361923.us-central1.run.app';
          const steerUrl = `${steerBaseUrl}/sessions/${steerSessionId}/messages`;
          const steerHeaders = {
            'Content-Type': 'application/json',
            ...(steerAuthToken ? { Authorization: `Bearer ${steerAuthToken}` } : {})
          };
          const steerPayload = reusingUnresolvedSteer
            ? unresolvedSteer.payload
            : buildMidTurnSteerPayload({
              messageContent: steerContent,
              chatId: currentChatId,
              requestId,
              userMessageId,
              assistantMessageId: aiMessageId,
              displayText: finalUserMessage,
              selectedModel,
              documentPrivacy: latestDocumentPrivacy,
            });
          const steerDelivery = await deliverMidTurnSteer({
            url: steerUrl,
            headers: steerHeaders,
            payload: steerPayload,
          });

          if (steerDelivery.outcome === 'accepted') {
            if (
              unresolvedSteerDeliveryRef.current?.identity?.requestId ===
              steerDelivery.identity.requestId
            ) {
              unresolvedSteerDeliveryRef.current = null;
            }
            const steerData = steerDelivery.data;
            const steerPlan = planAcceptedMidTurnSteer(
              steerData,
              aiMessageId
            );
            // 'steered' = injected into the live turn (current bubble carries
            // on). Anything else means the backend queued or started a fresh
            // turn, whose events need a staged entry or the idle-stream guard
            // will drop them. Stage it FIRST, before any UI awaits, so those
            // events cannot race past us.
            if (steerPlan.stageTurn) {
              createPendingTurn({
                aiMessageId: steerPlan.assistantMessageId,
                requestId,
                chatId: currentChatId,
              });
            }
            // Rendering: for steered/queued the backend echoes the text back
            // as a user_message_queued SSE event, which streamProcessor turns
            // into an inline user bubble INSIDE the live stream (repository
            // writes made mid-turn are not reliably reflected while a turn is
            // rendering, which is how steer messages went invisible). Only
            // the no-mode race (backend started a plain turn) still renders
            // the classic optimistic bubble.
            if (steerPlan.renderClassicUserMessage) {
              await enqueueMessageUpdate(() => addSanitizedMessage(newUserMessage), true);
            }
            setInput('');
            clearAttachments();
            resetTextareaHeight();
            console.log(`🎯 Message ${steerPlan.mode} while turn was running`);
            return;
          }

          if (steerDelivery.outcome === 'unknown') {
            unresolvedSteerDeliveryRef.current = {
              identity: steerDelivery.identity,
              sessionId: steerSessionId,
              chatId: currentChatId,
              displayText: finalUserMessage,
              attachmentKey,
              payload: steerPayload,
              newUserMessage,
              messageStartTime,
            };
            setInput(userMessage);
            setAttachmentsFromFiles(sourceFilesSnapshot);
            console.warn('[hooks] Steering delivery is unknown. The exact request will be reused on retry.', {
              requestId: steerDelivery.identity.requestId,
              userMessageId: steerDelivery.identity.userMessageId,
              assistantMessageId:
                steerDelivery.identity.proposedAssistantMessageId,
              message: steerDelivery.error?.message || 'network_error',
            });
            return;
          }

          if (
            unresolvedSteerDeliveryRef.current?.identity?.requestId ===
            steerDelivery.identity.requestId
          ) {
            unresolvedSteerDeliveryRef.current = null;
          }
          console.warn(
            '⚠️ Steering POST rejected; sending as a normal turn:',
            steerDelivery.status
          );
          if (reusingUnresolvedSteer) {
            setInput(userMessage);
            setAttachmentsFromFiles(sourceFilesSnapshot);
            console.warn(
              '[hooks] Exact steering reconciliation was rejected. Refresh this secure chat before sending another instruction.'
            );
            return;
          }
        } else if (reusingUnresolvedSteer) {
          setInput(userMessage);
          setAttachmentsFromFiles(sourceFilesSnapshot);
          return;
        }
      }
    }

    isChatStreamingRef.current = true;
    setIsChatStreaming(true);
    setRequestStartTime(messageStartTime);
    if (ephemeralTurn) {
      privateSessionActiveRef.current = true;
    }

    // A local send is deliberate engagement with THIS chat: hold off
    // auto-follow for the intent grace window.
    manualChatActionAtRef.current = Date.now();

    let chatIdToUse = ephemeralTurn
      ? (currentChatId || `private-${latestDocumentPrivacy.documentId}`)
      : currentChatId;
    const hasExistingUserMessages = currentMessages.some(m => m.sender === 'user');
    let isNewChat = false;
    if (!chatIdToUse && !ephemeralTurn) {
      chatIdToUse = `chat-${Date.now()}`;
      isNewChat = true;

      // Reset sessionId for new chat to prevent session continuity carryover.
      // Note: setSessionId is async; sendMessageToAI's closure will still read
      // the OLD stale value until re-render. We also pass `forceNewSession`
      // in options below so sendMessageToAI ignores the stale value and
      // creates a fresh Cloud Run session.
      setSessionId(null);
      sessionDocumentBindingRef.current = {
        sessionId: null,
        documentId: null,
      };
      console.log('💬 Creating new chat context before send:', { chatIdToUse });

      // Set override BEFORE switchToChat so that all addSanitizedMessage
      // calls during this turn write to the correct chat, even though
      // addMessage's closure still has currentChatId=null.
      chatIdOverrideRef.current = chatIdToUse;

      try {
        if (currentPresentation?.id) {
          await repository.createChat(currentPresentation.id, {
            id: chatIdToUse,
            name: 'New chat',
            // Label chats by the Office app they were started in.
            origin: app,
            transcriptMode: TRANSCRIPT_MODE_PENDING,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          });
        }
        if (typeof switchToChat === 'function') {
          markProgrammaticSwitch(chatIdToUse);
          switchToChat(chatIdToUse);
        }
      } catch (e) {
        console.warn('⚠️ Failed to pre-create chat locally:', e);
      }
    }

    // Pin the turn's home chat before the first write: the user message,
    // the primed bubble, and every later stream write belong to the chat
    // the user SENT from, regardless of where they navigate afterwards.
    activeTurnChatIdRef.current = chatIdToUse;

    if (
      ephemeralTurn &&
      ephemeralScopeRef.current.chatId !== chatIdToUse
    ) {
      beginEphemeralScope({
        documentId: latestDocumentPrivacy.documentId,
        chatId: chatIdToUse,
        sessionId: sessionStreamRef.current.sessionId || sessionId,
      });
    }
    const messagesToSend = ephemeralTurn
      ? [newUserMessage]
      : [...currentMessages, newUserMessage];
    setPendingMessage(newUserMessage);

    try {
      await routeTurnSink(turnUsesMemoryOnlySink({
        ephemeral: ephemeralTurn,
        privacyPending,
      }), {
        privateSink: () => upsertEphemeralMessage(newUserMessage),
        persistentSink: () => addSanitizedMessage(newUserMessage),
      });
      if (typeof window !== 'undefined') {
        window.setTimeout(() => scrollToLatestMessage('smooth', true), 0);
      } else {
        scrollToLatestMessage('smooth', true);
      }

      await primeStreamingMessage(
        aiMessageId,
        messageStartTime,
        { ephemeral: ephemeralTurn, privacyPending },
      );

      // When we just created a new chat locally, pass forceNewSession so
      // sendMessageToAI ignores any stale sessionId still sitting in React
      // state (setSessionId(null) hasn't re-rendered yet).
      const effectiveOptions = {
        ...options,
        ephemeral: ephemeralTurn,
        privacyPending,
        ...(FINANCE_CONNECTORS_UI_ENABLED
          ? { documentPrivacy: latestDocumentPrivacy }
          : {}),
        rawSourceFiles: ephemeralTurn ? [] : sourceFilesSnapshot,
        displayText: finalUserMessage,
        ...(isNewChat ? { forceNewSession: true } : {}),
      };

      const result = await sendMessageToAI(messagesToSend, requestId, chatIdToUse, aiMessageId, effectiveOptions);

      // Generate a descriptive title only after the server has classified the
      // transcript. Scope-bound chats keep a generic owner-readable title so
      // project content cannot survive an access revocation in chat metadata.
      if (
        !ephemeralTurn &&
        (isNewChat || !hasExistingUserMessages) &&
        userMessage &&
        currentPresentation?.id
      ) {
        const titleChat = (smartCache.get(currentPresentation.id)?.chats || [])
          .find((chat) => chat.id === chatIdToUse);
        if (transcriptModeForChat(titleChat) === TRANSCRIPT_MODE_ORDINARY) {
          const titleChatId = chatIdToUse;
          const titlePresentationId = currentPresentation.id;
          (async () => {
            try {
              const streamingBaseUrl = process.env.REACT_APP_STREAMING_SERVICE_URL || 'https://claude-streaming-service-103376361923.us-central1.run.app';
              const titleAuthToken = currentUser ? await currentUser.getIdToken() : null;
              const res = await axios.post(
                `${streamingBaseUrl}/generate-title`,
                { firstMessage: userMessage },
                { timeout: 15000, headers: titleAuthToken ? { Authorization: `Bearer ${titleAuthToken}` } : {} },
              );
              const title = res.data?.title;
              if (title) {
                repository.renameChat(titlePresentationId, titleChatId, title);
              }
            } catch (e) {
              console.warn('Failed to generate chat title:', e.message);
            }
          })();
        }
      }

      if (result.cancelled) {
        debug('✅ Request was cancelled, skipping stream handling');
        return;
      }

      console.log('🌊 Waiting for turn completion');
      const streamResult = await result.turnPromise;
      console.log('✅ Turn completed:', {
        hasResponse: !!streamResult,
        responseType: streamResult?.type
      });

    } catch (error) {
      // Check if this is a user-initiated stop or presentation switch (not a real error)
      const errorMsg = error?.message || String(error);
      const isAbort = error?.name === 'AbortError'
        || errorMsg === 'AbortError'
        || errorMsg === 'Turn interrupted by user'
        || errorMsg === 'presentation_switch'
        || errorMsg.includes('presentation_switch')
        || errorMsg.includes('Presentation switched')
        || errorMsg.includes('aborted')
        || error?.code === 'PRESENTATION_SWITCH'
        || (typeof error === 'string' && error === 'presentation_switch');

      const privateTurn = ephemeralTurn || privateSessionActiveRef.current;
      const secureDeliveryDecision =
        error?.suppressFreshRetry === true
          ? error.deliveryDecision
          : null;
      if (secureDeliveryDecision) {
        unresolvedInitialDeliveryRef.current = {
          chatId: chatIdToUse,
          requestId,
          outcome: secureDeliveryDecision.outcome,
        };
        const notice = createInitialDeliveryNotice({
          aiMessageId,
          decision: secureDeliveryDecision,
        });
        try {
          await addSanitizedMessage(notice);
        } catch (saveError) {
          console.error(
            '❌ Failed to preserve the secure delivery notice:',
            saveError
          );
        }
      } else if (privateTurn) {
        if (isAbort) {
          setEphemeralMessages((current) => current.map((message) =>
            message.id === aiMessageId
              ? {
                  ...message,
                  isStreaming: false,
                  isCancelled: true,
                  metadata: {
                    ...(message.metadata || {}),
                    isStreaming: false,
                    isComplete: true,
                    isCancelled: true,
                  },
                }
              : message
          ));
        } else {
          upsertEphemeralMessage({
            id: aiMessageId,
            sender: 'ai',
            components: [{
              type: 'text',
              content: 'This private request could not be completed. Check the connection and try again.',
              timestamp: new Date().toISOString(),
            }],
            timestamp: new Date().toISOString(),
            isStreaming: false,
            isError: true,
            errorCode: 'private_turn_failed',
          });
          console.warn('[hooks] Private turn failed', {
            code: error?.code || error?.name || 'private_turn_failed',
          });
        }
      } else if (isAbort) {
        console.log('✅ Request aborted (user stop or presentation switch) — no error card');
        // Mark last AI message components as complete without showing error
        const lastAiMessageIndex = currentMessages.findLastIndex(m => m.sender === 'ai');
        if (lastAiMessageIndex >= 0) {
          const lastAiMessage = currentMessages[lastAiMessageIndex];
          if (lastAiMessage.components && lastAiMessage.components.length > 0) {
            const { componentUpdaters } = await import('./streamProcessor');
            const updatedComponents = componentUpdaters.settleAllComponents(lastAiMessage.components, false);
            await addSanitizedMessage({
              ...lastAiMessage,
              components: updatedComponents,
              isStreaming: false,
              isCancelled: true
            });
          }
        }
      } else {
        console.error('❌ Error in handleSend:', error);

        const errorText = error.message || 'Something went wrong while processing your request.';
        const lastAiMessageIndex = currentMessages.findLastIndex(m => m.sender === 'ai');
        if (lastAiMessageIndex >= 0) {
          const lastAiMessage = currentMessages[lastAiMessageIndex];
          if (lastAiMessage.components && lastAiMessage.components.length > 0) {
            const { componentUpdaters } = await import('./streamProcessor');
            const updatedComponents = componentUpdaters.settleAllComponents(lastAiMessage.components, false);

            const updatedMessage = {
              ...lastAiMessage,
              components: updatedComponents,
              isStreaming: false,
              hasError: true
            };

            await addSanitizedMessage(updatedMessage);
          }
        }

        const errorMessage = {
          id: `msg-${Date.now()}-${Math.random()}`,
          sender: 'ai',
          components: [{
            type: 'text',
            content: errorText,
            timestamp: new Date().toISOString()
          }, {
            type: 'retry',
            originalMessage: userMessage
          }],
          timestamp: new Date().toISOString(),
          isError: true,
          errorCode: error.code
        };
        if (currentPresentation?.id) {
          try {
            await addSanitizedMessage(errorMessage);
          } catch (saveError) {
            console.error('❌ Failed to add error message:', saveError);
          }
        }
      }
    } finally {
      const privateTurn = ephemeralTurn || privateSessionActiveRef.current;
      if (privateTurn) {
        privateSessionActiveRef.current = false;
      }
      chatIdOverrideRef.current = null;
      activeTurnChatIdRef.current = null;
      isChatStreamingRef.current = false;
      setIsChatStreaming(false);
      activeRequestIdRef.current = null;
      abortControllerRef.current = null;
      setPendingMessage(null);
      setLiveStreamingMessage(null);
      lastPersistedMessageRef.current = null;
      if (!privateTurn) {
        await saveRequestDuration();
      }
      setRequestStartTime(null);

    }
  }, [
    addSanitizedMessage,
    answerUserQuestion,
    app,
    beginEphemeralScope,
    clearAttachments,
    currentChatId,
    currentMessages,
    currentPresentation?.id,
    currentUser,
    createPendingTurn,
    enqueueMessageUpdate,
    financeOfficeReady,
    financeConnectorReady,
    input,
    isChatStreaming,
    liveDataMode,
    markProgrammaticSwitch,
    powerpointBridge,
    primeStreamingMessage,
    readFreshDocumentPrivacy,
    repository,
    saveRequestDuration,
    resetTextareaHeight,
    selectedFiles,
    sendMessageToAI,
    sessionId,
    scrollToLatestMessage,
    setAttachmentsFromFiles,
    setInput,
    setIsChatStreaming,
    setPendingMessage,
    setRequestStartTime,
    startLiveDataTask,
    switchToChat,
    terminatePrivateLiveDataSession,
    resetLoopTelemetry,
    scopedTranscriptState,
    updateLiveDataSession,
    upsertEphemeralMessage,
  ]);

  // Keep the ref current so answerUserQuestion (defined far above) can fall
  // back to a normal send when a question's tool call has expired server-side.
  useEffect(() => {
    handleSendRef.current = handleSend;
  }, [handleSend]);

  // Compact the current chat's conversation context: runs the SDK /compact
  // command on the resumed session server-side (older history summarized,
  // important context kept), so long working sessions can continue past the
  // context window. Stages a turn entry first so the compact turn's SSE
  // events render as a normal (short) turn.
  const compactChat = useCallback(async () => {
    const sid = sessionStreamRef.current.sessionId || sessionId;
    if (!sid || isChatStreamingRef.current) return false;
    // Mark the session busy BEFORE any await: a Send racing this window
    // would otherwise post a normal turn that gets steered INTO the compact
    // query, consuming the compact's terminal event and leaving the send's
    // staged entry pending forever. Rolled back on rejection/failure.
    isChatStreamingRef.current = true;
    const compactAssistantMessageId = generateMessageId();
    const compactRequestId = `compact-${Date.now()}`;
    const staged = createPendingTurn({
      aiMessageId: compactAssistantMessageId,
      requestId: compactRequestId,
      chatId: currentChatIdRef.current
    });
    try {
      const authToken = currentUser ? await currentUser.getIdToken() : null;
      const streamingBaseUrl = process.env.REACT_APP_STREAMING_SERVICE_URL || 'https://claude-streaming-service-103376361923.us-central1.run.app';
      const resp = await fetch(`${streamingBaseUrl}/sessions/${sid}/compact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {})
        },
        body: JSON.stringify({
          clientAssistantMessageId:
            compactAssistantMessageId,
          clientRequestId: compactRequestId,
        }),
      });
      if (!resp.ok) {
        isChatStreamingRef.current = false;
        staged.cancel(new Error(`compact rejected: ${resp.status}`));
        const data = await resp.json().catch(() => ({}));
        console.warn('⚠️ Compact rejected:', resp.status, data?.message || '');
        return false;
      }
      setIsChatStreaming(true);
      console.log('🗜️ Compacting chat context...');
      return true;
    } catch (err) {
      isChatStreamingRef.current = false;
      staged.cancel(err);
      console.warn('⚠️ Compact failed:', err?.message);
      return false;
    }
  }, [sessionId, currentUser, createPendingTurn, setIsChatStreaming]);

  const retryMessage = useCallback((originalMessage) => {
    console.log('🔄 Retrying message:', originalMessage);
    setInput(originalMessage);
    setTimeout(() => {
      handleSend();
    }, 100);
  }, [handleSend, setInput]);

  const handleLiveDataModeChange = useCallback((enabled) => {
    if (privateSessionActiveRef.current) return false;
    if (
      enabled &&
      (!FINANCE_CONNECTORS_UI_ENABLED || !financeOfficeReady || !financeConnectorReady)
    ) {
      return false;
    }
    setLiveDataMode(enabled === true);
    if (enabled) {
      updateLiveDataSession(null);
    }
    return true;
  }, [financeConnectorReady, financeOfficeReady, updateLiveDataSession]);

  useEffect(() => {
    if (hasBootstrappedInitialPrompt) {
      return;
    }

    if (!currentPresentation?.id) {
      return;
    }

    if (typeof window === 'undefined') {
      return;
    }

    const pendingPrompt = window.sessionStorage.getItem('restack.initialPrompt');
    if (!pendingPrompt) {
      return;
    }

    const trimmedPrompt = pendingPrompt.trim();
    window.sessionStorage.removeItem('restack.initialPrompt');

    if (!trimmedPrompt) {
      setHasBootstrappedInitialPrompt(true);
      return;
    }

    setHasBootstrappedInitialPrompt(true);
    setInput(trimmedPrompt);
    handleSend(trimmedPrompt).finally(() => {
      setInput('');
    });
  }, [currentPresentation?.id, handleSend, hasBootstrappedInitialPrompt, setInput]);

  return {
    handleSend,
    handleStopRequest,
    retryMessage,
    isLoading: isChatStreaming,
    // The live bubble belongs to the turn's HOME chat: when the user views a
    // different chat mid-turn, don't float it into that transcript (its
    // writes keep landing in the home chat; the view shows it on return).
    pendingMessage:
      activeTurnChatIdRef.current && currentChatId && currentChatId !== activeTurnChatIdRef.current
        ? null
        : (liveStreamingMessage || pendingMessage),
    messagesContainerRef,
    messagesEndRef,
    pendingUserQuestion,
    answerUserQuestion,
    pendingProposeEdits,
    submitProposeEdits,
    applyProposeEdit,
    ephemeralMessages,
    documentPrivacy,
    liveDataAvailable:
      FINANCE_CONNECTORS_UI_ENABLED &&
      financeOfficeReady &&
      financeConnectorReady,
    liveDataMode,
    onLiveDataModeChange: handleLiveDataModeChange,
    liveDataSession,
    officeOperationRecoveryRequired,
    officeOperationRecovery,
    resolveOfficeOperationRecovery,
    dismissOfficeOperationRecovery,
    privateSessionActive:
      liveDataSession?.active === true ||
      (
        documentPrivacy?.state === 'licensed_data' &&
        isChatStreaming
      ),
    // Conversation compaction (SDK /compact on the resumed session)
    compactChat,
    canCompactChat:
      !!sessionId &&
      !isChatStreaming &&
      documentPrivacy?.state !== 'licensed_data' &&
      !liveDataMode,
    // Dismiss the cross-app waiting UI without touching the peer's work
    cancelPeerWait,
    // Cross-app connector presence (header indicator + click-to-reconnect)
    conductorOnline,
    conductorPeers,
    connectConductor,
    // Peer label while this pane waits on a cross-app reply (composer busy UI)
    awaitingPeerLabel
  };
};

// =============================================================================
// MODULE EXPORTS
// =============================================================================

export default {
  useTypewriterPlaceholder,
  useChatComposer,
  useStreamingSession
};
